<!-- eslint-disable vue/valid-v-slot -->
<template>
  <div class="driver-details-page">
    <!-- Page header -->
    <div class="page-header">
      <button class="back-btn" @click="$router.go(-1)" :title="$t('common.back') || 'Wstecz'">
        <v-icon size="18">mdi-arrow-left</v-icon>
      </button>
      <div class="page-avatar">
        <v-icon size="22" color="#fff">mdi-account</v-icon>
      </div>
      <div class="page-header-text">
        <h1 class="page-title">{{ (formData.firstName || '') + ' ' + (formData.lastName || '') }}</h1>
      </div>
    </div>

    <!-- Kierowca z rejestracji, którego nikt nie zaakceptował. Uzupełnienie
         współpracy i ZUS na karcie go NIE akceptuje — zostaje na liście
         „Kierowcy do akceptacji”, bez danych logowania i bez wpisu w wykazie
         (12–13.10.2026: K. Śmielecka, R. Cheran). -->
    <v-alert v-if="czekaNaAkceptacje" type="warning" outlined dense class="mx-4 mt-2 mb-0">
      <div class="d-flex flex-wrap align-center" style="gap: 8px">
        <span>Kierowca czeka na akceptację — uzupełnienie danych na karcie go nie akceptuje.</span>
        <v-spacer />
        <v-btn small color="success" :loading="akceptacjaTrwa" @click="zaakceptujKierowce">
          <v-icon left small>mdi-check</v-icon>Zaakceptuj kierowcę
        </v-btn>
      </div>
    </v-alert>

    <!-- Segmented tabs -->
    <div class="tabs-pill-wrap">
      <div class="tabs-pill" role="tablist">
        <template v-for="(t, i) in tabConfig">
          <button v-if="canAccess(t.slug)" :key="i"
            class="tab-pill"
            :class="{ 'tab-pill--active': activeTab === i }"
            @click="activeTab = i"
          >
            <v-icon size="14">{{ t.icon }}</v-icon>
            <span>{{ $t(t.key) }}</span>
          </button>
        </template>
      </div>
    </div>

    <!-- Bolt Fleet API 2.0: zawieszenie, oceny, auto z Bolta -->
    <BoltDriverQuality
      v-if="currentDriverId"
      :driver-id="currentDriverId"
      @assigned="boltAutoPrzypisane"
      @error="boltBlad"
    />

    <!-- Tab Content -->
    <v-tabs-items v-model="activeTab" touchless>
      <!-- General Information Tab -->
      <v-tab-item>
        <v-card>
          <v-card-title>
            <span class="text-h6">{{ $t('driverDetails.generalInfo') }}: {{ numericId }}</span>
          </v-card-title>
          <v-card-text>
            <v-form ref="form" v-model="valid" @submit.prevent="submitForm">
              <v-row>
                <!-- First Name -->
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="formData.firstName"
                    :label="$t('driverDetails.labels.firstName')"
                    :rules="[rules.required]"
                    required
                  />
                </v-col>

                <!-- Last Name -->
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="formData.lastName"
                    :label="$t('driverDetails.labels.lastName')"
                    :rules="[rules.required]"
                    required
                  />
                </v-col>

                <!-- Phone Number -->
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="formData.phone"
                    :label="$t('driverDetails.labels.phone')"
                    :rules="[rules.required]"
                    required
                  />
                </v-col>

                <!-- Email -->
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="formData.email"
                    :label="$t('driverDetails.labels.email')"
                    :rules="[rules.required, rules.email]"
                    required
                  />
                </v-col>
                <v-col cols="12" md="12">
                  <v-text-field
                    v-model="formData.password"
                    :label="$t('driverDetails.labels.password')"
                    type="password"
                    autocomplete="new-password"
                    :hint="$t('driverDetails.labels.passwordHint')"
                    persistent-hint
                  />
                </v-col>
                <v-col cols="12">
                  <v-text-field
                    v-model="formData.streetAddress"
                    :label="$t('driverDetails.labels.street')"
                  />
                </v-col>
                <v-col cols="6">
                  <v-text-field
                    v-model="formData.homeNumber"
                    :label="$t('driverDetails.labels.homeNumber')"
                  />
                </v-col>
                <v-col cols="6">
                  <v-text-field
                    v-model="formData.aptNumber"
                    :label="$t('driverDetails.labels.aptNumber')"
                  />
                </v-col>
                              <!-- Street Address 2 -->
                <v-col cols="12">
                  <v-text-field
                    v-model="formData.postalCode"
                    :label="$t('driverDetails.labels.postalCode')"
                  />
                </v-col>
                <!-- City -->
                <v-col cols="12" md="12">
                  <v-text-field v-model="formData.city" :label="$t('driverDetails.labels.city')" />
                </v-col>

                <!-- Street Address 1 -->

                <!-- License Number -->
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="formData.licenseNumber"
                    :label="$t('driverDetails.labels.licenseNumber')"
                  />
                </v-col>

                <!-- Bolt ID -->
                <v-col cols="12" md="6">
                  <v-text-field v-model="formData.boltId" label="Bolt ID" />
                </v-col>

                <!-- Uber ID -->
                <v-col cols="12" md="6">
                  <v-text-field v-model="formData.uberId" label="Uber ID" />
                </v-col>

                <!-- FreeNow ID -->
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="formData.freeNowId"
                    label="FreeNow ID"
                  />
                </v-col>

                <!-- Payout Type -->
                <v-col cols="12" md="6">
                  <v-select
                    v-model="formData.payoutType"
                    :items="payoutTypes"
                    :disabled="poleRozliczenZablokowane('payoutType')"
                    :hint="hintRozliczen('payoutType')"
                    persistent-hint
                    :label="$t('driverDetails.labels.payoutType')"
                    :rules="poleRozliczenZablokowane('payoutType') ? [] : [rules.required]"
                    required
                  />
                  <v-alert
                    v-if="formData.scheduledPayoutType"
                    type="info"
                    dense
                    text
                    class="mt-1 mb-0 text-caption"
                  >
                    Zaplanowana zmiana trybu:
                    <b>{{ getPayoutTypeLabel(formData.payoutType) }} →
                    {{ getPayoutTypeLabel(formData.scheduledPayoutType) }}</b>
                    — wejdzie od następnego tygodnia rozliczeniowego.
                  </v-alert>
                </v-col>

                <!-- Payout Schedule -->
                <v-col cols="12" md="6">
                  <v-select
                    v-model="formData.payoutSchedule"
                    :items="payoutSchedules"
                    :disabled="poleRozliczenZablokowane('payoutSchedule')"
                    :hint="hintRozliczen('payoutSchedule')"
                    persistent-hint
                    :label="$t('driverDetails.labels.payoutSchedule')"
                    :rules="poleRozliczenZablokowane('payoutSchedule') ? [] : [rules.required]"
                    required
                  />
                  <v-alert
                    v-if="formData.scheduledPayoutSchedule"
                    type="info"
                    dense
                    text
                    class="mt-1 mb-0 text-caption"
                  >
                    Zaplanowana zmiana częstotliwości:
                    <b>{{ getPayoutScheduleLabel(formData.payoutSchedule) }} →
                    {{ getPayoutScheduleLabel(formData.scheduledPayoutSchedule) }}</b>
                    — wejdzie od następnego tygodnia rozliczeniowego.
                  </v-alert>

                  <!-- Wymuszenie zmiany od reki — tylko head admin -->
                  <v-checkbox
                    v-if="isHeadAdmin"
                    v-model="formData.applySettlementChangeNow"
                    dense
                    hide-details
                    class="mt-2"
                    color="warning"
                  >
                    <template v-slot:label>
                      <span class="text-caption">
                        <b>Zastosuj natychmiast</b> — pomiń odroczenie do
                        następnego tygodnia. Wpłynie na już policzony tydzień:
                        opłaty, administracja i zleceniówka mogą wyjść inaczej.
                      </span>
                    </template>
                  </v-checkbox>
                </v-col>

                <!-- Cooperation Type -->
                <v-col cols="12" md="6">
                  <v-select
                    v-model="formData.cooperationType"
                    :items="cooperationTypes"
                    :disabled="poleRozliczenZablokowane('cooperationType')"
                    :hint="hintRozliczen('cooperationType')"
                    persistent-hint
                    :label="$t('driverDetails.labels.cooperationType')"
                    :rules="poleRozliczenZablokowane('cooperationType') ? [] : [rules.required]"
                    required
                  />
                  <v-alert
                    v-if="formData.scheduledCooperationType"
                    type="info"
                    dense
                    text
                    class="mt-1 mb-0 text-caption"
                  >
                    Zaplanowana zmiana typu:
                    <b>{{ getCooperationTypeLabel(formData.cooperationType) }} →
                    {{ getCooperationTypeLabel(formData.scheduledCooperationType) }}</b>
                    — wejdzie od następnego tygodnia rozliczeniowego.
                  </v-alert>
                </v-col>

                <!-- Rodzaj konta: kierowca / właściciel aut (podflota) -->
                <v-col cols="12" md="6">
                  <v-select
                    :value="formData.accountType || 'DRIVER'"
                    :items="[
                      { text: 'Kierowca', value: 'DRIVER' },
                      { text: 'Właściciel aut (podflota)', value: 'SUBFLEET_OWNER' },
                    ]"
                    label="Rodzaj konta"
                    hint="Zmiana zapisuje się od razu. Przełączenie na właściciela tworzy podflotę i zakładkę 'Moja podflota' w aplikacji."
                    persistent-hint
                    @change="changeAccountType"
                  />
                </v-col>

                <!-- Zus Type -->
                <v-col cols="12" md="6">
                  <v-select
                    v-model="formData.zusType"
                    :items="zusTypes"
                    :disabled="poleRozliczenZablokowane('zusType')"
                    :hint="hintRozliczen('zusType')"
                    persistent-hint
                    :label="$t('driverDetails.labels.zusType')"
                    :rules="poleRozliczenZablokowane('zusType') ? [] : [rules.required]"
                    required
                  />
                  <v-alert
                    v-if="formData.scheduledZusType"
                    type="info"
                    dense
                    text
                    class="mt-1 mb-0 text-caption"
                  >
                    Zaplanowana zmiana ZUS:
                    <b>{{ getZusTypeLabel(formData.zusType) }} →
                    {{ getZusTypeLabel(formData.scheduledZusType) }}</b>
                    — wejdzie od następnego tygodnia rozliczeniowego.
                  </v-alert>
                </v-col>

                <!-- Account Number -->
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="formData.accountNo"
                    :label="$t('driverDetails.labels.accountNo')"
                    :rules="formData.isAbroadBankAccount ? [] : [rules.bankAccount]"
                    :hint="$t('driverDetails.labels.accountNoHint')"
                    persistent-hint
                  />
                </v-col>

                <!-- Is Abroad Bank Account -->
                <v-col cols="12" md="6">
                  <v-checkbox
                    v-model="formData.isAbroadBankAccount"
                    :label="$t('driverDetails.labels.abroadAccount')"
                  />
                </v-col>

                <!-- Is Ready for Settlements -->
                <v-col cols="12" md="6">
                  <v-checkbox
                    disabled
                    v-model="formData.isReadyForSettlements"
                    :label="$t('driverDetails.labels.readyForSettlements')"
                  />
                </v-col>

                <!-- Blokada wypłaty: ręczna decyzja biura, z powodem dla kierowcy -->
                <v-col cols="12" md="6">
                  <v-switch
                    :input-value="formData.payoutBlocked"
                    :label="formData.payoutBlocked ? 'Wypłata zablokowana' : 'Wypłata odblokowana'"
                    :color="formData.payoutBlocked ? 'error' : 'success'"
                    hide-details
                    class="mt-1"
                    @change="onPayoutBlockToggle"
                  />
                  <div v-if="formData.payoutBlocked && formData.payoutBlockReason" class="caption error--text mt-2">
                    Powód: {{ formData.payoutBlockReason }}
                  </div>
                  <div v-else class="caption grey--text mt-2">
                    Blokada wstrzymuje generowanie wypłat; kierowca dostaje powiadomienie z powodem.
                  </div>
                </v-col>

                <!-- Dane do wniosków urzędowych. Wykaz kierowców zgłaszanych do
                     licencji wymaga terminów badań i daty zaświadczenia o niekaralności,
                     a do 23.09.2026 nie było ich w systemie gdzie trzymać — operator
                     przepisywał je do każdego wniosku od nowa. -->
                <v-col cols="12">
                  <v-divider class="mb-3" />
                  <div class="subtitle-2">Dane do wniosków urzędowych</div>
                  <div class="caption grey--text mb-3">
                    Wpisane raz, podstawią się same przy generowaniu wykazu kierowców do licencji.
                  </div>
                  <v-row dense>
                    <v-col cols="12" md="4">
                      <v-text-field v-model="formData.drivingLicenseValidTo" type="date" label="Prawo jazdy ważne do" :disabled="!!formData.drivingLicenseIndefinite" outlined dense hide-details />
                    </v-col>
                    <v-col cols="12" md="4">
                      <v-checkbox v-model="formData.drivingLicenseIndefinite" label="Prawo jazdy bezterminowe" dense hide-details class="mt-1" @change="formData.drivingLicenseIndefinite && (formData.drivingLicenseValidTo = '')" />
                    </v-col>
                    <v-col cols="12" md="4">
                      <v-text-field v-model="formData.criminalRecordDate" type="date" label="Niekaralność — data wydania" outlined dense hide-details />
                    </v-col>
                    <v-col cols="12" md="3">
                      <v-text-field v-model="formData.medicalCertificateNumber" label="Nr orzeczenia lekarskiego" maxlength="100" outlined dense hide-details />
                    </v-col>
                    <v-col cols="12" md="3">
                      <v-text-field v-model="formData.medicalValidTo" type="date" label="Badania lekarskie do" outlined dense hide-details />
                    </v-col>
                    <v-col cols="12" md="3">
                      <v-text-field v-model="formData.psychologicalCertificateNumber" label="Nr orzeczenia psychologicznego" maxlength="100" outlined dense hide-details />
                    </v-col>
                    <v-col cols="12" md="3">
                      <v-text-field v-model="formData.psychologicalValidTo" type="date" label="Psychologiczne do" outlined dense hide-details />
                    </v-col>
                    <!-- Dowód osobisty: dane zamiast zdjęcia (RODO, 12.10.2026) —
                         kopii dowodu nie przechowujemy. -->
                    <v-col cols="12" md="3">
                      <v-text-field v-model="formData.idCardNumber" label="Dowód osobisty — seria i numer" maxlength="20" outlined dense hide-details />
                    </v-col>
                    <v-col cols="12" md="3">
                      <v-text-field v-model="formData.idCardValidTo" type="date" label="Dowód ważny do" outlined dense hide-details />
                    </v-col>
                    <v-col cols="12">
                      <v-btn small color="primary" :loading="documentDataSaving" @click="saveDocumentData">Zapisz dane do wniosków</v-btn>
                    </v-col>
                  </v-row>
                </v-col>

                <!-- Missing Data -->
                <v-col cols="12">
                  <v-textarea
                    disabled
                    v-model="formData.missingData"
                    :label="$t('driverDetails.labels.missingData')"
                  />
                </v-col>

                <!-- Register Type -->
                <v-col cols="12" >
                  <v-select
                    v-model="formData.registerType"
                    :items="registerTypes"
                    :label="$t('driverDetails.labels.registerType')"
                    :rules="[rules.required]"
                    required
                  />
                </v-col>

                <!-- Subcompany IDs (Multi-select) -->
                <v-col cols="12" md="12">
                  <v-select
                    v-model="formData.subCompanyIds"
                    :items="subCompanies"
                    item-text="name"
                    item-value="id"
                    :label="$t('driverDetails.labels.department')"
                    multiple
                  />
                </v-col>
              </v-row>
            </v-form>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <!-- Cancel Button -->
            <v-btn text color="secondary" @click="goBack">{{ $t('driverDetails.goBack') }}</v-btn>
            <!-- Submit Button -->
            <v-btn color="primary" @click="submitForm">
              {{ $t('common.save') }}
            </v-btn>
          </v-card-actions>
        </v-card>

        <!-- Oświadczenia ZUS — w GŁÓWNEJ zakładce, celowo.
             Do 05.10.2026 te przełączniki były wyłącznie na „Rejestracjach
             kierowców", a ta lista pomija kierowców gotowych do pracy. Czyli po
             zatwierdzeniu kierowcy nie było już gdzie zaznaczyć mu renty —
             i 152 ze 158 kierowców miało puste oświadczenia. Od tych pól zależą
             składki ZUS w rozliczeniu ORAZ to, jakie miejsca na dokumenty
             kierowca widzi w aplikacji (student → legitymacja, emeryt →
             zaświadczenie o emeryturze, rencista → o rencie, orzeczenie
             → orzeczenie). -->
        <v-card class="mt-4" outlined>
          <v-card-title>
            <v-icon left color="primary">mdi-shield-account</v-icon>
            <span class="text-h6">Oświadczenia ZUS</span>
            <v-spacer></v-spacer>
            <v-btn
              v-if="!zusEditMode && mozeEdytowacRozliczenia"
              small text color="primary"
              @click="wejdzWEdycjeZus"
            >
              <v-icon left small>mdi-pencil</v-icon>Edytuj
            </v-btn>
          </v-card-title>
          <v-card-text>
            <v-alert v-if="!zusDeclarations && !zusLoading" type="info" outlined dense class="mb-3">
              Ten kierowca nie ma jeszcze wypełnionych oświadczeń ZUS. Zaznacz, co go dotyczy —
              od tego zależy, jakie dokumenty zobaczy w aplikacji.
            </v-alert>

            <div v-if="zusLoading" class="text-center py-3">
              <v-progress-circular indeterminate color="primary" size="24" />
            </div>

            <!-- PODGLĄD -->
            <v-row v-else-if="!zusEditMode" dense>
              <v-col v-for="pole in polaZus" :key="pole.klucz" cols="12" sm="6" md="4">
                <div class="zus-row">
                  <v-icon
                    size="18"
                    :color="zusDeclarations && zusDeclarations[pole.klucz] ? 'success' : 'grey lighten-1'"
                  >
                    {{ zusDeclarations && zusDeclarations[pole.klucz] ? 'mdi-check-circle' : 'mdi-minus-circle-outline' }}
                  </v-icon>
                  <span
                    class="ml-2"
                    :class="{ 'font-weight-medium': zusDeclarations && zusDeclarations[pole.klucz] }"
                  >{{ pole.etykieta }}</span>
                </div>
              </v-col>
              <v-col cols="12" sm="6" md="4">
                <div class="caption grey--text">Urząd skarbowy</div>
                <div>{{ (zusDeclarations && zusDeclarations.taxOffice) || '—' }}</div>
              </v-col>
              <v-col cols="12" sm="6" md="4">
                <div class="caption grey--text">Oddział NFZ</div>
                <div>{{ (zusDeclarations && zusDeclarations.nfzBranch) || '—' }}</div>
              </v-col>
            </v-row>

            <!-- EDYCJA -->
            <v-row v-else dense>
              <v-col v-for="pole in polaZus" :key="pole.klucz" cols="12" sm="6" md="4">
                <v-switch
                  v-model="zusForm[pole.klucz]"
                  :label="pole.etykieta"
                  dense hide-details class="mt-0 mb-2"
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field v-model="zusForm.taxOffice" label="Urząd skarbowy" outlined dense />
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field v-model="zusForm.nfzBranch" label="Oddział NFZ" outlined dense />
              </v-col>
            </v-row>
          </v-card-text>
          <v-card-actions v-if="zusEditMode">
            <v-spacer></v-spacer>
            <v-btn text @click="zusEditMode = false">Anuluj</v-btn>
            <v-btn color="primary" :loading="zusSaving" @click="zapiszZus">
              <v-icon left small>mdi-content-save</v-icon>Zapisz oświadczenia
            </v-btn>
          </v-card-actions>
        </v-card>

        <!-- Korekta twardych danych — tylko head admin (właściciel) -->
        <v-card v-if="isOwner" class="mt-4" outlined>
          <v-card-title>
            <v-icon left color="warning">mdi-shield-edit</v-icon>
            <span class="text-h6">Korekta twardych danych (head admin)</span>
          </v-card-title>
          <v-card-text>
            <v-alert type="warning" outlined dense class="mb-3">
              Dane identyfikacyjne zapisywane przy rejestracji. Korekta jest logowana w audycie. Nie dotyczy rozliczeń ani wypłat.
            </v-alert>
            <v-row>
              <v-col cols="12" md="6">
                <v-text-field v-model="hardData.pesel" label="PESEL" outlined dense maxlength="11" hint="11 cyfr (puste pole = wyczyść)" persistent-hint />
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field v-model="hardData.dateOfBirth" label="Data urodzenia" type="date" outlined dense />
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field v-model="hardData.citizenship" label="Obywatelstwo" outlined dense />
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field v-model="hardData.mainLanguage" label="Język główny" outlined dense />
              </v-col>
            </v-row>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="warning" :loading="savingHardData" @click="saveHardData">
              <v-icon left small>mdi-content-save-alert</v-icon>Zapisz korektę
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-tab-item>

      <!-- Dokumenty Tab -->
      <v-tab-item>
        <v-card>
          <v-card-title>
            <span class="text-h6">{{ $t('driverDetails.driverDocuments') }}</span>
            <v-spacer></v-spacer>
            <v-btn color="primary" @click="openUploadDocDialog">
              <v-icon left small>mdi-upload</v-icon>
              {{ $t('driverDetails.addDocument') }}
            </v-btn>
          </v-card-title>
          <v-card-text>
            <v-data-table
              :headers="driverDocumentsHeaders"
              :items="mergedDriverDocuments"
              class="elevation-1"
              :items-per-page="10"
              :no-data-text="$t('driverDetails.noDocuments')"
              :loading="loadingDriverDocuments || loadingOnboardingDocuments"
            >
              <template v-slot:[`item.docName`]="{ item: documentRecord }">
                <v-icon left small :color="documentRecord.exists
                  ? (documentRecord.source === 'template' ? 'indigo' : 'teal')
                  : 'grey lighten-1'">
                  {{ documentRecord.exists
                    ? (documentRecord.source === 'template' ? 'mdi-file-document-outline' : 'mdi-file-check-outline')
                    : 'mdi-file-remove-outline' }}
                </v-icon>
                {{ documentRecord.docName }}
              </template>
              <template v-slot:[`item.status`]="{ item: documentRecord }">
                <v-chip
                  v-if="!documentRecord.exists"
                  small
                  color="error"
                  text-color="white"
                >
                  <v-icon left x-small>mdi-close-circle</v-icon>
                  {{ $t('driverDetails.statusMissing') }}
                </v-chip>
                <div v-else-if="documentRecord.source === 'onboarding'">
                  <v-chip
                    small
                    :color="documentRecord.status === 'VERIFIED' ? 'success' : documentRecord.status === 'REJECTED' ? 'error' : 'warning'"
                    text-color="white"
                  >
                    <v-icon left x-small>{{ documentRecord.status === 'VERIFIED' ? 'mdi-check-circle' : documentRecord.status === 'REJECTED' ? 'mdi-close-circle' : 'mdi-clock-outline' }}</v-icon>
                    {{ documentRecord.status === 'VERIFIED' ? $t('driverDetails.statusAddedVerified') : documentRecord.status === 'REJECTED' ? $t('driverDetails.statusRejected') : $t('driverDetails.statusAddedPending') }}
                  </v-chip>
                  <div v-if="documentRecord.status === 'REJECTED' && documentRecord.raw?.rejectionReason" class="caption red--text mt-1" style="max-width: 240px;">
                    <v-icon x-small color="red">mdi-information-outline</v-icon>
                    {{ documentRecord.raw.rejectionReason }}
                  </div>
                </div>
                <v-chip
                  v-else
                  small
                  :color="getDriverDocumentStatusColor(documentRecord.raw)"
                  text-color="white"
                >
                  {{ getDriverDocumentStatusText(documentRecord.raw) }}
                </v-chip>
              </template>
              <template v-slot:[`item.expiryDate`]="{ item: documentRecord }">
                <v-chip
                  v-if="documentRecord.expiryDate"
                  small
                  :color="expiryChipColor(documentRecord.expiryDate)"
                  text-color="white"
                >
                  <v-icon left x-small>{{ expiryChipIcon(documentRecord.expiryDate) }}</v-icon>
                  {{ formatDate(documentRecord.expiryDate) }}{{ expiryLabel(documentRecord.expiryDate) }}
                </v-chip>
                <span v-else class="grey--text">—</span>
              </template>
              <template v-slot:[`item.createdAt`]="{ item: documentRecord }">
                <span v-if="documentRecord.createdAt">{{ formatDate(documentRecord.createdAt) }}</span>
                <span v-else class="grey--text">—</span>
              </template>
              <template v-slot:[`item.actions`]="{ item: documentRecord }">
                <v-btn
                  v-if="!documentRecord.exists"
                  small
                  color="primary"
                  @click="openUploadDocDialogForType(documentRecord.documentType)"
                >
                  <v-icon left small>mdi-upload</v-icon>
                  {{ $t('driverDetails.addBtn') }}
                </v-btn>
                <template v-else>
                  <v-btn
                    v-if="documentRecord.source === 'onboarding' && documentRecord.status === 'REJECTED'"
                    small
                    color="primary"
                    class="mr-2"
                    @click="openUploadDocDialogForType(documentRecord.documentType)"
                  >
                    <v-icon left small>mdi-upload</v-icon>
                    {{ $t('driverDetails.reAddBtn') }}
                  </v-btn>
                  <v-btn
                    small
                    color="info"
                    @click="previewAnyDocument(documentRecord)"
                    :loading="previewingDocuments.includes(documentRecord.id)"
                    class="mr-2"
                  >
                    <v-icon left small>mdi-eye</v-icon>
                    {{ $t('driverDetails.previewBtn') }}
                  </v-btn>
                  <v-btn
                    small
                    color="primary"
                    @click="downloadAnyDocument(documentRecord)"
                    :loading="downloadingDocuments.includes(documentRecord.id)"
                    class="mr-2"
                  >
                    <v-icon left small>mdi-download</v-icon>
                    {{ $t('driverDetails.downloadBtn') }}
                  </v-btn>
                  <v-btn
                    v-if="canAcceptDocument(documentRecord)"
                    small
                    color="success"
                    @click="acceptAnyDocument(documentRecord)"
                    :loading="processingDocuments.includes(documentRecord.id)"
                    class="mr-2"
                  >
                    <v-icon left small>mdi-check</v-icon>
                    {{ $t('driverDetails.acceptBtn') }}
                  </v-btn>
                  <v-btn
                    v-if="canRejectDocument(documentRecord)"
                    small
                    color="warning"
                    @click="rejectAnyDocument(documentRecord)"
                    :loading="processingDocuments.includes(documentRecord.id)"
                    class="mr-2"
                  >
                    <v-icon left small>mdi-close-circle</v-icon>
                    {{ $t('driverDetails.rejectBtn') }}
                  </v-btn>
                  <v-btn
                    v-if="documentRecord.source === 'template' && !documentRecord.raw.deleted"
                    small
                    color="error"
                    @click="deleteDriverDocument(documentRecord.raw)"
                    :loading="processingDocuments.includes(documentRecord.id)"
                  >
                    <v-icon left small>mdi-delete</v-icon>
                    {{ $t('driverDetails.deleteBtn') }}
                  </v-btn>
                </template>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>

        <!-- Upload Driver Document Dialog -->
        <v-dialog v-model="uploadDocDialog" max-width="600" persistent>
          <v-card>
            <v-card-title>
              <v-icon left>mdi-file-upload-outline</v-icon>
              {{ $t('driverDetails.uploadDocTitle') }}
            </v-card-title>
            <v-card-text>
              <v-select
                v-model="uploadDocForm.documentType"
                :items="uploadDocTypeOptions"
                :label="$t('driverDetails.uploadDocType')"
                item-text="text"
                item-value="value"
                outlined
                dense
                :rules="[v => !!v || $t('driverDetails.uploadDocTypeRequired')]"
              />
              <v-text-field
                v-model="uploadDocForm.documentNumber"
                :label="$t('driverDetails.uploadDocNumber')"
                outlined
                dense
              />
              <v-text-field
                v-if="uploadMaWaznosc"
                v-model="uploadDocForm.expiryDate"
                :label="$t('driverDetails.uploadExpiryDate')"
                type="date"
                outlined
                dense
                :rules="[v => !!v || $t('driverDetails.uploadExpiryRequired'), v => !v || new Date(v) > new Date(new Date().toDateString()) || $t('driverDetails.uploadExpiryFuture')]"
                required
              />
              <v-file-input
                v-model="uploadDocForm.file"
                :label="$t('driverDetails.uploadFileLabel')"
                accept="image/jpeg,image/png,image/webp,application/pdf"
                prepend-icon="mdi-paperclip"
                outlined
                dense
                show-size
                :rules="[v => !!v || $t('driverDetails.uploadFileRequired')]"
              />
              <v-alert v-if="uploadDocError" type="error" dense outlined>
                {{ uploadDocError }}
              </v-alert>
            </v-card-text>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn text :disabled="uploadingDoc" @click="uploadDocDialog = false">{{ $t('common.cancel') }}</v-btn>
              <v-btn
                color="primary"
                :loading="uploadingDoc"
                :disabled="!uploadDocForm.documentType || !uploadDocForm.file || (uploadMaWaznosc && !uploadDocForm.expiryDate)"
                @click="submitUploadDocument"
              >
                <v-icon left small>mdi-upload</v-icon>
                {{ $t('driverDetails.addBtn') }}
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>

        <!-- Onboarding Document Preview Dialog -->
        <v-dialog v-model="onboardingDocPreviewDialog" max-width="800">
          <v-card>
            <v-card-title>{{ $t('driverDetails.docPreviewTitle') }}</v-card-title>
            <v-card-text class="text-center">
              <iframe v-if="onboardingDocPreviewUrl && onboardingDocPreviewMime === 'application/pdf'" :src="onboardingDocPreviewUrl" style="width: 100%; height: 600px; border: none;"></iframe>
              <img v-else-if="onboardingDocPreviewUrl" :src="onboardingDocPreviewUrl" style="max-width: 100%; max-height: 600px;" />
            </v-card-text>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn text @click="onboardingDocPreviewDialog = false">{{ $t('common.close') }}</v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>

        <!-- Dokumenty wewnętrzne — tylko dla biura, kierowca ich nie widzi -->
        <internal-documents-section v-if="currentDriverId" :driver-id="currentDriverId" class="mt-4" />
      </v-tab-item>

      <!-- Faktury Tab -->
      <v-tab-item>
        <v-card>
          <v-card-text>
            <!-- Here, you can add a table or list to display invoices -->
            <v-data-table
              :headers="invoiceHeaders"
              :items="invoices"
              class="elevation-1"
              :loading="loadingInvoices"
              :search="searchInvoices"
              :options.sync="optionsInvoices"
              :server-items-length="totalItemsInvoices"
            >
              <template v-slot:top>
                <v-toolbar flat>
                  <v-toolbar-title>{{ $t('driverDetails.invoicesTitle') }}</v-toolbar-title>
                  <v-divider class="mx-4" inset vertical></v-divider>
                  <v-spacer></v-spacer>
                </v-toolbar>
                <div>
                  <v-text-field
                    v-model="searchInvoices"
                    :label="$t('driverDetails.searchLabel')"
                    class="mx-4"
                  ></v-text-field>
                </div>
              </template>
              <template v-slot:item.brutto23="{ item }">{{ nonZero(item.brutto23) }}</template>
              <template v-slot:item.netto23="{ item }">{{ nonZero(item.netto23) }}</template>
              <template v-slot:item.brutto8="{ item }">{{ nonZero(item.brutto8) }}</template>
              <template v-slot:item.netto8="{ item }">{{ nonZero(item.netto8) }}</template>
              <template v-slot:item.brutto5="{ item }">{{ nonZero(item.brutto5) }}</template>
              <template v-slot:item.netto5="{ item }">{{ nonZero(item.netto5) }}</template>
              <template v-slot:item.brutto0="{ item }">{{ nonZero(item.brutto0) }}</template>
              <template v-slot:item.netto0="{ item }">{{ nonZero(item.netto0) }}</template>
              <template v-slot:item.returnToDriver="{ item }">{{ nonZero(item.returnToDriver) }}</template>
              <template v-slot:item.status="{ item }">
                <v-chip
                  small
                  :color="invoiceStatusChipColor(item)"
                  text-color="white"
                >
                  {{ invoiceStatusChipLabel(item) }}
                </v-chip>
              </template>
              <template v-slot:item.preview="{ item }">
                <v-btn small color="info" @click="openInvoicePreview(item.id)">
                  <v-icon small>mdi-eye</v-icon>
                </v-btn>
              </template>
            </v-data-table>

            <v-dialog v-model="invoicePreviewDialog" fullscreen hide-overlay transition="dialog-bottom-transition">
              <v-card tile>
                <v-toolbar dark color="#1A2433" flat dense>
                  <v-toolbar-title class="text-subtitle-1">
                    Podgląd faktury
                    <span v-if="invoicePreviewData.name"> — {{ invoicePreviewData.name }}</span>
                  </v-toolbar-title>
                  <v-spacer />
                  <v-btn icon dark @click="invoicePreviewDialog = false">
                    <v-icon>mdi-close</v-icon>
                  </v-btn>
                </v-toolbar>
                <v-row no-gutters style="height: calc(100vh - 48px);">
                  <v-col cols="12" md="8" style="background:#222; height:100%;">
                    <div v-if="invoicePreviewLoading" class="d-flex justify-center align-center" style="height:100%;">
                      <v-progress-circular indeterminate color="white" size="48" />
                    </div>
                    <iframe v-else-if="invoicePreviewType === 'pdf' && invoicePreviewSrc"
                      :src="invoicePreviewSrc" style="width:100%; height:100%; border:none;" />
                    <div v-else-if="invoicePreviewType === 'image' && invoicePreviewSrc" class="d-flex justify-center align-center" style="height:100%;">
                      <img :src="invoicePreviewSrc" style="max-width:100%; max-height:100%;" />
                    </div>
                    <div v-else class="d-flex justify-center align-center white--text" style="height:100%;">
                      Brak pliku faktury
                    </div>
                  </v-col>
                  <v-col cols="12" md="4" class="pa-4" style="overflow-y:auto; height:100%;">
                    <div v-if="invoicePreviewData.id">
                      <div class="text-overline">Data</div>
                      <div class="mb-2">{{ invoicePreviewData.date }}</div>

                      <div class="text-overline">Opis</div>
                      <div class="mb-2">{{ invoicePreviewData.name || '—' }}</div>

                      <div class="text-overline">Brutto razem</div>
                      <div class="mb-2 text-h6">{{ invoicePreviewData.bruttoTotal }} zł</div>

                      <v-divider class="my-2" />

                      <div v-for="rate in [23, 8, 5, 0]" :key="rate">
                        <template v-if="(invoicePreviewData['brutto' + rate] || 0) > 0">
                          <div class="d-flex justify-space-between">
                            <span>Brutto {{ rate }}%:</span>
                            <strong>{{ (invoicePreviewData['brutto' + rate] / 100).toFixed(2) }} zł</strong>
                          </div>
                          <div class="d-flex justify-space-between">
                            <span>Netto {{ rate }}%:</span>
                            <strong>{{ (invoicePreviewData['netto' + rate] / 100).toFixed(2) }} zł</strong>
                          </div>
                          <v-divider class="my-1" />
                        </template>
                      </div>

                      <div class="d-flex justify-space-between mt-2">
                        <span>Zwrot dla kierowcy:</span>
                        <strong>{{ ((invoicePreviewData.driverReturn || 0) / 100).toFixed(2) }} zł</strong>
                      </div>
                      <div class="d-flex justify-space-between">
                        <span>Procent zwrotu:</span>
                        <strong>{{ invoicePreviewData.driverReturnPercent || 0 }}%</strong>
                      </div>

                      <v-divider class="my-3" />
                      <div class="text-overline">Status</div>
                      <v-chip small :color="invoiceStatusChipColor(invoicePreviewData)" text-color="white">
                        {{ invoiceStatusChipLabel(invoicePreviewData) }}
                      </v-chip>

                      <template v-if="invoicePreviewData.deleted && invoicePreviewData.rejectionReason">
                        <v-alert type="error" outlined dense class="mt-3 mb-0" icon="mdi-close-circle-outline">
                          <div class="text-overline mb-1" style="line-height:1;">Powód odrzucenia</div>
                          <div>{{ invoicePreviewData.rejectionReason }}</div>
                        </v-alert>
                      </template>
                    </div>
                  </v-col>
                </v-row>
              </v-card>
            </v-dialog>
          </v-card-text>
        </v-card>
      </v-tab-item>

      <!-- Bonusy Tab -->
      <v-tab-item>
      <v-dialog v-model="dialogBonus" max-width="600px">
      <v-card>
        <v-card-title>
          <span class="headline">{{ $t('driverDetails.bonusFormTitle') }}</span>
        </v-card-title>

        <v-card-text>
          <v-form ref="formBonus" v-model="validBonus" @submit.prevent="submitBonus">
            <v-row>
              <v-col cols="12">
                <v-file-input
                  v-model="bonus.file"
                  :label="$t('driverDetails.bonusLabels.file')"
                  outlined
                  accept="image/*,.pdf, image/*"
                  @change="previewFile"
                />
                <iframe
                  v-if="bonus.preview"
                  :src="bonus.preview"
                  style="width: 100%; height: 400px"
                  frameborder="0"
                ></iframe>
              </v-col>
              <!-- Date -->
              <v-col cols="12" md="6">
                <v-menu
                  ref="dateMenu"
                  v-model="menuDate"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  min-width="auto"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="bonus.date"
                      :label="$t('driverDetails.bonusLabels.date')"
                      outlined
                      readonly
                      v-bind="attrs"
                      v-on="on"
                      :rules="[validateRequired]"
                    />
                  </template>
                  <v-date-picker
                    v-model="bonus.date"
                    no-title
                    scrollable
                    show-week
                    first-day-of-week="1"
                    locale-first-day-of-year="4"
                    :locale="$i18n.locale"
                    @input="menuDate = false"
                  />
                </v-menu>
              </v-col>

              <!-- Status -->
              <v-col cols="12" md="6">
                <v-select
                  v-model="bonus.status"
                  :items="statusOptions"
                  :label="$t('driverDetails.bonusLabels.status')"
                  outlined
                  required
                  :rules="[validateRequired]"
                />
              </v-col>

              <!-- Status -->
              <v-col cols="12" md="6">
                <v-select
                  v-model="bonus.type"
                  :items="bonusType"
                  :label="$t('driverDetails.bonusLabels.type')"
                  outlined
                  required
                  :rules="[validateRequired]"
                />
              </v-col>
              <!-- Return Cost -->
              <v-col cols="12" md="12">
                <v-text-field
                  v-model="bonus.name"
                  :label="$t('driverDetails.bonusLabels.description')"
                  outlined
                  required
                  type="text"
                  :rules="[validateRequired]"
                />
              </v-col>
              <!-- Return Cost -->
              <v-col cols="12" md="12">
                <v-text-field
                  v-model.number="bonus.price"
                  :label="$t('driverDetails.bonusLabels.amount')"
                  outlined
                  required
                  type="number"
                  :rules="[validateRequired, validatePositiveNumber]"
                />
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>

        <v-card-actions>
                <v-spacer></v-spacer>
          <!-- Cancel Button -->
          <v-btn text @click="dialogBonus = false">{{ $t('common.cancel') }}</v-btn>
          <!-- Submit Button -->
          <v-btn color="primary" :disabled="!validBonus" @click="submitBonus">
            {{ $t('common.save') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
        <v-card>
          <v-card-text>
           <v-data-table
          :headers="bonusesHeaders"
          :items="bonuses"
          class="elevation-1"
          :loading="loadingBonuses"
          :search="searchBonuses"
          :options.sync="optionsBonuses"
          :server-items-length="totalItemsBonuses"
        >
          <template v-slot:top>
            <v-toolbar flat>
              <v-toolbar-title>{{ $t('driverDetails.bonusesTitle') }}</v-toolbar-title>
              <v-divider class="mx-4" inset vertical></v-divider>
              <v-spacer></v-spacer>                
              <v-btn
                class="primary"
                @click="((dialogBonus = true), (currentBonusId = null))"
                >{{ $t('common.add') }}</v-btn
              >
            </v-toolbar>
            <div>
              <v-text-field
                v-model="searchBonuses"
                :label="$t('driverDetails.searchLabel')"
                class="mx-4"
              ></v-text-field>
            </div>
          </template>
          <template v-slot:item.type="{ item }">
            <v-chip
              small
              :color="item.type === 'POSITIVE' ? 'success' : (item.type === 'RENT' ? 'orange' : 'error')"
              text-color="white"
            >
              <v-icon left x-small>
                {{ item.type === 'POSITIVE' ? 'mdi-plus' : (item.type === 'RENT' ? 'mdi-key-variant' : 'mdi-minus') }}
              </v-icon>
              {{ item.type === 'POSITIVE' ? $t('driverDetails.bonusTypePositive') : (item.type === 'RENT' ? $t('driverDetails.bonusTypeRent') : $t('driverDetails.bonusTypeNegative')) }}
            </v-chip>
          </template>
          <template v-slot:item.price="{ item }">
            <span
              class="font-weight-bold"
              :class="item.type === 'POSITIVE' ? 'success--text' : (item.type === 'RENT' ? 'orange--text' : 'error--text')"
            >
              {{ item.type === 'POSITIVE' ? '+' : '−' }}{{ Number(item.price).toFixed(2) }} PLN
            </span>
          </template>
          <template v-slot:item.download="{ item }">
        <v-btn v-if="item.fileId" class="secondary" @click="downloadFile(item.fileId)">
          {{ $t('driverDetails.downloadFile') }}
        </v-btn>
      </template>
          <template v-slot:item.deleteBonus="{ item }">
            <v-btn class="error" @click="deleteBonus(item.id)"
              ><v-icon small class="pt-1">mdi-delete</v-icon></v-btn
            >
          </template>
            </v-data-table>

            <!-- Kaucja kierowcy (trzymana przez BTP jako strona neutralna) -->
            <v-card outlined class="mt-4">
              <v-card-title class="text-subtitle-1">
                <v-icon left color="teal">mdi-shield-lock-outline</v-icon>
                Kaucja (BTP)
                <v-spacer />
                <v-btn v-if="!activeDeposit" color="primary" small @click="createDeposit">
                  <v-icon left small>mdi-plus</v-icon>Ustaw kaucję
                </v-btn>
                <template v-else>
                  <v-btn v-if="!activeDeposit.paidUpfrontAmount" color="teal" dark small class="mr-2"
                         @click="markDepositPaidDialog(activeDeposit)">
                    <v-icon left small>mdi-cash-check</v-icon>Wpłacona jednorazowo
                  </v-btn>
                  <v-btn color="warning" small @click="settleDepositDialog(activeDeposit)">
                    <v-icon left small>mdi-cash-refund</v-icon>Rozlicz / zwróć
                  </v-btn>
                </template>
              </v-card-title>
              <v-card-text>
                <div v-if="!deposits.length" class="grey--text">
                  Brak kaucji. Raty schodzą z wypłat automatycznie (co tydzień), zwrot przy zdaniu auta dopisuje się do najbliższej wypłaty.
                  Jeśli kierowca wpłacił całość z góry, zaznacz to przy zakładaniu kaucji — wtedy raty nie powstają.
                </div>
                <div v-for="d in deposits" :key="d.id" class="mb-3">
                  <div>
                    <v-chip x-small dark class="mr-2"
                      :color="d.status === 'ACTIVE' ? 'orange' : d.status === 'COLLECTED' ? 'teal' : 'success'">
                      {{ d.status === 'ACTIVE' ? 'Zbierana' : d.status === 'COLLECTED' ? 'Zebrana w całości' : 'Rozliczona' }}
                    </v-chip>
                    <b>{{ (d.totalAmount / 100).toFixed(2) }} zł</b>
                    <span v-if="d.paidUpfrontAmount">wpłacona jednorazowo</span>
                    <span v-else>w {{ d.installments }} ratach tyg.</span>
                    · zebrane: <b>{{ (d.collectedAmount / 100).toFixed(2) }} zł</b>
                    <span v-if="d.car"> · auto: {{ d.car.plate }}</span>
                    <span v-if="d.note" class="grey--text"> · {{ d.note }}</span>
                  </div>
                  <div v-if="d.paidUpfrontAmount" class="caption teal--text mt-1">
                    <v-icon x-small color="teal">mdi-cash-check</v-icon>
                    wpłata {{ (d.paidUpfrontAmount / 100).toFixed(2) }} zł
                    {{ d.paidUpfrontMethod === 'CASH' ? 'gotówką' : 'przelewem' }}
                    <span v-if="d.paidUpfrontAt"> · {{ new Date(d.paidUpfrontAt).toLocaleDateString('pl-PL') }}</span>
                    <span v-if="d.collectedFromInstallments" class="grey--text">
                      · wcześniej ratami: {{ (d.collectedFromInstallments / 100).toFixed(2) }} zł
                    </span>
                    <span v-if="d.paidUpfrontBy" class="grey--text"> · przyjął: {{ d.paidUpfrontBy }}</span>
                  </div>
                  <div v-if="d.status === 'SETTLED'" class="caption mt-1">
                    <span v-if="d.damageAmount" class="error--text">potrącone szkody: {{ (d.damageAmount / 100).toFixed(2) }} zł ({{ d.damageNote }})</span>
                    <span v-if="d.refundedAmount" class="success--text ml-2">zwrot do wypłaty: {{ (d.refundedAmount / 100).toFixed(2) }} zł</span>
                  </div>
                  <div class="caption grey--text mt-1">
                    <span v-for="b in d.installmentBonuses" :key="b.id" class="mr-3">
                      {{ b.name }}: {{ (b.price / 100).toFixed(2) }} zł
                      <v-icon x-small :color="b.status === 'PAID' ? 'success' : b.status === 'PAYOUT_CREATED' ? 'teal' : 'orange'">
                        {{ b.status === 'PENDING' ? 'mdi-clock-outline' : 'mdi-check' }}
                      </v-icon>
                    </span>
                  </div>
                </div>
              </v-card-text>
            </v-card>
          </v-card-text>
        </v-card>
      </v-tab-item>

    <!-- Bonusy Tab -->
      <v-tab-item>
        <v-card>
          <v-card-text>
           <v-data-table
          :headers="rentsHeaders"
          :items="rents"
          class="elevation-1"
          :loading="loadingRent"
          :search="searchRent"
          :options.sync="optionsRent"
          :server-items-length="totalItemsRent"
        >
          <template v-slot:top>
            <v-toolbar flat>
              <v-toolbar-title>{{ $t('driverDetails.carRentalTitle') }}</v-toolbar-title>
              <v-divider class="mx-4" inset vertical></v-divider>
              <v-spacer></v-spacer>
            </v-toolbar>
            <div>
              <v-text-field
                v-model="searchRent"
                :label="$t('driverDetails.searchLabel')"
                class="mx-4"
              ></v-text-field>
            </div>
          </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-tab-item>

      <!-- Imported Data Tab -->
      <v-tab-item>
        <v-card>
          <v-card-text>
            <v-data-table
          :headers="importedHeaders"
          :items="imported"
          class="elevation-1"
          :loading="loadingImport"
          :search="searchImport"
          :options.sync="optionsImport"
          :server-items-length="totalItemsImport"
          show-expand
          single-expand
          :expanded.sync="expandedImport"
          item-key="id"
        >
          <template v-slot:top>
            <v-toolbar flat>
              <v-toolbar-title>{{ $t('driverDetails.importedDataTitle') }}</v-toolbar-title>
              <v-divider class="mx-4" inset vertical></v-divider>
              <v-spacer></v-spacer>
            </v-toolbar>
            <div>
              <v-text-field
                v-model="searchImport"
                :label="$t('driverDetails.searchLabel')"
                class="mx-4"
              ></v-text-field>
            </div>
          </template>
          <template v-slot:expanded-item="{ headers, item }">
            <td :colspan="headers.length" class="pa-4">
              <v-row>
                <!-- Equation & Calculation Card -->
                <v-col cols="12" md="6" v-if="item.equation">
                  <v-card outlined class="mb-2">
                    <v-card-title class="text-subtitle-1 primary--text py-2">
                      <v-icon left color="primary" small>mdi-calculator</v-icon>
                      {{ $t('driverDetails.equationTitle') }}
                      <v-spacer></v-spacer>
                      <v-chip small color="primary" class="mr-1">{{ item.equation.name }}</v-chip>
                      <v-chip small outlined>{{ item.equation.type }}</v-chip>
                    </v-card-title>
                    <v-card-text>
                      <!-- Equation Formula -->
                      <v-alert dense color="blue-grey lighten-4" class="mb-3" style="font-family: monospace;">
                        <strong>{{ $t('driverDetails.formula') }}:</strong> {{ item.equation.equationStr }}
                      </v-alert>

                      <!-- Variables Used -->
                      <div class="mb-3">
                        <div class="text-subtitle-2 mb-2">
                          <v-icon small color="primary">mdi-variable</v-icon>
                          {{ $t('driverDetails.usedVariables') }}:
                        </div>
                        <v-row dense>
                          <v-col cols="6" v-for="(value, key) in getEquationFields(item.rawData, item.equation)" :key="key">
                            <v-chip small :color="getFieldColor(key, item.equation)" text-color="white" class="mr-1">
                              {{ key.replace(/_/g, ' ') }}
                            </v-chip>
                            <span class="font-weight-bold">= {{ value }}</span>
                          </v-col>
                          <v-col cols="6" v-for="(cv, idx) in (item.equation.customNumbers || [])" :key="'cv-'+idx">
                            <v-chip small color="purple" text-color="white" class="mr-1">
                              {{ cv.label }}
                            </v-chip>
                            <span class="font-weight-bold">= {{ cv.value }}</span>
                            <v-chip x-small outlined class="ml-1">{{ $t('driverDetails.constant') }}</v-chip>
                          </v-col>
                        </v-row>
                      </div>

                      <!-- Calculation Steps -->
                      <div class="mb-3">
                        <div class="text-subtitle-2 mb-2">
                          <v-icon small color="primary">mdi-format-list-numbered</v-icon>
                          {{ $t('driverDetails.calculationSteps') }}:
                        </div>
                        <v-timeline dense align-top>
                          <v-timeline-item
                            v-for="(step, idx) in getCalculationSteps(item.equation, item.rawData)"
                            :key="idx"
                            :color="step.isFinal ? 'success' : (step.result !== null ? 'info' : 'grey')"
                            small
                          >
                            <div>
                              <div class="text-caption font-weight-bold" :class="step.isFinal ? 'success--text' : ''">
                                {{ step.label }}
                              </div>
                              <code
                                class="d-block pa-2 calc-step-code"
                                :class="step.isFinal ? 'calc-step-final' : 'calc-step-normal'"
                              >
                                {{ step.expression }}
                              </code>
                            </div>
                          </v-timeline-item>
                        </v-timeline>
                      </div>

                      <!-- Result -->
                      <v-card color="success" dark class="pa-3">
                        <div class="text-caption">{{ $t('driverDetails.calculationResult') }}:</div>
                        <div class="text-h5 font-weight-bold">{{ item.calculated }} PLN</div>
                      </v-card>
                    </v-card-text>
                  </v-card>
                </v-col>

                <!-- Raw CSV Data Card -->
                <v-col cols="12" :md="item.equation ? 6 : 12">
                  <v-card outlined>
                    <v-card-title class="text-subtitle-1 primary--text py-2">
                      <v-icon left color="primary" small>mdi-file-delimited</v-icon>
                      {{ $t('driverDetails.csvData') }}
                    </v-card-title>
                    <v-card-text style="max-height: 400px; overflow-y: auto;">
                      <div v-if="item.rawData">
                        <v-simple-table dense>
                          <template v-slot:default>
                            <thead>
                              <tr>
                                <th>{{ $t('driverDetails.field') }}</th>
                                <th>{{ $t('driverDetails.value') }}</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr v-for="(value, key) in getOtherFields(item.rawData, item.equation)" :key="key">
                                <td class="text-caption" style="width: 40%;">
                                  {{ key.replace(/_/g, ' ') }}
                                </td>
                                <td class="font-weight-medium">
                                  {{ value || '-' }}
                                </td>
                              </tr>
                            </tbody>
                          </template>
                        </v-simple-table>
                      </div>
                      <v-alert v-else type="info" dense>
                        {{ $t('driverDetails.noDataToDisplay') }}
                      </v-alert>
                    </v-card-text>
                  </v-card>
                </v-col>
              </v-row>
            </td>
          </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-tab-item>

      <!-- Payouts Tab -->
      <v-tab-item>
        <v-card>
          <v-card-text>
            <v-data-table
              :headers="payoutsHeaders"
              :items="payouts"
              class="elevation-1"
              :loading="loadingPayouts"
              :search="searchPayouts"
              :options.sync="optionsPayouts"
              :server-items-length="totalItemsPayouts"
              show-expand
              single-expand
              :expanded.sync="expandedPayouts"
              item-key="id"
            >
              <template v-slot:top>
                <v-toolbar flat>
                  <v-toolbar-title>{{ $t('driverDetails.payoutsTitle') }}</v-toolbar-title>
                  <v-divider class="mx-4" inset vertical></v-divider>
                  <v-spacer></v-spacer>
                </v-toolbar>
                <div>
                  <v-text-field
                    v-model="searchPayouts"
                    :label="$t('driverDetails.searchLabel')"
                    class="mx-4"
                  ></v-text-field>
                </div>
              </template>
              <template v-slot:[`item.date`]="{ item: payoutItem }">
                <div class="d-flex align-center" style="gap: 6px;">
                  <span>{{ formatDateShort(payoutItem.date) }}</span>
                  <v-chip
                    v-if="weekNumber(payoutItem.date)"
                    x-small
                    color="#2A3553"
                    text-color="#9DB2E8"
                    title="Tydzień rozliczeniowy (pon.–niedz.)"
                  >
                    Tydz. {{ weekNumber(payoutItem.date) }} · {{ weekRange(payoutItem.date) }}
                  </v-chip>
                </div>
              </template>
              <template v-slot:[`item.status`]="{ item: payoutItem }">
                <v-chip
                  small
                  :color="getPayoutStatusColor(payoutItem)"
                  text-color="white"
                >
                  {{ getPayoutStatusText(payoutItem) }}
                </v-chip>
              </template>
              <template v-slot:[`item.invoiceAction`]="{ item: payoutItem }">
                <div class="d-flex align-center justify-center">
                  <template v-if="payoutItem.isInvoiceUploaded">
                    <v-chip small color="green" text-color="white" class="mr-1">{{ $t('driverDetails.uploaded') }}</v-chip>
                    <v-btn v-if="payoutItem.invoiceFileId" x-small color="blue" dark @click="previewSettlementInvoice(payoutItem.invoiceFileId)">
                      <v-icon x-small>mdi-eye</v-icon>
                    </v-btn>
                  </template>
                  <template v-else>
                    <v-btn x-small color="primary" dark @click="openSettlementInvoiceUpload(payoutItem.id)">
                      <v-icon x-small class="mr-1">mdi-file-upload</v-icon>{{ $t('driverDetails.uploadInvoice') }}
                    </v-btn>
                  </template>
                </div>
              </template>
              <template v-slot:expanded-item="{ headers, item }">
                <td :colspan="headers.length" class="pa-4">
                  <v-row>
                    <!-- Summary Card -->
                    <v-col cols="12">
                      <v-card outlined class="mb-3">
                        <v-card-title class="text-subtitle-1 primary--text py-2">
                          <v-icon left color="primary" small>mdi-calculator</v-icon>
                          {{ $t('driverDetails.settlementSummary') }}
                          <v-spacer></v-spacer>
                          <v-chip small :color="item.isSettled ? 'success' : 'warning'" text-color="white">
                            {{ item.isSettled ? $t('driverDetails.paid') : $t('driverDetails.pendingStatus') }}
                          </v-chip>
                        </v-card-title>
                        <v-card-text>
                          <!-- Kalkulacja rozliczenia — identyczny widok jak w aplikacji kierowcy -->
                          <div class="section-label">{{ $t('driverDetails.calcView.title') }}</div>
                          <div class="dt-table mb-4">
                            <div class="dt-row">
                              <div class="dt-cell" style="flex: 1.6">{{ $t('driverDetails.calcView.appsEarnings') }}</div>
                              <div class="dt-cell text-right" style="color: #2DCB73">+{{ formatCurrency(item.appsEarnings) }}</div>
                            </div>
                            <div class="dt-row" v-if="item.totalIncomesReturn > 0">
                              <div class="dt-cell" style="flex: 1.6">{{ $t('driverDetails.calcView.additionalIncomes') }}</div>
                              <div class="dt-cell text-right" style="color: #2DCB73">+{{ formatCurrency(item.totalIncomesReturn) }}</div>
                            </div>
                            <div class="dt-row" v-if="item.totalBonusesPositiveRaw > 0">
                              <div class="dt-cell" style="flex: 1.6">{{ $t('driverDetails.calcView.bonusesPositive') }}</div>
                              <div class="dt-cell text-right" style="color: #2DCB73">+{{ formatCurrency(item.totalBonusesPositiveRaw) }}</div>
                            </div>
                            <div class="dt-row" v-if="item.totalBonusesNegativeRaw > 0">
                              <div class="dt-cell" style="flex: 1.6">{{ $t('driverDetails.calcView.bonusesNegative') }}</div>
                              <div class="dt-cell text-right" style="color: #EF5350">−{{ formatCurrency(item.totalBonusesNegativeRaw) }}</div>
                            </div>
                            <div class="dt-row" v-if="item.totalRentRaw > 0">
                              <div class="dt-cell" style="flex: 1.6">{{ $t('driverDetails.calcView.rent') }}</div>
                              <div class="dt-cell text-right" style="color: #EF5350">−{{ formatCurrency(item.totalRentRaw) }}</div>
                            </div>
                            <div class="dt-row" v-if="item.totalImportCash > 0">
                              <div class="dt-cell" style="flex: 1.6">
                                {{ $t('driverDetails.calcView.cashCollected') }}
                                <span class="caption">{{ $t('driverDetails.calcView.cashCollectedHint') }}</span>
                              </div>
                              <div class="dt-cell text-right" style="color: #F59E0B">−{{ formatCurrency(item.totalImportCash) }}</div>
                            </div>
                            <div class="dt-row" v-if="item.settlementCostRaw > 0">
                              <div class="dt-cell" style="flex: 1.6">{{ $t('driverDetails.calcView.settlementCost') }}</div>
                              <div class="dt-cell text-right" style="color: #EF5350">−{{ formatCurrency(item.settlementCostRaw) }}</div>
                            </div>
                            <div class="dt-row" v-if="item.administrationTaxRaw > 0">
                              <div class="dt-cell" style="flex: 1.6">{{ $t('driverDetails.calcView.administrationTax') }}</div>
                              <div class="dt-cell text-right" style="color: #EF5350">−{{ formatCurrency(item.administrationTaxRaw) }}</div>
                            </div>
                            <div class="dt-row" v-if="item.dailyAdvancesDeductedRaw > 0">
                              <div class="dt-cell" style="flex: 1.6">
                                {{ $t('driverDetails.calcView.dailyAdvances', { count: item.dailyAdvancesCount }) }}
                                <span class="caption">{{ $t('driverDetails.calcView.dailyAdvancesHint') }}</span>
                              </div>
                              <div class="dt-cell text-right" style="color: #F59E0B">−{{ formatCurrency(item.dailyAdvancesDeductedRaw) }}</div>
                            </div>
                            <div class="dt-row" v-if="item.dailyPayoutFeesCollectedRaw > 0">
                              <div class="dt-cell" style="flex: 1.6">
                                {{ $t('driverDetails.calcView.dailyPayoutFees') }}
                                <span class="caption">{{ $t('driverDetails.calcView.dailyPayoutFeesHint', { count: item.dailyAdvancesCount }) }}</span>
                              </div>
                              <div class="dt-cell text-right" style="color: #6E7691">{{ formatCurrency(item.dailyPayoutFeesCollectedRaw) }}</div>
                            </div>
                            <div class="dt-row" v-if="item.zusDeductionRaw > 0">
                              <div class="dt-cell" style="flex: 1.6">
                                {{ $t('driverDetails.calcView.zusContributions') }}
                                <span v-if="item.zusTypeName" class="caption">({{ item.zusTypeName }})</span>
                              </div>
                              <div class="dt-cell text-right" style="color: #EF5350">−{{ formatCurrency(item.zusDeductionRaw) }}</div>
                            </div>
                            <div class="dt-row" v-if="item.totalInvoicesReturnCapped > 0">
                              <div class="dt-cell" style="flex: 1.6">
                                {{ $t('driverDetails.calcView.maxCostReimbursement') }}
                                <span class="caption">{{ $t('driverDetails.calcView.maxCostReimbursementHint') }}</span>
                              </div>
                              <div class="dt-cell text-right" style="color: #2DCB73">+{{ formatCurrency(item.totalInvoicesReturnCapped) }}</div>
                            </div>
                            <div class="dt-row dt-total">
                              <div class="dt-cell" style="flex: 1.6"><b>{{ $t('driverDetails.calcView.totalLabel') }}</b></div>
                              <div class="dt-cell text-right"><b>{{ item.totalToPayout }}</b></div>
                            </div>
                          </div>

                          <!-- Szczegóły ZUS + rozbicie na przelewy (panel admina) -->
                          <v-alert dense text color="info" class="mb-3" v-if="item.zusDeductionRaw > 0 || ['FIFTY_FIFTY','STUDENT','CONNECTION','RENT','B2B_VAT','B2B_NO_VAT'].includes(item.cooperationTypeRaw) || item.totalNetto > 0">
                            <v-simple-table dense class="transparent">
                              <tbody>
                                <!-- SECTION 2: ZUS -->
                                <tr v-if="item.zusDeductionRaw > 0">
                                  <td colspan="2" class="pt-3"><v-divider></v-divider></td>
                                </tr>
                                <tr v-if="item.zusDeductionRaw > 0" class="grey lighten-4">
                                  <td class="text-body-2 font-weight-bold">POTRĄCENIE ZUS ({{ item.zusTypeName }})</td>
                                  <td></td>
                                </tr>
                                <template v-if="item.zus && item.zus.length > 0">
                                  <tr v-for="z in item.zus" :key="'zus-calc-' + z.id">
                                    <td class="text-body-2 pl-4">
                                      Chorobowe: {{ formatCurrency(z.chorobowe) }} |
                                      Społeczne: {{ formatCurrency(z.spoleczne) }} |
                                      Zaliczka PIT: {{ formatCurrency(z.funduszPracy) }} |
                                      Zdrowotne: {{ formatCurrency(z.zdrowotne) }}
                                    </td>
                                    <td class="text-right error--text font-weight-bold">-{{ formatCurrency(z.amount) }}</td>
                                  </tr>
                                </template>
                                <tr v-if="item.zusDeductionRaw > 0" class="font-weight-bold">
                                  <td class="text-body-2 pl-4">= Po odjęciu ZUS</td>
                                  <td class="text-right">{{ formatCurrency(item.afterZus) }}</td>
                                </tr>

                                <!-- SECTION 3: Rozbicie na przelewy (FIFTY_FIFTY/STUDENT/CONNECTION/RENT) -->
                                <template v-if="item.cooperationTypeRaw === 'FIFTY_FIFTY' || item.cooperationTypeRaw === 'STUDENT' || item.cooperationTypeRaw === 'CONNECTION' || item.cooperationTypeRaw === 'RENT'">
                                  <tr>
                                    <td colspan="2" class="pt-3"><v-divider></v-divider></td>
                                  </tr>
                                  <tr class="grey lighten-4">
                                    <td class="text-body-2 font-weight-bold">ROZBICIE NA PRZELEWY</td>
                                    <td></td>
                                  </tr>

                                  <!-- Przelew 1: Zleceniówka -->
                                  <tr v-if="item.zleceniowkaAmountRaw > 0">
                                    <td class="text-body-2 pl-4">
                                      1. Zleceniówka = min(kwota z konfiga, po ZUS)
                                    </td>
                                    <td class="text-right blue--text font-weight-bold">{{ item.zleceniowkaAmount }}</td>
                                  </tr>
                                  <tr v-if="item.zleceniowkaAmountRaw === 0">
                                    <td class="text-body-2 pl-4 grey--text">
                                      1. Zleceniówka (już wypłacona w tym mc)
                                    </td>
                                    <td class="text-right grey--text">0.00 PLN</td>
                                  </tr>
                                  <tr v-if="item.zleceniowkaAmountRaw > 0">
                                    <td class="text-body-2 pl-6 grey--text">Pozostałe po zleceniówce</td>
                                    <td class="text-right grey--text">{{ formatCurrency(item.remainingAfterZleceniowka) }}</td>
                                  </tr>

                                  <!-- Przelew 2: Zwrot kosztów -->
                                  <tr>
                                    <td class="text-body-2 pl-4">
                                      2. Zwrot kosztów = min(brutto faktur {{ formatCurrency(item.totalInvoicesBrutto) }}, pozostałe {{ item.zleceniowkaAmountRaw > 0 ? formatCurrency(item.remainingAfterZleceniowka) : item.totalToPayout }})
                                    </td>
                                    <td class="text-right purple--text font-weight-bold">{{ item.costInvoiceReimbursement }}</td>
                                  </tr>
                                  <tr v-if="item.maxCostReimbursement > 0">
                                    <td class="text-body-2 pl-6 grey--text">Max kwota zwrotu kosztów (8% netto z apps)</td>
                                    <td class="text-right grey--text">{{ formatCurrency(item.maxCostReimbursement) }}</td>
                                  </tr>

                                  <!-- Przelew 3: Najem -->
                                  <tr>
                                    <td class="text-body-2 pl-4">
                                      3. Najem = pozostałe - zwrot kosztów
                                    </td>
                                    <td class="text-right orange--text font-weight-bold">{{ item.rentTransferAmount }}</td>
                                  </tr>
                                </template>

                                <!-- SECTION 3 alt: B2B -->
                                <template v-if="item.cooperationTypeRaw === 'B2B_VAT' || item.cooperationTypeRaw === 'B2B_NO_VAT'">
                                  <tr>
                                    <td colspan="2" class="pt-3"><v-divider></v-divider></td>
                                  </tr>
                                  <tr class="grey lighten-4">
                                    <td class="text-body-2 font-weight-bold">PRZELEW</td>
                                    <td></td>
                                  </tr>
                                  <tr>
                                    <td class="text-body-2 pl-4">Przelew z tyt. faktury</td>
                                    <td class="text-right blue--text font-weight-bold">{{ item.netPayout }}</td>
                                  </tr>
                                </template>

                                <!-- SECTION 4: Podsumowanie -->
                                <tr>
                                  <td colspan="2" class="pt-3"><v-divider></v-divider></td>
                                </tr>
                                <tr class="font-weight-bold" v-if="item.cooperationTypeRaw === 'FIFTY_FIFTY' || item.cooperationTypeRaw === 'STUDENT' || item.cooperationTypeRaw === 'CONNECTION' || item.cooperationTypeRaw === 'RENT'">
                                  <td class="text-subtitle-1">SUMA PRZELEWÓW (po ZUS)</td>
                                  <td class="text-right text-h6 primary--text">{{ formatCurrency(item.afterZus) }}</td>
                                </tr>

                                <!-- Info: 8% netto -->
                                <tr v-if="item.totalNetto > 0">
                                  <td colspan="2" class="pt-2">
                                    <v-alert dense text type="info" class="mb-0">
                                      <div class="text-caption">
                                        <strong>8% netto z apps:</strong> {{ formatCurrency(item.maxCostReimbursement) }}
                                        (suma netto: {{ formatCurrency(item.totalNetto) }}) — pole informacyjne
                                      </div>
                                    </v-alert>
                                  </td>
                                </tr>
                              </tbody>
                            </v-simple-table>
                          </v-alert>

                          <!-- Per App Breakdown -->
                          <div class="text-subtitle-2 mt-4 mb-2">
                            <v-icon small color="primary">mdi-cellphone</v-icon>
                            Rozliczenie per Aplikacja:
                          </div>
                          <v-simple-table dense>
                            <thead>
                              <tr>
                                <th>Aplikacja</th>
                                <th class="text-right">Brutto</th>
                                <th class="text-right">Netto</th>
                                <th class="text-right">1% z Brutto</th>
                                <th class="text-right">8% z Netto</th>
                                <th class="text-right">Kampanie+napiwki / −15%</th>
                                <th class="text-right">Obliczone</th>
                                <th class="text-right">Gotówka</th>
                              </tr>
                            </thead>
                            <tbody>
                              <template v-for="appType in ['BOLT', 'UBER', 'FREENOW']">
                                <tr v-if="item.perAppTotals[appType] && item.perAppTotals[appType].count > 0" :key="appType">
                                  <td>
                                    <v-chip small :color="getAppColor(appType)" text-color="white">{{ appType }}</v-chip>
                                    <span class="text-caption ml-1">({{ item.perAppTotals[appType].count }})</span>
                                  </td>
                                  <td class="text-right">{{ formatCurrency(item.perAppTotals[appType].brutto) }}</td>
                                  <td class="text-right">{{ formatCurrency(item.perAppTotals[appType].netto) }}</td>
                                  <td class="text-right info--text">{{ formatCurrency(item.perAppTotals[appType].onePercentFromBrutto) }}</td>
                                  <td class="text-right purple--text">{{ formatCurrency(item.perAppTotals[appType].eightPercentFromNetto) }}</td>
                                  <td class="text-right">
                                    <div>{{ formatCurrency(item.perAppTotals[appType].bonus) }}</div>
                                    <div class="text-caption error--text font-weight-bold">−15%: {{ formatCurrency(Math.round(item.perAppTotals[appType].bonus * 0.15)) }}</div>
                                  </td>
                                  <td class="text-right success--text font-weight-bold">{{ formatCurrency(item.perAppTotals[appType].calculated) }}</td>
                                  <td class="text-right warning--text">{{ formatCurrency(item.perAppTotals[appType].cash) }}</td>
                                </tr>
                              </template>
                              <tr class="font-weight-bold grey lighten-4">
                                <td>RAZEM</td>
                                <td class="text-right">{{ formatCurrency(item.totalBrutto) }}</td>
                                <td class="text-right">{{ formatCurrency(item.totalNetto) }}</td>
                                <td class="text-right info--text">{{ formatCurrency(item.totalOnePercent) }}</td>
                                <td class="text-right purple--text">{{ formatCurrency(item.totalEightPercent) }}</td>
                                <td class="text-right">
                                  <div>{{ formatCurrency(item.totalBonus) }}</div>
                                  <div class="text-caption error--text font-weight-bold">−15%: {{ formatCurrency(Math.round(item.totalBonus * 0.15)) }}</div>
                                </td>
                                <td class="text-right success--text">{{ formatCurrency(item.totalImportCalculated) }}</td>
                                <td class="text-right warning--text">{{ formatCurrency(item.totalImportCash) }}</td>
                              </tr>
                              <tr v-if="item.maxCostReimbursement > 0">
                                <td colspan="5" class="text-right text-caption grey--text">Max kwota zwrotu kosztów (8% netto):</td>
                                <td class="text-right purple--text font-weight-bold" colspan="3">{{ formatCurrency(item.maxCostReimbursement) }}</td>
                              </tr>
                            </tbody>
                          </v-simple-table>

                          <!-- ZUS Summary -->
                          <div class="text-subtitle-2 mt-4 mb-2" v-if="item.zus && item.zus.length > 0">
                            <v-icon small color="red">mdi-account-cash</v-icon>
                            Potrącenia ZUS:
                          </div>
                          <v-simple-table dense v-if="item.zus && item.zus.length > 0">
                            <thead>
                              <tr>
                                <th>Miesiąc/Rok</th>
                                <th>Typ ZUS</th>
                                <th class="text-right">Chorobowe</th>
                                <th class="text-right">Społeczne</th>
                                <th class="text-right">Fund. Pracy</th>
                                <th class="text-right">Zdrowotne</th>
                                <th class="text-right">Suma</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr v-for="z in item.zus" :key="z.id">
                                <td>{{ z.month }}/{{ z.year }}</td>
                                <td>{{ formatZusType(z.zusType) }}</td>
                                <td class="text-right">{{ formatCurrency(z.chorobowe) }}</td>
                                <td class="text-right">{{ formatCurrency(z.spoleczne) }}</td>
                                <td class="text-right">{{ formatCurrency(z.funduszPracy) }}</td>
                                <td class="text-right">{{ formatCurrency(z.zdrowotne) }}</td>
                                <td class="text-right error--text font-weight-bold">-{{ formatCurrency(z.amount) }}</td>
                              </tr>
                              <tr class="font-weight-bold grey lighten-4" v-if="item.zus.length > 1">
                                <td colspan="6">RAZEM ZUS</td>
                                <td class="text-right error--text">-{{ formatCurrency(item.totalZus) }}</td>
                              </tr>
                            </tbody>
                          </v-simple-table>

                          <!-- Quick Stats -->
                          <v-divider class="my-3"></v-divider>
                          <v-row>
                            <v-col cols="6" md="2">
                              <div class="text-caption grey--text">Suma</div>
                              <div class="text-subtitle-1 success--text font-weight-bold">{{ item.totalToPayout }}</div>
                            </v-col>
                            <v-col cols="6" md="2" v-if="item.zusDeductionRaw > 0">
                              <div class="text-caption grey--text">ZUS</div>
                              <div class="text-subtitle-1 error--text font-weight-bold">-{{ item.zusDeduction }}</div>
                            </v-col>
                            <v-col cols="6" md="2" v-if="item.zleceniowkaAmountRaw > 0">
                              <div class="text-caption grey--text">Zleceniówka</div>
                              <div class="text-subtitle-1 blue--text font-weight-bold">{{ item.zleceniowkaAmount }}</div>
                            </v-col>
                            <v-col cols="6" md="2" v-if="item.costInvoiceReimbursementRaw > 0">
                              <div class="text-caption grey--text">Zwrot kosztów</div>
                              <div class="text-subtitle-1 purple--text font-weight-bold">{{ item.costInvoiceReimbursement }}</div>
                              <div v-if="item.maxCostReimbursement > 0" class="text-caption grey--text">max 8% netto: {{ formatCurrency(item.maxCostReimbursement) }}</div>
                            </v-col>
                            <v-col cols="6" md="2" v-if="item.rentTransferAmountRaw > 0">
                              <div class="text-caption grey--text">Najem</div>
                              <div class="text-subtitle-1 orange--text font-weight-bold">{{ item.rentTransferAmount }}</div>
                            </v-col>
                          </v-row>
                        </v-card-text>
                      </v-card>
                    </v-col>

                    <!-- Import Data (Earnings from Apps) -->
                    <v-col cols="12" v-if="item.importData && item.importData.length > 0">
                      <v-card outlined>
                        <v-card-title class="text-subtitle-1 success--text py-2">
                          <v-icon left color="success" small>mdi-cash-plus</v-icon>
                          Przychody z Aplikacji ({{ item.importData.length }})
                        </v-card-title>
                        <v-card-text>
                          <v-expansion-panels accordion>
                            <v-expansion-panel v-for="imp in item.importData" :key="imp.id">
                              <v-expansion-panel-header>
                                <v-row no-gutters align="center">
                                  <v-col cols="2">
                                    <v-chip small :color="getAppColor(imp.type)" text-color="white">{{ imp.type }}</v-chip>
                                  </v-col>
                                  <v-col cols="2">
                                    <span class="text-caption">{{ imp.date }}</span>
                                  </v-col>
                                  <v-col cols="3">
                                    <span class="text-caption grey--text">{{ imp.equationName }}</span>
                                  </v-col>
                                  <v-col cols="2" class="text-right">
                                    <span class="success--text font-weight-bold">{{ formatCurrency(imp.calculated) }}</span>
                                  </v-col>
                                  <v-col cols="2" class="text-right">
                                    <span class="warning--text">Gotówka: {{ formatCurrency(imp.cash) }}</span>
                                  </v-col>
                                </v-row>
                              </v-expansion-panel-header>
                              <v-expansion-panel-content>
                                <v-row>
                                  <!-- Equation & Calculation Card -->
                                  <v-col cols="12" md="6" v-if="imp.equation">
                                    <v-card flat class="grey lighten-5 pa-3 mb-2">
                                      <div class="d-flex align-center mb-2">
                                        <v-icon small color="primary" class="mr-2">mdi-calculator</v-icon>
                                        <span class="text-subtitle-2 primary--text">Wzór Obliczeniowy</span>
                                        <v-spacer></v-spacer>
                                        <v-chip x-small color="primary" class="mr-1">{{ imp.equation.name }}</v-chip>
                                        <v-chip x-small outlined>{{ imp.equation.type }}</v-chip>
                                      </div>

                                      <!-- Equation Formula -->
                                      <v-alert dense color="blue-grey lighten-4" class="mb-3 py-1" style="font-family: monospace; font-size: 12px;">
                                        <strong>{{ $t('driverDetails.formula') }}:</strong> {{ imp.equation.equationStr }}
                                      </v-alert>

                                      <!-- Variables Used -->
                                      <div class="mb-3">
                                        <div class="text-caption font-weight-bold mb-1">
                                          <v-icon x-small color="primary">mdi-variable</v-icon>
                                          {{ $t('driverDetails.usedVariables') }}:
                                        </div>
                                        <div class="d-flex flex-wrap">
                                          <div v-for="(value, key) in getEquationFields(imp.rawData, imp.equation)" :key="key" class="mr-3 mb-1">
                                            <v-chip x-small :color="getFieldColor(key, imp.equation)" text-color="white" class="mr-1">
                                              {{ key.replace(/_/g, ' ') }}
                                            </v-chip>
                                            <span class="text-caption font-weight-bold">= {{ value }}</span>
                                          </div>
                                          <div v-for="(cv, idx) in (imp.equation.customNumbers || [])" :key="'cv-'+idx" class="mr-3 mb-1">
                                            <v-chip x-small color="purple" text-color="white" class="mr-1">
                                              {{ cv.label }}
                                            </v-chip>
                                            <span class="text-caption font-weight-bold">= {{ cv.value }}</span>
                                            <v-chip x-small outlined class="ml-1">{{ $t('driverDetails.constant') }}</v-chip>
                                          </div>
                                        </div>
                                      </div>

                                      <!-- Calculation Steps -->
                                      <div class="mb-3">
                                        <div class="text-caption font-weight-bold mb-1">
                                          <v-icon x-small color="primary">mdi-format-list-numbered</v-icon>
                                          {{ $t('driverDetails.calculationSteps') }}:
                                        </div>
                                        <v-timeline dense align-top>
                                          <v-timeline-item
                                            v-for="(step, idx) in getCalculationSteps(imp.equation, imp.rawData)"
                                            :key="idx"
                                            :color="step.isFinal ? 'success' : (step.result !== null ? 'info' : 'grey')"
                                            small
                                          >
                                            <div>
                                              <div class="text-caption font-weight-bold" :class="step.isFinal ? 'success--text' : ''">
                                                {{ step.label }}
                                              </div>
                                              <code
                                                class="d-block pa-2 calc-step-code calc-step-code-sm"
                                                :class="step.isFinal ? 'calc-step-final' : 'calc-step-normal'"
                                              >
                                                {{ step.expression }}
                                              </code>
                                            </div>
                                          </v-timeline-item>
                                        </v-timeline>
                                      </div>

                                      <!-- Result -->
                                      <v-card color="success" dark class="pa-2">
                                        <div class="text-caption">{{ $t('driverDetails.calculationResult') }}:</div>
                                        <div class="text-h6 font-weight-bold">{{ formatCurrency(imp.calculated) }}</div>
                                      </v-card>
                                    </v-card>
                                  </v-col>

                                  <!-- Details & Raw Data -->
                                  <v-col cols="12" :md="imp.equation ? 6 : 12">
                                    <!-- Summary -->
                                    <v-card flat class="grey lighten-4 pa-2 mb-2">
                                      <div class="text-caption font-weight-bold mb-2">Podsumowanie</div>
                                      <v-row dense>
                                        <v-col cols="6">
                                          <div class="text-caption grey--text">ID w aplikacji</div>
                                          <div class="font-weight-medium">{{ imp.recordId || '-' }}</div>
                                        </v-col>
                                        <v-col cols="6">
                                          <div class="text-caption grey--text">Typ współpracy</div>
                                          <div class="font-weight-medium">{{ getCooperationTypeLabel(imp.cooperationType) }}</div>
                                        </v-col>
                                        <v-col cols="6">
                                          <div class="text-caption grey--text">Obliczona kwota</div>
                                          <div class="font-weight-bold success--text">{{ formatCurrency(imp.calculated) }}</div>
                                        </v-col>
                                        <v-col cols="6">
                                          <div class="text-caption grey--text">Gotówka</div>
                                          <div class="font-weight-bold warning--text">{{ formatCurrency(imp.cash) }}</div>
                                        </v-col>
                                      </v-row>
                                    </v-card>

                                    <!-- Raw CSV Data -->
                                    <v-card flat class="grey lighten-4 pa-2" v-if="imp.rawData">
                                      <div class="text-caption font-weight-bold mb-2">
                                        <v-icon x-small>mdi-file-delimited</v-icon>
                                        Dane z CSV
                                      </div>
                                      <v-simple-table dense style="max-height: 200px; overflow-y: auto;">
                                        <template v-slot:default>
                                          <thead>
                                            <tr>
                                              <th style="width: 40%;">Pole</th>
                                              <th>Wartość</th>
                                            </tr>
                                          </thead>
                                          <tbody>
                                            <tr v-for="(value, key) in getOtherFields(imp.rawData, imp.equation)" :key="key">
                                              <td class="text-caption">{{ key.replace(/_/g, ' ') }}</td>
                                              <td class="font-weight-medium">{{ value || '-' }}</td>
                                            </tr>
                                          </tbody>
                                        </template>
                                      </v-simple-table>
                                    </v-card>
                                  </v-col>
                                </v-row>
                              </v-expansion-panel-content>
                            </v-expansion-panel>
                          </v-expansion-panels>
                          <v-divider class="my-3"></v-divider>
                          <v-row class="font-weight-bold">
                            <v-col cols="6">Razem</v-col>
                            <v-col cols="3" class="text-right success--text">{{ formatCurrency(item.totalImportCalculated) }}</v-col>
                            <v-col cols="3" class="text-right warning--text">Gotówka: {{ formatCurrency(item.totalImportCash) }}</v-col>
                          </v-row>
                        </v-card-text>
                      </v-card>
                    </v-col>

                    <!-- Invoices -->
                    <v-col cols="12" md="6" v-if="item.invoices && item.invoices.length > 0">
                      <v-card outlined>
                        <v-card-title class="text-subtitle-1 blue--text py-2">
                          <v-icon left color="blue" small>mdi-file-document</v-icon>
                          Faktury ({{ item.invoices.length }})
                        </v-card-title>
                        <v-card-text style="max-height: 300px; overflow-y: auto;">
                          <v-simple-table dense>
                            <thead>
                              <tr>
                                <th>Data</th>
                                <th class="text-right">Brutto</th>
                                <th class="text-right">Dla Kierowcy</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr v-for="inv in item.invoices" :key="inv.id">
                                <td>{{ inv.date }}</td>
                                <td class="text-right">{{ formatCurrency(inv.brutto) }}</td>
                                <td class="text-right success--text">{{ formatCurrency(inv.driverReturn) }}</td>
                              </tr>
                              <tr class="font-weight-bold">
                                <td>Razem</td>
                                <td class="text-right">{{ formatCurrency(item.totalInvoicesBrutto) }}</td>
                                <td class="text-right success--text">{{ formatCurrency(item.totalInvoicesReturn) }}</td>
                              </tr>
                            </tbody>
                          </v-simple-table>
                          <div v-if="item.maxCostReimbursement > 0" class="mt-2 text-caption grey--text">
                            Max kwota zwrotu kosztów (8% netto z apps): <strong class="purple--text">{{ formatCurrency(item.maxCostReimbursement) }}</strong>
                          </div>
                        </v-card-text>
                      </v-card>
                    </v-col>

                    <!-- Bonuses -->
                    <v-col cols="12" md="6" v-if="item.bonuses && item.bonuses.length > 0">
                      <v-card outlined>
                        <v-card-title class="text-subtitle-1 purple--text py-2">
                          <v-icon left color="purple" small>mdi-gift</v-icon>
                          Bonusy / Kary ({{ item.bonuses.length }})
                        </v-card-title>
                        <v-card-text style="max-height: 300px; overflow-y: auto;">
                          <v-simple-table dense>
                            <thead>
                              <tr>
                                <th>Opis</th>
                                <th>Typ</th>
                                <th class="text-right">Kwota</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr v-for="bonus in item.bonuses" :key="bonus.id">
                                <td>{{ bonus.name }}</td>
                                <td>
                                  <v-chip x-small :color="bonus.type === 'POSITIVE' ? 'success' : (bonus.type === 'RENT' ? 'orange' : 'error')">
                                    {{ bonus.type === 'POSITIVE' ? '+' : '-' }}
                                  </v-chip>
                                </td>
                                <td class="text-right" :class="bonus.type === 'POSITIVE' ? 'success--text' : 'error--text'">
                                  {{ bonus.type === 'POSITIVE' ? '+' : '-' }}{{ formatCurrency(bonus.price) }}
                                </td>
                              </tr>
                              <tr class="font-weight-bold">
                                <td colspan="2">Razem</td>
                                <td class="text-right" :class="item.totalBonusesNet >= 0 ? 'success--text' : 'error--text'">
                                  {{ item.totalBonusesNet >= 0 ? '+' : '' }}{{ formatCurrency(item.totalBonusesNet) }}
                                </td>
                              </tr>
                            </tbody>
                          </v-simple-table>
                        </v-card-text>
                      </v-card>
                    </v-col>

                    <!-- Incomes -->
                    <v-col cols="12" md="6" v-if="item.incomes && item.incomes.length > 0">
                      <v-card outlined>
                        <v-card-title class="text-subtitle-1 teal--text py-2">
                          <v-icon left color="teal" small>mdi-cash-multiple</v-icon>
                          Przychody ({{ item.incomes.length }})
                        </v-card-title>
                        <v-card-text style="max-height: 300px; overflow-y: auto;">
                          <v-simple-table dense>
                            <thead>
                              <tr>
                                <th>Data</th>
                                <th class="text-right">Dla Kierowcy</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr v-for="inc in item.incomes" :key="inc.id">
                                <td>{{ inc.date }}</td>
                                <td class="text-right success--text">{{ formatCurrency(inc.driverReturn) }}</td>
                              </tr>
                              <tr class="font-weight-bold">
                                <td>Razem</td>
                                <td class="text-right success--text">{{ formatCurrency(item.totalIncomesReturn) }}</td>
                              </tr>
                            </tbody>
                          </v-simple-table>
                        </v-card-text>
                      </v-card>
                    </v-col>

                    <!-- ZUS Info -->
                    <v-col cols="12" md="6" v-if="item.zus && item.zus.length > 0">
                      <v-card outlined>
                        <v-card-title class="text-subtitle-1 red--text py-2">
                          <v-icon left color="red" small>mdi-account-cash</v-icon>
                          ZUS
                        </v-card-title>
                        <v-card-text>
                          <v-simple-table dense>
                            <thead>
                              <tr>
                                <th>Miesiąc/Rok</th>
                                <th>Typ ZUS</th>
                                <th class="text-right">Chorobowe</th>
                                <th class="text-right">Społeczne</th>
                                <th class="text-right">Fund. Pracy</th>
                                <th class="text-right">Zdrowotne</th>
                                <th class="text-right">Suma</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr v-for="z in item.zus" :key="z.id">
                                <td>{{ z.month }}/{{ z.year }}</td>
                                <td>{{ formatZusType(z.zusType) }}</td>
                                <td class="text-right">{{ formatCurrency(z.chorobowe) }}</td>
                                <td class="text-right">{{ formatCurrency(z.spoleczne) }}</td>
                                <td class="text-right">{{ formatCurrency(z.funduszPracy) }}</td>
                                <td class="text-right">{{ formatCurrency(z.zdrowotne) }}</td>
                                <td class="text-right error--text font-weight-bold">-{{ formatCurrency(z.amount) }}</td>
                              </tr>
                            </tbody>
                          </v-simple-table>
                        </v-card-text>
                      </v-card>
                    </v-col>

                    <!-- Empty state -->
                    <v-col cols="12" v-if="(!item.importData || item.importData.length === 0) && (!item.invoices || item.invoices.length === 0) && (!item.bonuses || item.bonuses.length === 0) && (!item.incomes || item.incomes.length === 0)">
                      <v-alert type="info" dense text>
                        Brak szczegółowych danych dla tego rozliczenia.
                      </v-alert>
                    </v-col>
                  </v-row>
                </td>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-tab-item>

      <!-- Auta Tab -->
      <v-tab-item>
        <v-card>
          <v-card-title>
            <span class="text-h6">{{ $t('driverDetails.companyCars') }}</span>
          </v-card-title>
          <v-card-text>
            <v-alert v-if="!formData.car && !formData.secondCar" type="info" text dense>
              {{ $t('driverDetails.noCompanyCars') }}
            </v-alert>
            <v-row v-else>
              <v-col cols="12" md="6" v-if="formData.car">
                <v-card outlined>
                  <v-card-title class="text-subtitle-1">
                    <v-icon left color="primary">mdi-car</v-icon>
                    {{ $t('driverDetails.mainCar') }}
                  </v-card-title>
                  <v-card-text>
                    <v-simple-table dense>
                      <tbody>
                        <tr><td class="font-weight-bold">{{ $t('driverDetails.carLabels.makeModel') }}</td><td>{{ formData.car.make }} {{ formData.car.model }}</td></tr>
                        <tr><td class="font-weight-bold">{{ $t('driverDetails.carLabels.registration') }}</td><td>{{ formData.car.plate }}</td></tr>
                        <tr><td class="font-weight-bold">{{ $t('driverDetails.carLabels.sideNumber') }}</td><td>{{ formData.car.sideNumber || '-' }}</td></tr>
                        <tr><td class="font-weight-bold">{{ $t('driverDetails.carLabels.color') }}</td><td>{{ formData.car.color }}</td></tr>
                        <tr><td class="font-weight-bold">{{ $t('driverDetails.carLabels.productionYear') }}</td><td>{{ formData.car.productionYear }}</td></tr>
                        <tr><td class="font-weight-bold">{{ $t('driverDetails.carLabels.wrapType') }}</td><td>{{ formData.car.wrapType }}</td></tr>
                        <tr><td class="font-weight-bold">{{ $t('driverDetails.carLabels.mileage') }}</td><td>{{ formData.car.odometer }} km</td></tr>
                        <tr><td class="font-weight-bold">{{ $t('driverDetails.carLabels.rentPrice') }}</td><td>{{ (formData.car.rentPrice / 100).toFixed(2) }} PLN</td></tr>
                      </tbody>
                    </v-simple-table>
                    <div v-if="formData.car.licenses && formData.car.licenses.length > 0" class="mt-3">
                      <div class="font-weight-bold mb-1">{{ $t('driverDetails.licenseExcerpts') }}:</div>
                      <v-chip v-for="lic in formData.car.licenses" :key="lic.id" small class="mr-1 mb-1">
                        {{ lic.text }}{{ lic.licenseNo ? ' (' + lic.licenseNo + ')' : '' }}{{ lic.city ? ' - ' + lic.city : '' }}
                      </v-chip>
                    </div>
                  </v-card-text>
                </v-card>
              </v-col>
              <v-col cols="12" md="6" v-if="formData.secondCar">
                <v-card outlined>
                  <v-card-title class="text-subtitle-1">
                    <v-icon left color="secondary">mdi-car</v-icon>
                    {{ $t('driverDetails.additionalCar') }}
                  </v-card-title>
                  <v-card-text>
                    <v-simple-table dense>
                      <tbody>
                        <tr><td class="font-weight-bold">{{ $t('driverDetails.carLabels.makeModel') }}</td><td>{{ formData.secondCar.make }} {{ formData.secondCar.model }}</td></tr>
                        <tr><td class="font-weight-bold">{{ $t('driverDetails.carLabels.registration') }}</td><td>{{ formData.secondCar.plate }}</td></tr>
                        <tr><td class="font-weight-bold">{{ $t('driverDetails.carLabels.sideNumber') }}</td><td>{{ formData.secondCar.sideNumber || '-' }}</td></tr>
                        <tr><td class="font-weight-bold">{{ $t('driverDetails.carLabels.color') }}</td><td>{{ formData.secondCar.color }}</td></tr>
                        <tr><td class="font-weight-bold">{{ $t('driverDetails.carLabels.productionYear') }}</td><td>{{ formData.secondCar.productionYear }}</td></tr>
                        <tr><td class="font-weight-bold">{{ $t('driverDetails.carLabels.wrapType') }}</td><td>{{ formData.secondCar.wrapType }}</td></tr>
                        <tr><td class="font-weight-bold">{{ $t('driverDetails.carLabels.mileage') }}</td><td>{{ formData.secondCar.odometer }} km</td></tr>
                        <tr><td class="font-weight-bold">{{ $t('driverDetails.carLabels.rentPrice') }}</td><td>{{ (formData.secondCar.rentPrice / 100).toFixed(2) }} PLN</td></tr>
                      </tbody>
                    </v-simple-table>
                    <div v-if="formData.secondCar.licenses && formData.secondCar.licenses.length > 0" class="mt-3">
                      <div class="font-weight-bold mb-1">{{ $t('driverDetails.licenseExcerpts') }}:</div>
                      <v-chip v-for="lic in formData.secondCar.licenses" :key="lic.id" small class="mr-1 mb-1">
                        {{ lic.text }}{{ lic.licenseNo ? ' (' + lic.licenseNo + ')' : '' }}{{ lic.city ? ' - ' + lic.city : '' }}
                      </v-chip>
                    </div>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <v-card class="mt-4">
          <v-card-title>
            <span class="text-h6">{{ $t('driverDetails.privateCars') }}</span>
          </v-card-title>
          <v-card-text>
            <v-data-table
              :headers="driverPrivateCarsHeaders"
              :items="driverPrivateCars"
              :loading="driverPrivateCarsLoading"
              class="elevation-1"
              :no-data-text="$t('driverDetails.noPrivateCars')"
            >
              <template v-slot:item.sideNumber="{ item }">
                {{ item.sideNumber || '-' }}
              </template>
              <template v-slot:item.mileage="{ item }">
                {{ item.currentMileage }} km
              </template>
              <template v-slot:item.licenses="{ item }">
                <template v-if="item.licenses && item.licenses.length > 0">
                  <v-chip v-for="lic in item.licenses" :key="lic.id" x-small class="mr-1">
                    {{ lic.text }}{{ lic.licenseNo ? ' (' + lic.licenseNo + ')' : '' }}{{ lic.city ? ' - ' + lic.city : '' }}
                  </v-chip>
                </template>
                <template v-else>-</template>
              </template>
              <template v-slot:item.actions="{ item }">
                <v-btn icon small color="red" @click="deleteDriverPrivateCar(item)"><v-icon small>mdi-delete</v-icon></v-btn>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-tab-item>

      <!-- Komornicy Tab -->
      <v-tab-item>
        <v-card>
          <v-card-title>
            <span class="text-h6">{{ $t('driverDetails.bailiffsTitle') }}</span>
            <v-spacer></v-spacer>
            <v-btn color="primary" small @click="openBailiffDialog()">{{ $t('driverDetails.addBailiff') }}</v-btn>
          </v-card-title>
          <v-card-text>
            <v-data-table
              :headers="bailiffHeaders"
              :items="bailiffs"
              :loading="bailiffsLoading"
              class="elevation-1"
              :no-data-text="$t('driverDetails.noBailiffs')"
            >
              <template v-slot:item.isActive="{ item }">
                <v-icon :color="item.isActive ? 'green' : 'grey'" :title="item.isActive ? 'Aktywne' : (item.closedAt ? 'Zamknięte (spłacone)' : 'Nieaktywne')">
                  {{ item.isActive ? 'mdi-check-circle' : (item.closedAt ? 'mdi-lock-check' : 'mdi-circle-outline') }}
                </v-icon>
              </template>
              <template v-slot:item.transferTypes="{ item }">
                <v-chip x-small class="mr-1" v-for="t in item.transferTypes" :key="t">
                  {{ transferTypeLabels[t] || t }}
                </v-chip>
              </template>
              <template v-slot:item.progress="{ item }">
                <span v-if="item.totalAmountCents != null">
                  {{ ((item.paidAmountCents || 0) / 100).toFixed(2) }} / {{ (item.totalAmountCents / 100).toFixed(2) }} zł
                  <v-chip v-if="!item.isActive && item.closedAt" x-small color="success" text-color="white" class="ml-1">spłacone</v-chip>
                </span>
                <span v-else class="grey--text">
                  {{ ((item.paidAmountCents || 0) / 100).toFixed(2) }} zł <span class="caption">(bez limitu)</span>
                </span>
              </template>
              <template v-slot:item.createdAt="{ item }">
                {{ formatDate(item.createdAt) }}
              </template>
              <template v-slot:item.actions="{ item }">
                <v-btn x-small color="primary" class="mr-1" @click="openBailiffDialog(item)">{{ $t('common.edit') }}</v-btn>
                <v-btn x-small color="error" @click="deleteBailiff(item.id)">{{ $t('common.delete') }}</v-btn>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>

        <!-- Pisma komornicze -->
        <v-card class="mt-4">
          <v-card-title>
            <span class="text-h6">Pisma komornicze</span>
            <v-spacer></v-spacer>
            <v-btn color="primary" small @click="openKomornikLetterDialog()">Dodaj pismo</v-btn>
          </v-card-title>
          <v-card-text>
            <v-data-table
              :headers="komornikLetterHeaders"
              :items="komornikLetters"
              :loading="komornikLettersLoading"
              class="elevation-1"
              no-data-text="Brak pism komorniczych"
            >
              <template v-slot:item.receivedAt="{ item }">{{ formatDate(item.receivedAt) }}</template>
              <template v-slot:item.responseDeadline="{ item }">
                <span v-if="!item.responseDeadline">—</span>
                <span v-else :class="komornikOverdue(item) ? 'red--text font-weight-bold' : ''">
                  {{ formatDate(item.responseDeadline) }}
                </span>
              </template>
              <template v-slot:item.status="{ item }">
                <v-chip x-small :color="komornikStatusColor(item)" dark>{{ komornikStatusLabel(item) }}</v-chip>
              </template>
              <template v-slot:item.feeCents="{ item }">{{ (item.feeCents / 100).toFixed(2) }} zł</template>
              <template v-slot:item.files="{ item }">
                <a v-for="f in item.files" :key="f.id" :href="f.url" target="_blank" class="mr-1">
                  <v-icon small>mdi-file-document</v-icon>
                </a>
                <span v-if="!item.files || item.files.length === 0">—</span>
              </template>
              <template v-slot:item.actions="{ item }">
                <v-btn x-small color="primary" class="mr-1" @click="openResponseDialog(item)">Generuj odpowiedź</v-btn>
                <template v-for="r in item.responses">
                  <a :key="r.id + '-pdf'" :href="r.file ? r.file.url : '#'" target="_blank" class="mr-1" title="Pobierz odpowiedź">
                    <v-icon small color="success">mdi-file-pdf-box</v-icon>
                  </a>
                  <v-btn :key="r.id + '-mail'" x-small text class="mr-1" :title="r.sentAt ? ('Wysłano: ' + r.sentTo) : 'Wyślij e-mailem'" @click="sendResponseEmail(item, r)">
                    <v-icon small :color="r.sentAt ? 'success' : 'primary'">{{ r.sentAt ? 'mdi-email-check' : 'mdi-email-arrow-right' }}</v-icon>
                  </v-btn>
                  <v-btn v-if="r.confirmationFile" :key="r.id + '-conf'" x-small text class="mr-1" title="Pobierz potwierdzenie wysłania" @click="openKomornikFile(r.confirmationFile.id)">
                    <v-icon small color="teal">mdi-file-check</v-icon>
                  </v-btn>
                </template>
                <v-btn v-if="!item.respondedAt" x-small color="success" class="mr-1" @click="markKomornikResponded(item)">Odpowiedziano</v-btn>
                <v-btn x-small color="error" @click="deleteKomornikLetter(item.id)">{{ $t('common.delete') }}</v-btn>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>

        <!-- Komornik Letter Dialog -->
        <v-dialog v-model="komornikLetterDialog" max-width="600">
          <v-card>
            <v-card-title>Dodaj pismo komornicze</v-card-title>
            <v-card-text>
              <v-text-field v-model="komornikLetterForm.title" label="Tytuł pisma" outlined dense />
              <v-text-field v-model="komornikLetterForm.caseNumber" label="Sygnatura akt (np. KM 123/26)" outlined dense />
              <v-select
                v-model="komornikLetterForm.bailiffId"
                :items="bailiffs"
                :item-text="bailiffItemText"
                item-value="id"
                label="Komornik (który wystosował pismo)"
                outlined dense clearable
                no-data-text="Brak komorników — dodaj najpierw w tabeli Komornicy"
              />
              <v-textarea v-model="komornikLetterForm.description" label="Opis / uwagi" outlined dense rows="2" />
              <v-text-field v-model="komornikLetterForm.receivedAt" label="Data otrzymania" type="date" outlined dense />
              <v-text-field v-model="komornikLetterForm.responseDeadline" label="Termin odpowiedzi (opcjonalnie)" type="date" outlined dense />
              <v-file-input v-model="komornikLetterForm.files" label="Pliki pism (PDF/zdjęcia)" outlined dense multiple show-size prepend-icon="mdi-paperclip" />
              <v-alert dense type="info" class="mt-2" v-if="komornikFeeZl > 0">
                Za dodanie pisma zostanie naliczona opłata <b>{{ komornikFeeZl.toFixed(2) }} zł</b> (do rozliczenia z tygodnia daty otrzymania).
              </v-alert>
            </v-card-text>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn text @click="komornikLetterDialog = false">{{ $t('common.cancel') }}</v-btn>
              <v-btn color="primary" :loading="komornikLetterSaving" :disabled="!komornikLetterForm.title" @click="saveKomornikLetter()">{{ $t('common.save') }}</v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>

        <!-- Komornik Response Dialog -->
        <v-dialog v-model="responseDialog" max-width="760" persistent>
          <v-card>
            <v-card-title>Generuj odpowiedź na pismo komornicze</v-card-title>
            <v-card-text>
              <v-select
                v-model="responseForm.variant"
                :items="responseVariants"
                item-text="label"
                item-value="value"
                label="Wariant odpowiedzi"
                outlined dense
                @change="loadResponseTemplate"
              />
              <v-textarea
                v-model="responseForm.body"
                :label="'Treść pisma (placeholdery {{...}} zostaną podstawione automatycznie)'"
                outlined rows="10" auto-grow
              />
              <v-radio-group v-model="responseForm.signatureType" row label="Podpis:" class="mt-0" @change="onResponseSigTypeChange">
                <v-radio v-if="canUseFacsimileSelf" label="Auto-podpis (faksymilka)" value="FACSIMILE" />
                <v-radio label="Podpis ręczny (tablet)" value="MANUAL" />
              </v-radio-group>
              <div v-if="!canUseFacsimileSelf" class="caption grey--text mb-2">
                Auto-podpis niedostępny — brak uprawnienia do faksymilki lub nie wgrano jej w profilu.
              </div>
              <div v-if="responseForm.signatureType === 'MANUAL'">
                <div class="caption mb-1">Podpisz w polu poniżej:</div>
                <SignatureField
                  width="100%" height="180px" ref="komornikSigPad"
                  class="signature-pad" style="border:1px solid #ccc; border-radius:6px; background:#fff;"
                />
                <v-btn x-small text class="mt-1" @click="clearKomornikSig">Wyczyść podpis</v-btn>
              </div>
            </v-card-text>
            <v-card-actions>
              <v-btn text @click="previewResponse" :loading="responsePreviewing">Podgląd PDF</v-btn>
              <v-spacer></v-spacer>
              <v-btn text @click="responseDialog = false">{{ $t('common.cancel') }}</v-btn>
              <v-btn color="primary" :loading="responseSaving" @click="generateResponse">Generuj i podpisz</v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>

        <!-- Bailiff Dialog -->
        <v-dialog v-model="bailiffDialog" max-width="600">
          <v-card>
            <v-card-title>{{ editingBailiff ? $t('driverDetails.editBailiff') : $t('driverDetails.addBailiff') }}</v-card-title>
            <v-card-text>
              <v-autocomplete
                v-if="!editingBailiff"
                :items="bailiffRegistry"
                :item-text="registryItemText"
                return-object
                label="Wybierz istniejącego komornika (z innych kierowców)"
                outlined dense clearable
                no-data-text="Brak zapisanych komorników"
                class="mb-2"
                @change="onPickRegistryBailiff"
              />
              <v-text-field v-model="bailiffForm.name" :label="$t('driverDetails.bailiffLabels.name')" outlined dense />
              <v-text-field v-model="bailiffForm.accountNo" :label="$t('driverDetails.bailiffLabels.accountNo')" outlined dense :rules="[rules.bankAccount]" />
              <v-text-field v-model="bailiffForm.wireTitle" :label="$t('driverDetails.bailiffLabels.wireTitle')" outlined dense />
              <v-text-field v-model="bailiffForm.addressLine1" :label="$t('driverDetails.bailiffLabels.address1')" outlined dense />
              <v-text-field v-model="bailiffForm.addressLine2" :label="$t('driverDetails.bailiffLabels.address2')" outlined dense />
              <v-text-field v-model="bailiffForm.email" label="E-mail komornika (do wysyłki odpowiedzi)" outlined dense />
              <v-select
                v-model="bailiffForm.transferTypes"
                :items="transferTypeOptions"
                :label="$t('driverDetails.bailiffLabels.transferTypes')"
                multiple
                chips
                small-chips
                outlined
                dense
              />
              <v-row dense>
                <v-col cols="6">
                  <v-text-field
                    v-model.number="bailiffForm.totalAmountZl"
                    label="Kwota zajęcia (zł)"
                    type="number" step="0.01" min="0"
                    outlined dense
                    hint="Po spłacie zajęcie zamknie się samo. Puste = bez limitu."
                    persistent-hint
                  />
                </v-col>
                <v-col cols="6">
                  <v-text-field
                    v-model.number="bailiffForm.paidAmountZl"
                    label="Spłacono (zł)"
                    type="number" step="0.01" min="0"
                    outlined dense
                    hint="Nalicza się automatycznie; tu możesz skorygować ręcznie."
                    persistent-hint
                  />
                </v-col>
              </v-row>
              <v-text-field
                v-model.number="bailiffForm.monthlyLimitZl"
                label="Maksymalnie miesięcznie do komornika (zł)"
                type="number" step="0.01" min="0"
                outlined dense
                hint="Puste = bez limitu, komornik dostaje zajęte składniki w całości. Po wyczerpaniu limitu reszta idzie do kierowcy."
                persistent-hint
                class="mb-2"
              />
              <v-checkbox v-model="bailiffForm.isActive" :label="$t('driverDetails.bailiffLabels.isActive')" />
            </v-card-text>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn text @click="bailiffDialog = false">{{ $t('common.cancel') }}</v-btn>
              <v-btn color="primary" @click="saveBailiff()">{{ $t('common.save') }}</v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
      </v-tab-item>

      <!-- Historia Tab -->
      <v-tab-item>
        <v-card>
          <v-card-title>
            <span class="text-h6">{{ $t('driverDetails.historyTitle') }}</span>
          </v-card-title>
          <v-card-text>
            <!-- Cooperation Type History -->
            <v-card outlined class="mb-4">
              <v-card-title class="py-2">
                <v-icon left>mdi-handshake</v-icon>
                {{ $t('driverDetails.cooperationTypeHistory') }}
                <v-chip v-if="cooperationTypeHistory.length > 0" small class="ml-2">
                  {{ cooperationTypeHistory.length }} {{ $t('driverDetails.changes') }}
                </v-chip>
              </v-card-title>
              <v-card-text>
                <v-timeline v-if="cooperationTypeHistory.length > 0" dense align-top>
                  <v-timeline-item
                    v-for="(history, idx) in cooperationTypeHistory"
                    :key="idx"
                    :color="!history.endDate ? 'success' : 'grey'"
                    small
                  >
                    <v-card outlined class="pa-3">
                      <div class="d-flex align-center justify-space-between flex-wrap">
                        <div class="mb-1">
                          <v-chip
                            :color="!history.endDate ? 'success' : 'grey'"
                            text-color="white"
                            class="mr-2"
                          >
                            {{ getCooperationTypeLabel(history.cooperationType) }}
                          </v-chip>
                          <span v-if="history.mathEquation" class="text-caption ml-2">
                            <v-icon x-small>mdi-calculator</v-icon>
                            {{ $t('driverDetails.equationLabel') }}: {{ history.mathEquation.name }}
                          </span>
                        </div>
                        <div class="text-body-2">
                          <v-icon x-small class="mr-1">mdi-calendar-start</v-icon>
                          <span>{{ formatDateShort(history.startDate) }}</span>
                          <span v-if="history.endDate">
                            <v-icon x-small class="mx-1">mdi-arrow-right</v-icon>
                            <v-icon x-small class="mr-1">mdi-calendar-end</v-icon>
                            {{ formatDateShort(history.endDate) }}
                          </span>
                          <v-chip v-else x-small color="success" class="ml-2">{{ $t('driverDetails.current') }}</v-chip>
                        </div>
                      </div>
                    </v-card>
                  </v-timeline-item>
                </v-timeline>
                <v-alert v-else type="info" dense text>
                  Brak historii - ustaw rodzaj współpracy aby rozpocząć śledzenie zmian.
                </v-alert>
              </v-card-text>
            </v-card>

            <!-- Historia najmu aut (zdarzenia RENT_START / RENT_END) -->
            <v-card outlined class="mb-4">
              <v-card-title class="py-2">
                <v-icon left>mdi-car-key</v-icon>
                Historia najmu aut
                <v-chip v-if="rentHistory.length > 0" small class="ml-2">{{ rentHistory.length }}</v-chip>
              </v-card-title>
              <v-card-text>
                <v-timeline v-if="rentHistory.length > 0" dense align-top>
                  <v-timeline-item
                    v-for="(ev, idx) in rentHistory"
                    :key="ev.id || idx"
                    :color="ev.historyRecordType === 'RENT_START' ? 'success' : 'deep-orange'"
                    small
                  >
                    <v-card outlined class="pa-3">
                      <div class="d-flex align-center justify-space-between flex-wrap">
                        <div class="mb-1">
                          <v-chip
                            :color="ev.historyRecordType === 'RENT_START' ? 'success' : 'deep-orange'"
                            text-color="white"
                            class="mr-2"
                          >
                            {{ ev.historyRecordType === 'RENT_START' ? 'Rozpoczęcie najmu' : 'Zakończenie najmu' }}
                          </v-chip>
                          <span v-if="ev.car" class="text-caption ml-1">
                            <v-icon x-small>mdi-car</v-icon>
                            {{ ev.car.model }} ({{ ev.car.plate }})
                          </span>
                        </div>
                        <div class="text-body-2">
                          <v-icon x-small class="mr-1">mdi-calendar</v-icon>
                          {{ formatDateShort(ev.startDate || ev.createdAt) }}
                        </div>
                      </div>
                      <div v-if="ev.description" class="text-body-2 mt-2 grey--text text--darken-1">{{ ev.description }}</div>
                    </v-card>
                  </v-timeline-item>
                </v-timeline>
                <v-alert v-else type="info" dense text>
                  Brak zdarzeń najmu dla tego kierowcy.
                </v-alert>
              </v-card-text>
            </v-card>

            <!-- Historia zmian ustawień (typ współpracy / harmonogram wypłat / ZUS) -->
            <v-card outlined class="mb-4">
              <v-card-title class="py-2">
                <v-icon left>mdi-cog-refresh</v-icon>
                Historia zmian ustawień
                <v-chip v-if="settingsHistory.length > 0" small class="ml-2">{{ settingsHistory.length }}</v-chip>
              </v-card-title>
              <v-card-text>
                <v-timeline v-if="settingsHistory.length > 0" dense align-top>
                  <v-timeline-item
                    v-for="(entry, idx) in settingsHistory"
                    :key="entry.id || idx"
                    color="primary"
                    small
                  >
                    <v-card outlined class="pa-3">
                      <div class="d-flex align-center justify-space-between flex-wrap mb-2">
                        <span class="text-caption grey--text">
                          <v-icon x-small class="mr-1">mdi-account-tie</v-icon>{{ entry.admin }}
                        </span>
                        <span class="text-body-2">
                          <v-icon x-small class="mr-1">mdi-calendar-clock</v-icon>{{ formatDateShort(entry.timestamp) }}
                        </span>
                      </div>
                      <div v-for="(ch, ci) in entry.changes" :key="ci" class="mb-1">
                        <span class="font-weight-medium">{{ getSettingFieldLabel(ch.field) }}:</span>
                        <v-chip x-small color="grey lighten-2" class="mx-1">{{ getSettingValueLabel(ch.field, ch.from) }}</v-chip>
                        <v-icon x-small>mdi-arrow-right</v-icon>
                        <v-chip x-small color="primary" text-color="white" class="mx-1">{{ getSettingValueLabel(ch.field, ch.to) }}</v-chip>
                      </div>
                    </v-card>
                  </v-timeline-item>
                </v-timeline>
                <v-alert v-else type="info" dense text>
                  Brak zarejestrowanych zmian ustawień. Nowe zmiany (typ współpracy, harmonogram wypłat, ZUS) będą tu widoczne.
                </v-alert>
              </v-card-text>
            </v-card>
          </v-card-text>
        </v-card>
      </v-tab-item>

      <!-- Legalizacja pobytu i pracy -->
      <v-tab-item>
        <v-card flat>
          <v-card-text>
            <legalization-section
              :driver-id="currentDriverId"
              :can-edit="canEditLegalization"
            />
          </v-card-text>
        </v-card>
      </v-tab-item>
    </v-tabs-items>

    <!-- Settlement Invoice Upload Dialog -->
    <v-dialog v-model="settlementInvoiceUploadDialog" max-width="500">
      <v-card>
        <v-card-title>Wgraj fakturę do wypłaty</v-card-title>
        <v-card-text>
          <v-file-input
            v-model="settlementInvoiceFile"
            label="Wybierz plik faktury"
            outlined
            dense
            accept=".pdf,.jpg,.jpeg,.png"
            prepend-icon="mdi-file-document"
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="settlementInvoiceUploadDialog = false">Anuluj</v-btn>
          <v-btn color="primary" :disabled="!settlementInvoiceFile" @click="uploadSettlementInvoice()">Wgraj</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Settlement Invoice Preview Dialog -->
    <v-dialog v-model="settlementInvoicePreviewDialog" max-width="800">
      <v-card>
        <v-card-title>
          Podgląd faktury
          <v-spacer></v-spacer>
          <v-btn icon @click="settlementInvoicePreviewDialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-card-text class="text-center">
          <v-progress-circular v-if="settlementInvoicePreviewLoading" indeterminate color="primary" />
          <iframe
            v-else-if="settlementInvoicePreviewUrl && settlementInvoicePreviewIsPdf"
            :src="settlementInvoicePreviewUrl"
            style="width: 100%; height: 600px; border: none;"
          />
          <img
            v-else-if="settlementInvoicePreviewUrl"
            :src="settlementInvoicePreviewUrl"
            style="max-width: 100%; max-height: 600px;"
          />
        </v-card-text>
      </v-card>
    </v-dialog>
    <!-- Document Preview Dialog -->
    <v-dialog v-model="documentPreviewDialog" max-width="900" scrollable>
      <v-card>
        <v-card-title>
          <v-icon left>mdi-eye</v-icon>
          Podgląd dokumentu
          <v-spacer />
          <v-btn icon @click="documentPreviewDialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-card-text>
          <div class="pa-4" style="background: white; border: 1px solid #e0e0e0; border-radius: 4px; font-family: 'Times New Roman', serif; line-height: 1.6; color: #333;" v-html="documentPreviewHtml"></div>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import SignatureField from "@/components/SignatureField.vue";
import LegalizationSection from "@/components/LegalizationSection.vue";
import BoltDriverQuality from "@/components/bolt/BoltDriverQuality.vue";
import InternalDocumentsSection from "@/components/InternalDocumentsSection.vue";
import { maDateWaznosci } from "@/utils/dokumentyBezWaznosci";
import Swal from "sweetalert2";
import { canAccessSubtab, canEditSubtab } from "@/services/permissions";
const axios = require("axios");
import * as dayjs from "dayjs";

// Baza dla potrącenia 15% = człon `0.15*(...)` we wzorze (kampanie + napiwki
// / promocje / Program Quest — zależnie od formatu pliku). Wcześniej kolumna
// "Bonus / 15%" czytała `bonusField`, które we WSZYSTKICH wzorach jest puste →
// zawsze 0, mimo że wzór realnie odejmuje te 15% (siedzi w "Obliczone").
// Tu wyciągamy realną bazę z tych samych pól, których używa wzór. Zwraca grosze.
function bonus15Base(equationStr, rawData) {
  if (!equationStr || !rawData) return 0;
  const m =
    equationStr.match(/0\.15\s*\*\s*\(([^)]+)\)/) ||
    equationStr.match(/0\.15\s*\*\s*([A-Za-z0-9_]+)/);
  if (!m) return 0;
  const fields = m[1].split("+").map((f) => f.trim()).filter(Boolean);
  let sum = 0;
  for (const f of fields) {
    const v = parseFloat(String(rawData[f] == null ? "0" : rawData[f]).replace(",", "."));
    if (!isNaN(v)) sum += v;
  }
  return Math.round(sum * 100);
}

export default {
  name: "DriverDetailsPage",
  components: { SignatureField, LegalizationSection, BoltDriverQuality, InternalDocumentsSection },
  props: ["selectedDriver", "showFull"],
  data() {
    return {
      // Uprawnienie do pól rozliczeniowych — nie ma go w tokenie, czytamy z /me.
      mojeUprawnienieRozliczen: false,
      // Wartości pól rozliczeniowych POBRANE Z BAZY (nie z formularza) — po nich
      // poznajemy, czy admin bez uprawnienia ustawia pole po raz pierwszy
      // (wolno mu, backend też na to pozwala), czy zmienia istniejące (nie wolno).
      polaRozliczenZBazy: {},
      deposits: [],
      validBonus: false,
      dialogBonus: false,
      menuDate: false,
          statusOptions: [
        { text: "Oczekujący", value: "PENDING" },
        { text: "Wypłata utworzona", value: "PAYOUT_CREATED" },
        { text: "Zapłacony", value: "PAID" },
      ],
      bonusType: [
        { text: "POZYTYWNY", value: "POSITIVE" },
        { text: "NEGATYWNY", value: "NEGATIVE" },
      ],
          bonus: {
        date: null,
        name: null,
        type: null,
        status: null,
        price: null,
        file: null,
        preview: null,
      },
      numericId: 0,
      loadingInvoices: false,
      totalItemsInvoices: 0,
      searchInvoices: "",
      optionsInvoices: {
        page: 1,
        itemsPerPage: 10,
      },
      loadingBonuses: false,
      totalItemsBonuses: 0,
      searchBonuses: "",
      optionsBonuses: {
        page: 1,
        itemsPerPage: 10,
      },      
      loadingRent: false,
      totalItemsRent: 0,
      searchRent: "",
      optionsRent: {
        page: 1,
        itemsPerPage: 10,
      },
      loadingImport: false,
      totalItemsImport: 0,
      searchImport: "",
      optionsImport: {
        page: 1,
        itemsPerPage: 10,
      },
      expandedImport: [],
      loadingPayouts: false,
      totalItemsPayouts: 0,
      searchPayouts: "",
      optionsPayouts: {
        page: 1,
        itemsPerPage: 10,
      },
      payouts: [],
      expandedPayouts: [],
      activeTab: 0, // To manage which tab is active
      valid: false, // Form validation flag
      documentDataSaving: false,
      formData: {
        firstName: "",
        lastName: "",
        phone: "",
        email: "",
        city: "",
        streetAddress: "",
        homeNumber: "",
        aptNumber: "",
        postalCode: "",
        licenseNumber: "",
        boltId: "",
        uberId: "",
        freeNowId: "",
        payoutType: "",
        payoutSchedule: "",
        applySettlementChangeNow: false,
        cooperationType: "",
        zusType: "",
        accountNo: "",
        isAbroadBankAccount: false,
        isReposession: false,
        reposessionAccountNo: "",
        reposessionWireTitle: "",
        reposessionAddressLine1: "",
        reposessionAddressLine2: "",
        isReadyForSettlements: false,
        payoutBlocked: false,
        drivingLicenseValidTo: '',
        drivingLicenseIndefinite: false,
        medicalCertificateNumber: '',
        medicalValidTo: '',
        psychologicalCertificateNumber: '',
        psychologicalValidTo: '',
        criminalRecordDate: '',
        idCardNumber: '',
        idCardValidTo: '',
        payoutBlockReason: '',
        missingData: [],
        registerType: "",
        carId: null,
        subCompanyIds: [],
      },
      departments: [],
      subCompanies: [],
      akceptacjaTrwa: false,
      payoutTypes: [
        { text: "Gotówka", value: "CASH" },
        { text: "Przelew", value: "WIRE" }
      ],
      payoutSchedules: [
        { text: "Codziennie", value: "DAILY" },
        { text: "Tygodniowo", value: "WEEKLY" },
        { text: "Co 2 tygodnie", value: "EVERY_2_WEEKS" },
        { text: "Miesięcznie", value: "MONTH" }
      ],
      cooperationTypes: [
        { text: "B2B z VAT", value: "B2B_VAT" },
        { text: "B2B bez VAT", value: "B2B_NO_VAT" },
        { text: "50/50", value: "FIFTY_FIFTY" },
        { text: "Wynajem", value: "RENT" },
        { text: "Połączenie", value: "CONNECTION" },
      ],
      zusTypes: [
        { text: "B2B", value: "B2B" },
        { text: "Nie zatrudniony z L4", value: "NOT_HIRED_L4" },
        { text: "Nie zatrudniony bez L4", value: "NOT_HIRED_NO_L4" },
        { text: "Zatrudniony poniżej z L4", value: "HIRED_BELOW_WITH_L4" },
        { text: "Zatrudniony poniżej bez L4", value: "HIRED_BELOW_NO_L4" },
        { text: "Zatrudniony powyżej", value: "HIRED_ABOVE" },
        { text: "Student", value: "STUDENT" },
        { text: "Poniżej 26 lat bez L4", value: "UNDER_26_NO_L4" },
        { text: "Poniżej 26 lat z L4", value: "UNDER_26_L4" },
      ],
      registerTypes: [
        { text: 'Jestem kierowcą', value: 'IM_DRIVER' },
        { text: 'Z polecenia', value: 'RECOMENDATION' },
        { text: 'Od innego partnera', value: 'OTHER_PARTNER' },
        { text: 'Ogłoszenie o pracę', value: 'JOB_LOOKUP' },
        { text: 'Dodany przez admina', value: 'ADMIN' },
      ],
      rules: {
        required: (value) => !!value || "To pole jest wymagane",
        email: (value) =>
          /.+@.+\..+/.test(value) || "Wprowadź prawidłowy adres email",
        bankAccount: (value) => {
          if (!value) return true;
          const digits = value.replace(/\s/g, '');
          if (!/^\d{26}$/.test(digits)) return "Numer konta musi mieć 26 cyfr";
          // NRB/IBAN mod 97 check (PL prefix)
          const rearranged = digits.substring(2) + '2521' + digits.substring(0, 2);
          let remainder = '';
          for (const ch of rearranged) {
            remainder += ch;
            remainder = String(parseInt(remainder) % 97);
          }
          return parseInt(remainder) === 1 || "Nieprawidłowy numer konta bankowego";
        },
      },
      documents: {
        license: null,
        insurance: null,
        registration: null,
      },
      invoices: [],
      settlements: [
        { id: 1, date: "2024-01-05", amount: 800, status: "Completed" },
        { id: 2, date: "2024-01-12", amount: 400, status: "Pending" },
      ],
bonusesHeaders: [
        {
          text: "Data",
          align: "start",
          sortable: false,
          value: "date",
        },
        {
          text: "Typ",
          align: "start",
          sortable: false,
          value: "type",
        },
        {
          text: "Kwota",
          align: "start",
          sortable: false,
          value: "price",
        },
                {
          text: "Opis",
          align: "start",
          sortable: false,
          value: "description",
        },
        {
          text: "Status",
          align: "start",
          sortable: false,
          value: "status",
        },
        { text: "", value: "download", sortable: false },
        { text: "", value: "deleteBonus", sortable: false },
      ],
    rentsHeaders: [
        {
          text: "Data",
          align: "start",
          sortable: false,
          value: "date",
        },
        {
          text: "Kwota",
          align: "start",
          sortable: false,
          value: "price",
        },
        {
          text: "Status",
          align: "start",
          sortable: false,
          value: "status",
        },
        { text: "", value: "details", sortable: false },
        { text: "", value: "deleteRent", sortable: false },
      ],
      bonuses:[],
      invoiceHeaders: [
        { text: "Data", align: "start", sortable: false, value: "date" },
        { text: "Brutto 23%", align: "end", sortable: false, value: "brutto23" },
        { text: "Netto 23%", align: "end", sortable: false, value: "netto23" },
        { text: "Brutto 8%", align: "end", sortable: false, value: "brutto8" },
        { text: "Netto 8%", align: "end", sortable: false, value: "netto8" },
        { text: "Brutto 5%", align: "end", sortable: false, value: "brutto5" },
        { text: "Netto 5%", align: "end", sortable: false, value: "netto5" },
        { text: "Brutto 0%", align: "end", sortable: false, value: "brutto0" },
        { text: "Netto 0%", align: "end", sortable: false, value: "netto0" },
        { text: "Dla Kierowcy", align: "end", sortable: false, value: "returnToDriver" },
        { text: "Status", align: "start", sortable: false, value: "status" },
        { text: "Podgląd", align: "center", sortable: false, value: "preview" },
      ],
      invoicePreviewDialog: false,
      invoicePreviewLoading: false,
      invoicePreviewType: null,
      invoicePreviewSrc: null,
      invoicePreviewData: {},
      rents:[],
      importedHeaders: [
        { text: "Data", sortable: false,align: "start", value: "date" },
        { text: "Gotówka", sortable: false,align: "start", value: "cash" },
        { text: "Obliczone", sortable: false,align: "start", value: "calculated" },
        { text: "Rodzaj Wzoru", sortable: false,align: "start", value: "cooperationType" },
        { text: "Aplikacja", sortable: false,align: "start", value: "type"},
        { text: "Status", sortable: false,align: "start", value: "status"},
        { text: "Wzór", sortable: false,align: "start", value: "equationName"},
        { text: "Id Rozliczenia", sortable: false,align: "start", value: "settlementId"},
      ],
      // Driver private cars
      driverPrivateCars: [],
      driverPrivateCarsLoading: false,
      driverPrivateCarsHeaders: [
        { text: "Nazwa", value: "name" },
        { text: "Nr boczny", value: "sideNumber" },
        { text: "Marka", value: "brand" },
        { text: "Model", value: "model" },
        { text: "Rejestracja", value: "plate" },
        { text: "Rok", value: "year" },
        { text: "Przebieg", value: "mileage" },
        { text: "Licencje", value: "licenses", sortable: false },
        { text: "Status", value: "status" },
        { text: "Akcje", value: "actions", sortable: false, width: "100px" },
      ],
      // Settlement invoice upload/preview
      settlementInvoiceUploadDialog: false,
      settlementInvoiceUploadId: null,
      settlementInvoiceFile: null,
      settlementInvoicePreviewDialog: false,
      settlementInvoicePreviewUrl: null,
      settlementInvoicePreviewIsPdf: false,
      settlementInvoicePreviewLoading: false,
      // payoutsHeaders is now a computed property
      currentDriverId: null,
      hardData: { pesel: '', dateOfBirth: '', citizenship: '', mainLanguage: '' },
      savingHardData: false,
      cars: [],
      imported: [],
      cooperationTypeHistory: [],
      rentHistory: [],
      settingsHistory: [],

      // Bailiffs (Komornicy)
      bailiffs: [],
      bailiffsLoading: false,
      bailiffDialog: false,
      editingBailiff: null,
      bailiffForm: {
        name: "",
        accountNo: "",
        wireTitle: "",
        addressLine1: "",
        addressLine2: "",
        email: "",
        isActive: false,
        transferTypes: [],
        totalAmountZl: null,
        paidAmountZl: null,
        monthlyLimitZl: null,
      },
      bailiffRegistry: [],
      bailiffHeaders: [
        { text: "Aktywny", value: "isActive", sortable: false, width: "80px" },
        { text: "Nazwa", value: "name" },
        { text: "Nr konta", value: "accountNo" },
        { text: "Tytuł przelewu", value: "wireTitle" },
        { text: "Przelewy", value: "transferTypes", sortable: false },
        { text: "Spłata", value: "progress", sortable: false },
        { text: "Data dodania", value: "createdAt" },
        { text: "Akcje", value: "actions", sortable: false },
      ],
      transferTypeOptions: [
        { text: "Zleceniówka", value: "ZLECENIOWKA" },
        { text: "Zwrot kosztów", value: "ZWROT_KOSZTOW" },
        { text: "Najem", value: "NAJEM" },
      ],
      transferTypeLabels: {
        ZLECENIOWKA: "Zleceniówka",
        ZWROT_KOSZTOW: "Zwrot kosztów",
        NAJEM: "Najem",
      },

      // Pisma komornicze
      komornikLetters: [],
      komornikLettersLoading: false,
      komornikLetterDialog: false,
      komornikLetterSaving: false,
      komornikFeeZl: 0,
      komornikLetterForm: {
        title: "",
        caseNumber: "",
        bailiffId: null,
        description: "",
        receivedAt: "",
        responseDeadline: "",
        files: [],
      },
      // Generowanie odpowiedzi
      responseDialog: false,
      responseSaving: false,
      responsePreviewing: false,
      responseLetter: null,
      canUseFacsimileSelf: false,
      komornikTemplates: [],
      responseForm: {
        variant: "WORKS",
        body: "",
        signatureType: "MANUAL",
      },
      responseVariants: [
        { value: "WORKS", label: "Pracuje / współpracuje" },
        { value: "NOT_WORKS", label: "Nie pracuje / nie współpracuje" },
        { value: "GARNISHMENT", label: "Zbieg egzekucji (zbieg zajęć)" },
        { value: "CUSTOM", label: "Własne pismo" },
      ],
      komornikLetterHeaders: [
        { text: "Tytuł / sygnatura", value: "title" },
        { text: "Otrzymano", value: "receivedAt" },
        { text: "Termin odpowiedzi", value: "responseDeadline" },
        { text: "Status", value: "status", sortable: false },
        { text: "Opłata", value: "feeCents", sortable: false },
        { text: "Pliki", value: "files", sortable: false },
        { text: "Akcje", value: "actions", sortable: false },
      ],

      // Driver documents data
      driverDocuments: [],
      loadingDriverDocuments: false,
      processingDocuments: [],
      downloadingDocuments: [],
      previewingDocuments: [],
      documentPreviewDialog: false,
      documentPreviewHtml: '',

      driverDocumentsHeaders: [
        { text: 'Nazwa dokumentu', value: 'docName' },
        { text: 'Status', value: 'status' },
        { text: 'Data ważności', value: 'expiryDate' },
        { text: 'Data utworzenia', value: 'createdAt' },
        { text: 'Akcje', value: 'actions', sortable: false }
      ],

      // Onboarding documents (4g)
      onboardingDocuments: [],
      loadingOnboardingDocuments: false,
      downloadingOnboardingDocs: [],
      onboardingDocPreviewDialog: false,
      onboardingDocPreviewUrl: null,
      onboardingDocPreviewMime: '',
      onboardingDocumentsHeaders: [
        { text: 'Typ dokumentu', value: 'documentType' },
        { text: 'Nazwa pliku', value: 'originalName' },
        { text: 'Status', value: 'status' },
        { text: 'Data przesłania', value: 'createdAt' },
        { text: 'Akcje', value: 'actions', sortable: false }
      ],

      // Upload document (admin)
      uploadDocDialog: false,
      uploadingDoc: false,
      uploadDocError: '',
      uploadDocForm: {
        documentType: null,
        documentNumber: '',
        expiryDate: '',
        file: null,
      },
      // ── Oświadczenia ZUS w głównej zakładce ──
      zusDeclarations: null,
      zusLoading: true,
      zusEditMode: false,
      zusSaving: false,
      zusForm: {},
      // Kolejność i nazwy jak na „Rejestracjach kierowców", żeby biuro widziało
      // w obu miejscach to samo. Cztery pierwsze decydują o dokumentach w apce.
      polaZus: [
        { klucz: 'isStudent', etykieta: 'Student' },
        { klucz: 'hasPensionRights', etykieta: 'Prawa emerytalne' },
        { klucz: 'hasRentRights', etykieta: 'Prawa rentowe' },
        { klucz: 'hasDisabilityCert', etykieta: 'Orzeczenie o niepełnosprawności' },
        { klucz: 'isEmployedElsewhere', etykieta: 'Zatrudniony gdzie indziej' },
        { klucz: 'earnsAboveMinimum', etykieta: 'Zarabia powyżej minimalnej' },
        { klucz: 'wantsVoluntarySickness', etykieta: 'Dobrowolne chorobowe' },
        { klucz: 'isRegisteredUnemployed', etykieta: 'Zarejestrowany bezrobotny' },
      ],
      // Kod obywatelstwa kierowcy (PL, UA…) — Polakowi nie pokazujemy braku
      // karty pobytu na liście dokumentów.
      kodObywatelstwa: null,
      uploadDocTypeOptions: [
        // Dowód osobisty (przód/tył) usunięty 12.10.2026 — kopii nie
        // przechowujemy (RODO); seria i numer są w „Danych do wniosków”.
        { text: 'Karta pobytu (przód)', value: 'RESIDENCE_CARD_FRONT' },
        { text: 'Karta pobytu (tył)', value: 'RESIDENCE_CARD_BACK' },
        { text: 'Prawo jazdy (przód)', value: 'DRIVING_LICENSE' },
        { text: 'Prawo jazdy (tył)', value: 'DRIVING_LICENSE_BACK' },
        { text: 'Dowód rejestracyjny (przód)', value: 'VEHICLE_REG_FRONT' },
        { text: 'Dowód rejestracyjny (tył)', value: 'VEHICLE_REG_BACK' },
        { text: 'Zdjęcie pojazdu (przód)', value: 'VEHICLE_PHOTO_FRONT' },
        { text: 'Zdjęcie pojazdu (tył)', value: 'VEHICLE_PHOTO_BACK' },
        { text: 'Ubezpieczenie OC', value: 'INSURANCE_OC' },
        { text: 'Badanie psychotechniczne', value: 'PSYCHO_CERT' },
        { text: 'Badanie lekarskie', value: 'MEDICAL_CERT' },
        { text: 'Zaświadczenie o niekaralności (PL)', value: 'CRIMINAL_RECORD' },
        { text: 'Zaświadczenie o niekaralności (zagraniczne)', value: 'CRIMINAL_RECORD_FOREIGN' },
        { text: 'Tłumaczenie zaświadczenia o niekaralności', value: 'CRIMINAL_RECORD_TRANSLATION' },
        { text: 'Legitymacja studencka (przód)', value: 'STUDENT_ID_FRONT' },
        { text: 'Legitymacja studencka (tył)', value: 'STUDENT_ID_BACK' },
        { text: 'Zaświadczenie o emeryturze', value: 'PENSION_CERT' },
        { text: 'Orzeczenie o niepełnosprawności', value: 'DISABILITY_CERT' },
        { text: 'Zaświadczenie o rencie', value: 'RENT_CERT' },
      ],
    };
  },
  computed: {
    // Zdjęcia pojazdu i niekaralność — bez daty ważności.
    uploadMaWaznosc() {
      return maDateWaznosci(this.uploadDocForm.documentType);
    },
    czekaNaAkceptacje() {
      const st = this.formData && this.formData.onboardingStatus;
      return !!st && !['READY_TO_WORK', 'REGISTRATION_STARTED'].includes(st);
    },
    // Pola rozliczeniowe może ruszyć head admin ALBO admin z uprawnieniem nadanym
    // w zakładce Administratorzy. Wcześniej decydowała sama rola, przez co
    // pracownik nie zapisał karty nowego kierowcy: pola były wyszarzone, a
    // jednocześnie wymagane, więc formularz nigdy nie stawał się poprawny.
    mozeEdytowacRozliczenia() {
      return this.isHeadAdmin || this.mojeUprawnienieRozliczen;
    },
    activeDeposit() {
      return this.deposits.find((d) => d.status === 'ACTIVE' || d.status === 'COLLECTED') || null;
    },
    // Auta, na które można założyć kaucję — te, które kierowca faktycznie ma
    // przypisane. Kaucja bez auta nie ma sensu przy zdaniu i zwrocie.
    depositCars() {
      const lista = [];
      const dodaj = (car, opis) => {
        if (!car || !car.id || lista.some((c) => c.id === car.id)) return;
        const nazwa = [car.make, car.model].filter(Boolean).join(' ');
        lista.push({
          id: car.id,
          label: `${car.plate || 'bez rejestracji'}${nazwa ? ` — ${nazwa}` : ''}${opis ? ` (${opis})` : ''}`,
        });
      };
      dodaj(this.formData?.car, 'główne');
      dodaj(this.formData?.secondCar, 'drugie');
      return lista;
    },
    isHeadAdmin() {
      // Typ/sposób rozliczeń zmienia wyłącznie head admin (reguła też na backendzie).
      return localStorage.getItem('actualRole') === 'headadmin' || localStorage.getItem('role') === 'headadmin';
    },
    isOwner() {
      try {
        const t = localStorage.getItem('token');
        if (!t) return false;
        const p = JSON.parse(atob(t.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
        const u = p.user || p;
        // TYLKO head admin (właściciel). isHead/hasAccessToAllCompanies mają też
        // inni admini — nie wystarczają (patrz zacieśnienie impersonacji 2026-07-13).
        return u.actualRole === 'headadmin';
      } catch (e) {
        return false;
      }
    },
    tabConfig() {
      return [
        { key: 'driverDetails.tabs.general',     icon: 'mdi-account-circle' },
        { key: 'driverDetails.tabs.documents',   icon: 'mdi-file-document-outline' },
        { key: 'driverDetails.tabs.invoices',    icon: 'mdi-receipt' },
        { key: 'driverDetails.tabs.bonuses',     icon: 'mdi-gift-outline' },
        { key: 'driverDetails.tabs.carRental',   icon: 'mdi-key-chain' },
        { key: 'driverDetails.tabs.importData',  icon: 'mdi-database-import' },
        { key: 'driverDetails.tabs.payouts',     icon: 'mdi-cash-multiple' },
        { key: 'driverDetails.tabs.cars',        icon: 'mdi-car' },
        { key: 'driverDetails.tabs.bailiffs',    icon: 'mdi-gavel' },
        { key: 'driverDetails.tabs.history',     icon: 'mdi-history' },
        { key: 'driverDetails.tabs.legalization', icon: 'mdi-passport', slug: 'legalization' },
      ];
    },
    // Zapis danych pobytowych wymaga tego samego uprawnienia, którego pilnuje
    // backend (@PageEdit('legalization')) — inaczej przycisk obiecywałby coś,
    // czego serwer i tak nie przyjmie.
    canEditLegalization() {
      return canEditSubtab('legalization', 'profile');
    },
    mergedDriverDocuments() {
      // Checklist: wszystkie wymagane typy z uploadDocTypeOptions + ewentualne template-based
      const onboardingByType = new Map(
        (this.onboardingDocuments || []).map((d) => [d.documentType, d]),
      );
      const checklist = (this.uploadDocTypeOptions || [])
        // Karta pobytu dotyczy tylko cudzoziemców — u Polaka bez wgranej
        // karty nie pokazujemy „brak". Przy nieustalonym obywatelstwie zostaje.
        .filter((opt) => !(
          String(opt.value).startsWith('RESIDENCE_CARD_') &&
          this.kodObywatelstwa === 'PL' &&
          !onboardingByType.has(opt.value)
        ))
        .map((opt) => {
        const doc = onboardingByType.get(opt.value);
        if (doc) {
          return {
            id: 'onb-' + doc.id,
            rawId: doc.id,
            source: 'onboarding',
            docName: opt.text,
            documentType: opt.value,
            exists: true,
            status: doc.status || 'PENDING',
            createdAt: doc.createdAt,
            expiryDate: doc.expiryDate || null,
            raw: doc,
          };
        }
        return {
          id: 'missing-' + opt.value,
          rawId: null,
          source: 'missing',
          docName: opt.text,
          documentType: opt.value,
          exists: false,
          status: 'MISSING',
          createdAt: null,
          expiryDate: null,
          raw: null,
        };
      });
      const fromTemplates = (this.driverDocuments || []).map((d) => ({
        id: 'tpl-' + d.id,
        rawId: d.id,
        source: 'template',
        docName: d.template?.name || 'Dokument z szablonu',
        documentType: null,
        exists: true,
        status: d.accepted ? 'ACCEPTED' : (d.submitted ? 'SUBMITTED' : 'PENDING'),
        createdAt: d.createdAt,
        expiryDate: null,
        raw: d,
      }));
      // Najpierw checklist (po nazwie), potem template-based
      checklist.sort((a, b) => a.docName.localeCompare(b.docName, 'pl'));
      return [...checklist, ...fromTemplates];
    },
    payoutsHeaders() {
      const ct = this.formData.cooperationType;
      if (ct === 'B2B_VAT' || ct === 'B2B_NO_VAT') {
        return [
          { text: "Data", sortable: false, align: "start", value: "date" },
          { text: "Suma", sortable: false, align: "end", value: "totalToPayout" },
          { text: "Przelew z tyt. faktury", sortable: false, align: "end", value: "netPayout" },
          { text: "Typ Współpracy", sortable: false, align: "start", value: "cooperationType" },
          { text: "Faktura", sortable: false, align: "center", value: "invoiceAction" },
          { text: "Status", sortable: false, align: "center", value: "status" },
        ];
      }
      return [
        { text: "Data", sortable: false, align: "start", value: "date" },
        { text: "Suma", sortable: false, align: "end", value: "totalToPayout" },
        { text: "Potr. ZUS", sortable: false, align: "end", value: "zusDeduction" },
        { text: "Zleceniówka", sortable: false, align: "end", value: "zleceniowkaAmount" },
        { text: "Zwrot kosztów", sortable: false, align: "end", value: "costInvoiceReimbursement" },
        { text: "Najem", sortable: false, align: "end", value: "rentTransferAmount" },
        { text: "Typ Współpracy", sortable: false, align: "start", value: "cooperationType" },
        { text: "Faktura", sortable: false, align: "center", value: "invoiceAction" },
        { text: "Status", sortable: false, align: "center", value: "status" },
      ];
    },
  },
  watch: {
    "formData.subCompanyIds": function (newVal) {
      if (newVal) {
        this.getDepartmentsSelect();
      } else {
        this.departments = [];
      }
    },
    activeTab: {
      async handler() {
        switch (this.activeTab) {
          case 1:
            await this.fetchDriverDocuments();
            await this.fetchOnboardingDocuments();
            break;
          case 2:
            await this.getInvoices();
            break;
          case 3:
            await this.getBonuses()
            break;
          case 4:
            await this.getRents()
            break;
          case 5:
            await this.getImported()
            break;
          case 6:
            await this.getPayouts()
            break;
          case 7:
            await this.fetchDriverPrivateCars();
            break;
          case 8:
            await this.fetchBailiffs()
            await this.fetchKomornikLetters()
            break;
          case 9:
            await this.getCooperationTypeHistory()
            await this.getRentHistory()
            await this.getSettingsHistory()
            break;
        }
      },
    },
    optionsInvoices: {
      async handler() {
        await this.getInvoices();
      },
    },
    searchInvoices: {
      async handler() {
        await this.getInvoices();
      },
    },
    optionsBonuses: {
      async handler() {
        await this.getBonuses();
      },
    },
    searchBonuses: {
      async handler() {
        await this.getBonuses();
      },
    },
    optionsRent: {
      async handler() {
        await this.getRents();
      },
    },
    searchRent: {
      async handler() {
        await this.getRents();
      },
    },
    optionsImport: {
      async handler() {
        await this.getImported();
      },
    },
    searchImport: {
      async handler() {
        await this.getImported();
      },
    },
    optionsPayouts: {
      async handler() {
        await this.getPayouts();
      },
    },
    searchPayouts: {
      async handler() {
        await this.getPayouts();
      },
    },
  },
  async created() {
    console.log(dayjs('2025-04-1').diff(dayjs(),'months'))
    const params = new URLSearchParams(document.location.search);
    const id = params.get("id");
    this.currentDriverId = id;
    await this.initialize(id);
    this.loadDeposits();
    this.pobierzMojeUprawnienia();
    this.pobierzOswiadczeniaZus();
    // Jeśli user nie ma dostępu do bieżącej zakładki, przejdź do pierwszej dozwolonej
    const firstAllowed = this.tabConfig.findIndex((t) => this.canAccess(t.slug));
    if (firstAllowed >= 0 && !this.canAccess(this.tabConfig[this.activeTab]?.slug)) {
      this.activeTab = firstAllowed;
    }
  },
  methods: {
    // Bolt Fleet API 2.0 — przycisk „Przypisz” w sekcji auta z Bolta.
    async boltAutoPrzypisane(auto) {
      Swal.fire({ icon: 'success', title: this.$t('boltQuality.carAssigned', { plate: auto.plate }), timer: 1800, showConfirmButton: false });
      if (this.currentDriverId) await this.initialize(this.currentDriverId);
    },
    boltBlad(m) {
      Swal.fire('Błąd', String(m), 'error');
    },
    // Ta sama akceptacja co na liście „Kierowcy do akceptacji”.
    async zaakceptujKierowce() {
      const ok = await Swal.fire({
        icon: 'question',
        title: 'Zaakceptować kierowcę?',
        html: 'Kierowca zniknie z listy do akceptacji, dostanie SMS i e-mail z nowym hasłem do aplikacji (zmieni je przy pierwszym logowaniu) i trafi do wykazu kierowców.',
        showCancelButton: true,
        confirmButtonText: 'Zaakceptuj',
        cancelButtonText: 'Anuluj',
      });
      if (!ok.isConfirmed) return;
      this.akceptacjaTrwa = true;
      try {
        await axios({
          method: 'post',
          url: process.env.VUE_APP_ROOT_API + 'driver-registration/admin/approve',
          headers: { Authorization: 'Bearer ' + localStorage.getItem('token') },
          data: {
            driverId: this.formData.id,
            companyId: this.formData.companyId,
            subCompanyIds: this.formData.subCompanyIds || [],
          },
        });
        this.formData = { ...this.formData, onboardingStatus: 'READY_TO_WORK' };
        Swal.fire({ icon: 'success', title: 'Kierowca zaakceptowany', timer: 1800, showConfirmButton: false });
      } catch (e) {
        Swal.fire({ icon: 'error', title: 'Nie udało się zaakceptować', text: e.response?.data?.message || '' });
      } finally {
        this.akceptacjaTrwa = false;
      }
    },
    /**
     * Oświadczenia ZUS kierowcy. Ta sama trasa, z której korzystają
     * „Rejestracje kierowców" — i, co kluczowe, NIE filtruje po statusie
     * onboardingu, więc działa też dla kierowcy gotowego do pracy.
     */
    async pobierzOswiadczeniaZus() {
      this.zusLoading = true;
      try {
        const res = await axios({
          method: 'get',
          url: process.env.VUE_APP_ROOT_API + 'driver-registration/admin/driver/' + this.currentDriverId,
          headers: { Authorization: 'Bearer ' + localStorage.getItem('token') },
        });
        this.zusDeclarations = res.data?.data?.zusDeclarations || null;
      } catch (e) {
        console.warn('Nie udało się pobrać oświadczeń ZUS:', e);
        this.zusDeclarations = null;
      } finally {
        this.zusLoading = false;
      }
    },
    wejdzWEdycjeZus() {
      // Formularz startuje od PEŁNEGO stanu z bazy, nie od pustego obiektu.
      // Zapis nadpisuje cały zestaw i każde pominięte pole ląduje jako „nie",
      // więc edycja jednego przełącznika na pustym formularzu wyzerowałaby
      // pozostałe. Dlatego wypełniamy wszystkie klucze jawnie.
      const d = this.zusDeclarations || {};
      const form = {};
      for (const pole of this.polaZus) form[pole.klucz] = !!d[pole.klucz];
      form.taxOffice = d.taxOffice || '';
      form.nfzBranch = d.nfzBranch || '';
      this.zusForm = form;
      this.zusEditMode = true;
    },
    async zapiszZus() {
      this.zusSaving = true;
      try {
        await axios({
          method: 'post',
          url: process.env.VUE_APP_ROOT_API + 'driver-registration/admin/edit-zus',
          headers: { Authorization: 'Bearer ' + localStorage.getItem('token') },
          data: { driverId: this.currentDriverId, ...this.zusForm },
        });
        this.zusEditMode = false;
        await this.pobierzOswiadczeniaZus();
        Swal.fire({
          title: 'Zapisano',
          text: 'Oświadczenia ZUS zaktualizowane. Dokumenty w aplikacji kierowcy dopasują się przy następnym wejściu w zakładkę Dokumenty.',
          icon: 'success',
          timer: 4000,
        });
      } catch (e) {
        Swal.fire({
          title: 'Błąd',
          text: e.response?.data?.message || 'Nie udało się zapisać oświadczeń ZUS',
          icon: 'error',
        });
      } finally {
        this.zusSaving = false;
      }
    },
    // Blokada wypłaty — osobna trasa, bo wymaga powodu i wysyła powiadomienie.
    async onPayoutBlockToggle(val) {
      const blocked = !!val;
      if (blocked) {
        const r = await Swal.fire({
          title: 'Zablokować wypłatę?',
          input: 'textarea',
          inputLabel: 'Powód — zobaczy go kierowca',
          inputPlaceholder: 'np. brak rozliczonej gotówki za ubiegły tydzień',
          inputValidator: (v) => (!v || !v.trim() ? 'Podaj powód blokady' : undefined),
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Zablokuj wypłatę',
          cancelButtonText: 'Anuluj',
        });
        if (!r.isConfirmed) { this.formData.payoutBlocked = false; return; }
        await this.savePayoutBlock(true, (r.value || '').trim());
      } else {
        const r = await Swal.fire({
          title: 'Zdjąć blokadę wypłaty?',
          text: 'Kierowca dostanie powiadomienie, a rozliczenie wejdzie w najbliższym przebiegu.',
          icon: 'question',
          showCancelButton: true,
          confirmButtonText: 'Zdejmij blokadę',
          cancelButtonText: 'Anuluj',
        });
        if (!r.isConfirmed) { this.formData.payoutBlocked = true; return; }
        await this.savePayoutBlock(false, '');
      }
    },
    // Dane przepisywane na wnioski do urzędu. Osobny endpoint od reszty karty,
    // bo zapis pozostałych pól niesie reguły rozliczeniowe (typ współpracy,
    // harmonogram wypłat) i potrafi odroczyć zmianę do następnego tygodnia.
    async saveDocumentData() {
      this.documentDataSaving = true;
      try {
        const res = await axios({
          method: 'patch',
          url: process.env.VUE_APP_ROOT_API + 'drivers/' + this.currentDriverId + '/document-data',
          headers: { Authorization: 'Bearer ' + localStorage.getItem('token') },
          data: {
            drivingLicenseValidTo: this.formData.drivingLicenseValidTo || '',
            drivingLicenseIndefinite: !!this.formData.drivingLicenseIndefinite,
            medicalCertificateNumber: this.formData.medicalCertificateNumber || '',
            medicalValidTo: this.formData.medicalValidTo || '',
            psychologicalCertificateNumber: this.formData.psychologicalCertificateNumber || '',
            psychologicalValidTo: this.formData.psychologicalValidTo || '',
            criminalRecordDate: this.formData.criminalRecordDate || '',
            idCardNumber: this.formData.idCardNumber || '',
            idCardValidTo: this.formData.idCardValidTo || '',
          },
        });
        const d = (res.data && res.data.data) || {};
        // Serwer oddaje daty w ISO, a pola `type="date"` chcą RRRR-MM-DD.
        ['drivingLicenseValidTo', 'medicalValidTo', 'psychologicalValidTo', 'criminalRecordDate', 'idCardValidTo']
          .forEach((k) => { this.formData[k] = d[k] ? String(d[k]).slice(0, 10) : ''; });
        this.formData.drivingLicenseIndefinite = !!d.drivingLicenseIndefinite;
        this.formData.idCardNumber = d.idCardNumber || '';
        Swal.fire({
          icon: 'success',
          title: 'Zapisano',
          text: 'Dane podstawią się przy kolejnym wniosku do urzędu.',
          timer: 2200,
          showConfirmButton: false,
        });
      } catch (e) {
        Swal.fire('Błąd', (e.response && e.response.data && String(e.response.data.message)) || 'Nie udało się zapisać danych', 'error');
      } finally {
        this.documentDataSaving = false;
      }
    },
    async savePayoutBlock(blocked, reason) {
      try {
        const res = await axios({
          method: 'patch',
          url: process.env.VUE_APP_ROOT_API + 'drivers/' + this.currentDriverId + '/payout-block',
          headers: { Authorization: 'Bearer ' + localStorage.getItem('token') },
          data: { blocked, reason },
        });
        const d = res.data?.data || {};
        this.formData.payoutBlocked = !!d.payoutBlocked;
        this.formData.payoutBlockReason = d.payoutBlockReason || '';
        Swal.fire({
          icon: 'success',
          title: blocked ? 'Wypłata zablokowana' : 'Blokada zdjęta',
          text: blocked
            ? 'Kierowca dostał powiadomienie z powodem.'
            : 'Kierowca dostał powiadomienie o zdjęciu blokady.',
          timer: 2500,
          showConfirmButton: false,
        });
      } catch (e) {
        this.formData.payoutBlocked = !blocked;
        Swal.fire('Błąd', e.response?.data?.message?.toString() || 'Nie udało się zapisać blokady', 'error');
      }
    },
    // Pole rozliczeniowe jest zablokowane tylko wtedy, gdy admin nie ma
    // uprawnienia I pole ma już wartość. Pierwsze ustawienie (puste w bazie)
    // wolno każdemu — backend blokuje wyłącznie ZMIANĘ już ustawionej wartości,
    // a panel był ostrzejszy, przez co pracownik nie mógł dokończyć karty
    // nowego kierowcy (zgłoszenie „Karol Kalkowski – brak id bolt", 31.08.2026).
    poleRozliczenZablokowane(pole) {
      if (this.mozeEdytowacRozliczenia) return false;
      return !!this.polaRozliczenZBazy[pole];
    },
    hintRozliczen(pole) {
      if (this.poleRozliczenZablokowane(pole)) {
        return 'Zmianę robi head admin lub administrator z nadanym uprawnieniem';
      }
      if (!this.polaRozliczenZBazy[pole]) {
        return 'Ustawiasz po raz pierwszy — zapisze się od razu';
      }
      // Gotówka/przelew nie zmienia kwoty — wchodzi od razu, także dla
      // niewypłaconych rozliczeń (decyzja właściciela 20.10.2026).
      if (pole === 'payoutType') {
        return 'Zmiana wejdzie od razu — dotyczy też jeszcze niewypłaconych rozliczeń';
      }
      return 'Zmiana wejdzie od następnego tygodnia rozliczeniowego';
    },
    // Uprawnienia nadane przez head admina nie siedzą w JWT — trzeba je dociągnąć.
    async pobierzMojeUprawnienia() {
      try {
        const me = await axios({
          method: "get",
          url: process.env.VUE_APP_ROOT_API + "company-admins/me",
          headers: { Authorization: "Bearer " + localStorage.getItem("token") },
        });
        this.mojeUprawnienieRozliczen = !!me.data.data.canEditSettlementFields;
      } catch (e) {
        // Brak odpowiedzi = zostajemy przy samej roli, pola pozostaną wyszarzone.
      }
    },

    canAccess(sub) { return canAccessSubtab('driver-details', sub); },
    async changeAccountType(accountType) {
      const isOwner = accountType === 'SUBFLEET_OWNER';
      const res = await Swal.fire({
        icon: 'question',
        title: isOwner ? 'Przepiąć konto na właściciela aut?' : 'Przepiąć konto na kierowcę?',
        html: isOwner
          ? 'Konto dostanie zakładkę <b>„Moja podflota”</b> w aplikacji. Jeśli osoba nie ma jeszcze podfloty — utworzy się automatycznie.'
          : 'Konto wraca do trybu kierowcy. Podflota (jeśli istnieje) zostaje bez zmian — możesz ją odpiąć w Podflotach.',
        showCancelButton: true,
        confirmButtonText: 'Przepnij',
        cancelButtonText: 'Anuluj',
      });
      if (!res.isConfirmed) {
        this.$forceUpdate();
        return;
      }
      try {
        const r = await axios({
          method: 'patch',
          url: process.env.VUE_APP_ROOT_API + 'sub-fleet/account-type/' + this.currentDriverId,
          data: { accountType },
          headers: { Authorization: 'Bearer ' + localStorage.getItem('token') },
        });
        this.formData.accountType = accountType;
        const d = r.data?.data || {};
        Swal.fire({
          icon: 'success',
          title: 'Rodzaj konta zmieniony',
          text: isOwner && d.subFleetName ? `Podflota: ${d.subFleetName}` : undefined,
          timer: 2200,
          showConfirmButton: false,
        });
      } catch (e) {
        console.error(e);
        this.$forceUpdate();
        Swal.fire('Błąd', e.response?.data?.message || String(e), 'error');
      }
    },
    async loadDeposits() {
      try {
        const res = await axios({
          method: 'get',
          url: process.env.VUE_APP_ROOT_API + 'deposits/driver/' + this.currentDriverId,
          headers: { Authorization: 'Bearer ' + localStorage.getItem('token') },
        });
        this.deposits = res.data?.data || [];
      } catch (e) {
        console.error('Failed to load deposits:', e);
      }
    },
    async createDeposit() {
      // Kaucja dotyczy konkretnego auta — bez wskazania auta backend jej nie przyjmie.
      if (!this.depositCars.length) {
        Swal.fire(
          'Brak auta',
          'Kaucja wiąże się z autem, które kierowca wynajmuje. Przypisz auto kierowcy i spróbuj ponownie.',
          'warning',
        );
        return;
      }
      const opcjeAut = this.depositCars
        .map((c) => `<option value="${c.id}">${c.label}</option>`)
        .join('');
      const { value, isConfirmed } = await Swal.fire({
        title: 'Ustaw kaucję',
        html:
          '<select id="dep-car" class="swal2-select" style="width:80%">' + opcjeAut + '</select>' +
          '<input id="dep-amount" type="number" min="1" step="0.01" class="swal2-input" placeholder="Kwota kaucji (zł), np. 1000">' +
          '<select id="dep-mode" class="swal2-select" style="width:80%">' +
          '<option value="INSTALLMENTS">Zbierana ratami z wypłat</option>' +
          '<option value="UPFRONT">Wpłacona jednorazowo</option>' +
          '</select>' +
          '<input id="dep-inst" type="number" min="1" max="52" class="swal2-input" placeholder="Liczba rat tygodniowych, np. 4">' +
          '<select id="dep-method" class="swal2-select" style="width:80%;display:none">' +
          '<option value="CASH">Gotówka</option>' +
          '<option value="TRANSFER">Przelew</option>' +
          '</select>' +
          '<input id="dep-note" type="text" class="swal2-input" placeholder="Notatka (opcjonalnie)">',
        focusConfirm: false,
        showCancelButton: true,
        confirmButtonText: 'Ustaw',
        cancelButtonText: 'Anuluj',
        didOpen: () => {
          // Raty i sposób wpłaty wykluczają się — pokazujemy tylko to, co ma sens.
          const mode = document.getElementById('dep-mode');
          const inst = document.getElementById('dep-inst');
          const method = document.getElementById('dep-method');
          const przelacz = () => {
            const upfront = mode.value === 'UPFRONT';
            inst.style.display = upfront ? 'none' : '';
            method.style.display = upfront ? '' : 'none';
          };
          mode.addEventListener('change', przelacz);
          przelacz();
        },
        preConfirm: () => ({
          carId: document.getElementById('dep-car').value,
          amount: parseFloat(document.getElementById('dep-amount').value),
          upfront: document.getElementById('dep-mode').value === 'UPFRONT',
          installments: parseInt(document.getElementById('dep-inst').value, 10),
          method: document.getElementById('dep-method').value,
          note: document.getElementById('dep-note').value,
        }),
      });
      if (!isConfirmed) return;
      if (!value.amount || value.amount <= 0) {
        Swal.fire('Błąd', 'Podaj kwotę kaucji', 'error');
        return;
      }
      if (!value.upfront && (!value.installments || value.installments < 1)) {
        Swal.fire('Błąd', 'Podaj liczbę rat', 'error');
        return;
      }
      try {
        await axios({
          method: 'post',
          url: process.env.VUE_APP_ROOT_API + 'deposits',
          data: {
            driverId: this.currentDriverId,
            carId: value.carId,
            totalAmount: Math.round(value.amount * 100),
            installments: value.upfront ? 1 : value.installments,
            paidUpfront: value.upfront || undefined,
            paidUpfrontMethod: value.upfront ? value.method : undefined,
            note: value.note || undefined,
          },
          headers: { Authorization: 'Bearer ' + localStorage.getItem('token') },
        });
        Swal.fire({
          icon: 'success',
          title: value.upfront
            ? 'Kaucja zapisana jako wpłacona — raty nie będą schodzić z wypłat'
            : 'Kaucja ustawiona — pierwsza rata zejdzie z najbliższej wypłaty',
          timer: 2400,
          showConfirmButton: false,
        });
        this.loadDeposits();
      } catch (e) {
        console.error(e);
        Swal.fire('Błąd', e.response?.data?.message || String(e), 'error');
      }
    },
    // Kierowca wpłacił kaucję jednorazowo już po jej założeniu (np. dopłacił
    // resztę gotówką) — raty jeszcze niepobrane przestają schodzić z wypłat.
    async markDepositPaidDialog(deposit) {
      const zebrane = ((deposit.collectedFromInstallments || 0) / 100).toFixed(2);
      const doWplaty = Math.max(
        0,
        deposit.totalAmount - (deposit.collectedFromInstallments || 0),
      );
      const czekajace = (deposit.installmentBonuses || []).filter(
        (b) => b.type === 'NEGATIVE' && b.status === 'PENDING',
      ).length;
      const { value, isConfirmed } = await Swal.fire({
        title: 'Kaucja wpłacona jednorazowo',
        html:
          `<div style="text-align:left;margin-bottom:10px">` +
          `Kaucja: <b>${(deposit.totalAmount / 100).toFixed(2)} zł</b><br>` +
          `Zebrane ratami do tej pory: <b>${zebrane} zł</b><br>` +
          `Do zapisania jako wpłata: <b>${(doWplaty / 100).toFixed(2)} zł</b>` +
          (czekajace
            ? `<br><span style="color:#b26a00">Raty jeszcze niepobrane (${czekajace}) zostaną anulowane.</span>`
            : '') +
          `</div>` +
          '<select id="dep-paid-method" class="swal2-select" style="width:80%">' +
          '<option value="CASH">Gotówka</option>' +
          '<option value="TRANSFER">Przelew</option>' +
          '</select>' +
          '<input id="dep-paid-note" type="text" class="swal2-input" placeholder="Notatka (opcjonalnie)">',
        focusConfirm: false,
        showCancelButton: true,
        confirmButtonText: 'Zapisz wpłatę',
        cancelButtonText: 'Anuluj',
        preConfirm: () => ({
          method: document.getElementById('dep-paid-method').value,
          note: document.getElementById('dep-paid-note').value,
        }),
      });
      if (!isConfirmed) return;
      try {
        await axios({
          method: 'post',
          url: process.env.VUE_APP_ROOT_API + 'deposits/' + deposit.id + '/mark-paid',
          data: { method: value.method, note: value.note || undefined },
          headers: { Authorization: 'Bearer ' + localStorage.getItem('token') },
        });
        Swal.fire({
          icon: 'success',
          title: 'Zapisane — kaucja opłacona, raty nie będą schodzić z wypłat',
          timer: 2400,
          showConfirmButton: false,
        });
        this.loadDeposits();
      } catch (e) {
        console.error(e);
        Swal.fire('Błąd', e.response?.data?.message || String(e), 'error');
      }
    },
    async settleDepositDialog(deposit) {
      const collected = (deposit.collectedAmount / 100).toFixed(2);
      const { value, isConfirmed } = await Swal.fire({
        title: 'Rozliczenie kaucji',
        html:
          `<div style="margin-bottom:8px">Zebrana kaucja: <b>${collected} zł</b></div>` +
          '<input id="dep-damage" type="number" min="0" step="0.01" class="swal2-input" placeholder="Potrącenie za szkody (zł, 0 = pełny zwrot)">' +
          '<input id="dep-damage-note" type="text" class="swal2-input" placeholder="Opis szkód (wymagany przy potrąceniu)">',
        focusConfirm: false,
        showCancelButton: true,
        confirmButtonText: 'Rozlicz',
        cancelButtonText: 'Anuluj',
        confirmButtonColor: '#e65100',
        preConfirm: () => ({
          damage: parseFloat(document.getElementById('dep-damage').value || '0'),
          note: document.getElementById('dep-damage-note').value,
        }),
      });
      if (!isConfirmed) return;
      try {
        await axios({
          method: 'post',
          url: process.env.VUE_APP_ROOT_API + 'deposits/' + deposit.id + '/settle',
          data: {
            damageAmount: Math.round((value.damage || 0) * 100),
            damageNote: value.note || undefined,
          },
          headers: { Authorization: 'Bearer ' + localStorage.getItem('token') },
        });
        Swal.fire({ icon: 'success', title: 'Kaucja rozliczona — zwrot dopisze się do najbliższej wypłaty', timer: 2200, showConfirmButton: false });
        this.loadDeposits();
      } catch (e) {
        console.error(e);
        Swal.fire('Błąd', e.response?.data?.message || String(e), 'error');
      }
    },
    nonZero(value) {
      const n = parseFloat(value);
      return Number.isFinite(n) && n !== 0 ? value : '—';
    },
    invoiceStatusChipColor(item) {
      if (item.deleted) return 'error';
      if (item.status === 'PAID') return 'success';
      if (item.status === 'PAYOUT_CREATED') return 'info';
      if (item.accepted) return item.isCompanyInvoice ? 'orange' : 'primary';
      return 'warning';
    },
    invoiceStatusChipLabel(item) {
      if (item.deleted) return this.$t('invoices.statuses.rejected');
      if (item.status === 'PAID') return this.$t('invoices.statuses.paid');
      if (item.status === 'PAYOUT_CREATED') return this.$t('invoices.statuses.payoutCreated');
      if (item.accepted) {
        return item.isCompanyInvoice
          ? this.$t('invoices.statuses.company')
          : this.$t('invoices.statuses.accepted');
      }
      return this.$t('invoices.statuses.pending');
    },
    async openInvoicePreview(id) {
      this.invoicePreviewDialog = true;
      this.invoicePreviewLoading = true;
      this.invoicePreviewSrc = null;
      this.invoicePreviewType = null;
      this.invoicePreviewData = {};
      try {
        const res = await axios({
          method: "get",
          url: process.env.VUE_APP_ROOT_API + "invoices/details/" + id,
          headers: { Authorization: "Bearer " + localStorage.getItem("token") },
        });
        const data = res.data.data;
        const bruttoTotalGr = (data.brutto23 || 0) + (data.brutto8 || 0) + (data.brutto5 || 0) + (data.brutto0 || 0);
        this.invoicePreviewData = {
          id: data.id,
          name: data.name,
          date: dayjs(data.date).format("DD.MM.YYYY"),
          brutto23: data.brutto23 || 0,
          netto23: data.netto23 || 0,
          brutto8: data.brutto8 || 0,
          netto8: data.netto8 || 0,
          brutto5: data.brutto5 || 0,
          netto5: data.netto5 || 0,
          brutto0: data.brutto0 || 0,
          netto0: data.netto0 || 0,
          bruttoTotal: (bruttoTotalGr / 100).toFixed(2),
          driverReturn: data.driverReturn || 0,
          driverReturnPercent: data.driverReturnPercent || 0,
          accepted: data.accepted || false,
          deleted: data.deleted || false,
          isCompanyInvoice: data.isCompanyInvoice || false,
          rejectionReason: data.rejectionReason || null,
          status: data.status,
          file: data.file || [],
        };
        const fileObj = (data.file || [])[0];
        if (fileObj && fileObj.id) {
          const response = await fetch(`${process.env.VUE_APP_ROOT_API}aws/download-file/${fileObj.id}`, {
            method: 'POST',
            headers: { 'Authorization': 'Bearer ' + localStorage.getItem('token') }
          });
          if (response.ok) {
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const fileName = (fileObj.orginalName || fileObj.url || '').toLowerCase();
            this.invoicePreviewType = (blob.type === 'application/pdf' || fileName.endsWith('.pdf')) ? 'pdf' : 'image';
            this.invoicePreviewSrc = url;
          }
        }
      } catch (e) {
        console.error(e);
        Swal.fire("Błąd", "Nie udało się załadować faktury", "error");
      } finally {
        this.invoicePreviewLoading = false;
      }
    },
        validateRequired(value) {
      return !!value || "To pole jest wymagane";
    },
    validatePositiveNumber(value) {
      return value >= 0 || "Wartość musi być dodatnia";
    },
    goBack() {
      location.href = "/drivers";
    },
    validateUUID(value) {
      const uuidRegex =
        /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
      return !value || uuidRegex.test(value) || "Nieprawidłowy format UUID";
    },
    validateOptionalUUID(value) {
      return value === null || this.validateUUID(value.id || value);
    },
    formatFieldValue(_key, value) {
      // Format numeric values that look like money
      const numVal = parseFloat(value);
      if (!isNaN(numVal) && value !== '' && numVal !== 0) {
        return `${numVal.toFixed(2)} PLN`;
      }
      return value || '-';
    },
    getFieldColor(key, equation) {
      if (!equation) return 'grey';
      // Check if field is used in equation
      if (equation.bruttoField === key) return 'success';
      if (equation.cashField === key) return 'warning';
      if (equation.idField === key) return 'info';
      // Check if field is used in the equation string
      if (equation.equationStr && equation.equationStr.includes(key)) return 'primary';
      return 'grey';
    },
    isFieldUsedInEquation(key, equation) {
      if (!equation) return false;
      if (equation.bruttoField === key) return true;
      if (equation.cashField === key) return true;
      if (equation.idField === key) return true;
      if (equation.equationStr && equation.equationStr.includes(key)) return true;
      return false;
    },
    getEquationFields(rawData, equation) {
      if (!rawData || !equation) return {};
      return Object.keys(rawData)
        .filter(key => this.isFieldUsedInEquation(key, equation))
        .reduce((obj, key) => {
          obj[key] = rawData[key];
          return obj;
        }, {});
    },
    getOtherFields(rawData, equation) {
      if (!rawData) return {};
      if (!equation) return rawData;
      return Object.keys(rawData)
        .filter(key => !this.isFieldUsedInEquation(key, equation))
        .reduce((obj, key) => {
          obj[key] = rawData[key];
          return obj;
        }, {});
    },
    getCalculationSteps(equation, rawData) {
      if (!equation || !equation.equationStr || !rawData) return [];
      const steps = [];
      let currentExpr = equation.equationStr;

      // Step 1: Show original formula
      steps.push({
        label: 'Formuła',
        expression: equation.equationStr,
        result: null
      });

      // Step 2: Substitute variables from rawData (sort by length desc to avoid partial matches)
      const sortedKeys = Object.keys(rawData).sort((a, b) => b.length - a.length);
      sortedKeys.forEach(key => {
        if (currentExpr.includes(key)) {
          const value = parseFloat(rawData[key]) || 0;
          // Use word boundary-like replacement to avoid partial matches
          currentExpr = currentExpr.split(key).join(value.toString());
        }
      });

      // Substitute custom numbers (also sort by length desc)
      if (equation.customNumbers && Array.isArray(equation.customNumbers)) {
        const sortedCustom = [...equation.customNumbers].sort((a, b) => b.label.length - a.label.length);
        sortedCustom.forEach(cv => {
          if (currentExpr.includes(cv.label)) {
            currentExpr = currentExpr.split(cv.label).join(cv.value.toString());
          }
        });
      }

      steps.push({
        label: 'Podstawienie wartości',
        expression: currentExpr,
        result: null
      });

      // Step 3: Find all brackets and evaluate them
      let safeExpr = currentExpr;
      const bracketRegex = /\(([^()]+)\)/g;
      const allMatches = [...currentExpr.matchAll(bracketRegex)];

      // Evaluate each bracket and show individual results
      if (allMatches.length > 0) {
        const bracketResults = [];
        allMatches.forEach((match, idx) => {
          const innerExpr = match[1];
          try {
            const innerResult = Function('"use strict"; return (' + innerExpr + ')')();
            const roundedResult = Math.round(innerResult * 100) / 100;
            bracketResults.push({
              original: match[0],
              result: roundedResult
            });
            steps.push({
              label: `Nawias ${idx + 1}`,
              expression: `(${innerExpr}) = ${roundedResult}`,
              result: roundedResult
            });
          } catch (e) {
            console.log('Error evaluating bracket', e);
          }
        });

        // Replace all brackets with their results
        bracketResults.forEach(br => {
          safeExpr = safeExpr.replace(br.original, br.result.toString());
        });

        // Show the expression after all brackets are resolved
        if (bracketResults.length > 1) {
          steps.push({
            label: 'Po obliczeniu nawiasów',
            expression: safeExpr,
            result: null
          });
        }
      }

      // Step 4: Final calculation
      try {
        const finalResult = Function('"use strict"; return (' + safeExpr + ')')();
        const roundedFinal = Math.round(finalResult * 100) / 100;
        steps.push({
          label: 'Wynik końcowy',
          expression: `${safeExpr} = ${roundedFinal}`,
          result: roundedFinal,
          isFinal: true
        });
      } catch (e) {
        console.log('Error evaluating expression', e);
      }

      return steps;
    },
    async toggleLoading(type) {
      const toggle = type;
      localStorage.setItem("loading", toggle);
      window.dispatchEvent(
        new CustomEvent("toggleLoading", {
          detail: {
            loading: localStorage.getItem("loading"),
            overlay: localStorage.getItem("loading"),
          },
        })
      );
    },
        async downloadFile(fileId) {
        const file = await axios({
          method: "post",
          url: process.env.VUE_APP_ROOT_API + "aws/download-file/" + fileId,
          headers: {
            Authorization: "Bearer " + localStorage.getItem("token"),
          },
          responseType: "blob",
        });
        let blob = new Blob([file.data], { type: file.data.type }),
          url = window.URL.createObjectURL(blob);
        window.open(url);
      
    },
 async submitBonus() {
      // Process the bonus data here
      try {
        const formData = new FormData();
      try {
        formData.append("file", this.bonus.file, this.bonus.file.name);
      } catch (e) {
        console.log("Missing Image");
      }
        formData.append("date", this.bonus.date);
        formData.append("name", this.bonus.name);
        formData.append("type", this.bonus.type);
        formData.append("driverId", this.currentDriverId);
        formData.append("status", this.bonus.status);
        formData.append("price", this.bonus.price);
        formData.append("id", this.currentBonusId || undefined);
        await this.toggleLoading(true);
        await axios({
          method: "post",
          url: process.env.VUE_APP_ROOT_API + "bonuses",
          headers: {
            Authorization: "Bearer " + localStorage.getItem("token"),
            "Content-Type": "multipart/form-data",
          },
          data: formData,
        });
        this.closeDialog();
        await this.toggleLoading(false);
         await this.getBonuses()
      } catch (e) {
        await this.toggleLoading(false);
        console.log(e);
        Swal.fire("Błąd", e.response.data.message.toString(), "error");
      }
    },
    closeDialog() {
      this.dialogBonus = false;
      this.bonus.file = null;
      this.$refs.formBonus.resetValidation(); // Reset form validation
    },
      previewFile(file) {
      console.log(file);
      if (file && file instanceof File) {
        const reader = new FileReader();
        reader.onload = (e) => {
          console.log(e.target.result);
          this.bonus.preview = e.target.result;
        };
        reader.readAsDataURL(file);
      }
    },
  async deleteBonus(id) {
      Swal.fire({
        title: "Napewno usunąć?",
        text: "",
        icon: "question",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Tak",
        cancelButtonText: "Anuluj",
      }).then(async (result) => {
        if (result.isConfirmed) {
          try {
            await axios({
              method: "delete",
              url: process.env.VUE_APP_ROOT_API + "bonuses/" + id,
              headers: {
                Authorization: "Bearer " + localStorage.getItem("token"),
              },
            });
            await this.getBonuses()
          } catch (e) {
            console.log(e);
            Swal.fire("Błąd", e.response.data.message.toString(), "error");
          }
        }
      });
    },
    async getCarsSelect() {
      await this.toggleLoading(true);
      try {
        const cars = await axios({
          method: "get",
          url: process.env.VUE_APP_ROOT_API + `cars?take=10&skip=1`,
          headers: {
            Authorization: "Bearer " + localStorage.getItem("token"),
          },
        });
        this.cars = cars.data.data.result.map((it) => {
          return {
            id: it.id,
            name: `${it.make}-${it.model} ${it.plate}`,
          };
        });
      } catch (e) {
        console.log(e);
        Swal.fire("Błąd", e.response.data.message.toString(), "error");
      }
      await this.toggleLoading(false);
    },
    async initialize(id) {
      await this.getSubcompanySelect();
      await this.getCarsSelect();

      await this.toggleLoading(true);
      try {
        const dep = await axios({
          method: "get",
          url: process.env.VUE_APP_ROOT_API + "drivers/" + id,
          headers: {
            Authorization: "Bearer " + localStorage.getItem("token"),
          },
        });
        this.numericId = dep.data.data.numericId
        this.formData = dep.data.data;
        // Pola `type="date"` nie przyjmują pełnego ISO — tniemy do RRRR-MM-DD.
        ['drivingLicenseValidTo', 'medicalValidTo', 'psychologicalValidTo', 'criminalRecordDate', 'idCardValidTo']
          .forEach((k) => { this.formData[k] = this.formData[k] ? String(this.formData[k]).slice(0, 10) : ''; });
        this.polaRozliczenZBazy = {
          cooperationType: dep.data.data.cooperationType || null,
          payoutSchedule: dep.data.data.payoutSchedule || null,
          payoutType: dep.data.data.payoutType || null,
          zusType: dep.data.data.zusType || null,
        };
        this.formData.carId = dep.data.data.carId || dep.data.data.secondCarId
        this.formData.subCompanyIds = dep.data.data.subCompanies.map(
          (it) => it.subcompanyId
        );
        // Twarde dane do owner-only korekty (PESEL przychodzi już odszyfrowany)
        this.kodObywatelstwa =
          dep.data.data.citizenshipCode ||
          (/^\s*pol(ska|skie)?\s*$/i.test(dep.data.data.citizenship || '') ? 'PL' : null);
        this.hardData = {
          pesel: dep.data.data.pesel || '',
          dateOfBirth: dep.data.data.dateOfBirth ? String(dep.data.data.dateOfBirth).substring(0, 10) : '',
          citizenship: dep.data.data.citizenship || '',
          mainLanguage: dep.data.data.mainLanguage || '',
        };

        this.dialog = true;
        await this.toggleLoading(false);
      } catch (e) {
        await this.toggleLoading(false);
        console.log(e);
        Swal.fire("Błąd", e.response.data.message.toString(), "error");
      }
    },
    async getSubcompanySelect() {
      await this.toggleLoading(true);
      try {
        const subCompanies = await axios({
          method: "get",
          url: process.env.VUE_APP_ROOT_API + `sub-company`,
          headers: {
            Authorization: "Bearer " + localStorage.getItem("token"),
          },
        });
        this.subCompanies = subCompanies.data.data.map((it) => {
          return {
            id: it.id,
            name: it.name,
          };
        });
      } catch (e) {
        console.log(e);
        Swal.fire("Błąd", e.response.data.message.toString(), "error");
      }
      await this.toggleLoading(false);
    },
  //  async checkCar() {
  //     await this.toggleLoading(true);
  //     try {
  //       const car = await axios({
  //         method: "get",
  //         url: process.env.VUE_APP_ROOT_API + `cars/check`,
  //         headers: {
  //           Authorization: "Bearer " + localStorage.getItem("token"),
  //         },
  //         params: {
  //           driverId: this.currentDriverId,
  //           carId: this.formData.carId.id,
  //           triggerType: 'driver'
  //         },
  //       });
  //       if(car.data.data.msg){
  //         Swal.fire("Błąd", car.data.data.msg, "warning");
  //       }
  //     } catch (e) {
  //       console.log(e);
  //       Swal.fire("Błąd", e.response.data.message.toString(), "error");
  //     }
  //     await this.toggleLoading(false);
  //   },
    async getDepartmentsSelect() {
      await this.toggleLoading(true);
      try {
        const departaments = await axios({
          method: "get",
          url: process.env.VUE_APP_ROOT_API + `departments`,
          headers: {
            Authorization: "Bearer " + localStorage.getItem("token"),
          },
          params: {
            subCompanyIds: this.formData.subCompanyIds,
          },
        });
        this.departments = departaments.data.data.map((it) => {
          return {
            id: it.id,
            name: it.name,
          };
        });
      } catch (e) {
        console.log(e);
        Swal.fire("Błąd", e.response.data.message.toString(), "error");
      }
      await this.toggleLoading(false);
    },
    async saveHardData() {
      this.savingHardData = true;
      try {
        await axios({
          method: "patch",
          url: process.env.VUE_APP_ROOT_API + "drivers/" + this.currentDriverId + "/correct-hard-data",
          headers: { Authorization: "Bearer " + localStorage.getItem("token") },
          data: {
            pesel: this.hardData.pesel,
            dateOfBirth: this.hardData.dateOfBirth,
            citizenship: this.hardData.citizenship,
            mainLanguage: this.hardData.mainLanguage,
          },
        });
        Swal.fire({ title: "Zapisano korektę", text: "Zmiany zapisane i zalogowane w audycie.", icon: "success", timer: 1600, showConfirmButton: false });
        await this.initialize(this.currentDriverId);
      } catch (e) {
        Swal.fire("Błąd", e.response?.data?.message?.toString() || "Nie udało się zapisać korekty", "error");
      } finally {
        this.savingHardData = false;
      }
    },
    // Które wymagane pola są puste — po nazwach z etykiet, żeby biuro wiedziało,
    // czego szukać w długim formularzu.
    brakujaceWymaganePola() {
      const wymagane = ['firstName', 'lastName', 'phone', 'email', 'registerType'];
      // Pola rozliczeniowe ustawia wyłącznie główny administrator — nie ma sensu
      // wypisywać ich komuś, kto ma je wyszarzone.
      if (this.mozeEdytowacRozliczenia) {
        wymagane.push('payoutType', 'payoutSchedule', 'cooperationType', 'zusType');
      }
      return wymagane
        .filter((pole) => {
          const v = this.formData[pole];
          return v === null || v === undefined || String(v).trim() === '';
        })
        .map((pole) => this.$t(`driverDetails.labels.${pole}`));
    },

    async submitForm() {
      // Handle form submission logic
      console.log(this.formData)
      if(this.formData.cooperationType === 'RENT' && !this.formData.carId){
        Swal.fire("Uwaga", 'Musisz najpierw przypisać auto aby uzyc tego sposóbu rozliczenia!', "warning");
        return;
      }
      if (!this.$refs.form.validate()) {
        // Bez tego kliknięcie „Zapisz" przy pustym polu wymaganym nie robiło
        // NIC i nic nie mówiło — a formularz jest długi, więc czerwone pole
        // potrafi być poza ekranem. Wyglądało to jak zacięty panel: biuro nie
        // mogło uzupełnić adresu świeżo założonemu kierowcy.
        const brakujace = this.brakujaceWymaganePola();
        Swal.fire({
          title: this.$t('driverDetails.missingRequiredTitle'),
          html: brakujace.length
            ? `${this.$t('driverDetails.missingRequiredText')}<br><b>${brakujace.join(', ')}</b>`
            : this.$t('driverDetails.missingRequiredFallback'),
          icon: "warning",
        });
        return;
      }
      // Blokujący overlay na czas zapisu — bez niego wolny PATCH pozwalał
      // klikać dalej, a późniejszy location.reload() kasował tę pracę.
      await this.toggleLoading(true);
      try {
        const res = await axios({
          method: "patch",
          url:
            process.env.VUE_APP_ROOT_API + "drivers/" + this.currentDriverId,
          headers: {
            Authorization: "Bearer " + localStorage.getItem("token"),
          },
          data: {
            ...this.formData,
            boltId: this.formData.boltId?.replace(/\s/g, '') || null,
            uberId: this.formData.uberId?.replace(/\s/g, '') || null,
            freeNowId: this.formData.freeNowId?.replace(/\s/g, '') || null,
            // Bez spacji z brzegów — pole jest maskowane, a wklejone hasło
            // często ma spację na końcu; kierowca wpisuje je bez niej
            // i dostaje „niepoprawne hasło” (15.10.2026, Ł. Chylarecki).
            password:
              this.formData.password?.trim().length > 0
                ? this.formData.password.trim()
                : null,
            // Jawna intencja zmiany hasła — backend zignoruje password bez tej flagi.
            changePassword: this.formData.password?.trim().length > 0,
          },
        });
        await this.toggleLoading(false);
        // Jeśli zmiana typu współpracy została ODROCZONA (jest import na tydzień)
        // — pokaż wyraźny komunikat, że wejdzie od następnego importu.
        const scheduled = res.data?.data?.scheduledCooperationChange;
        if (scheduled) {
          await Swal.fire({
            title: "Zapisano — zmiana typu odroczona",
            html:
              "Pozostałe zmiany zapisano. <b>Zmiana typu współpracy</b> " +
              "(" + this.getCooperationTypeLabel(scheduled.from) + " → " +
              this.getCooperationTypeLabel(scheduled.to) + ") " +
              "<b>wejdzie od następnego tygodnia (następnego importu)</b> — " +
              "bo na bieżący tydzień jest już import. Ten tydzień rozliczy się starym typem.",
            icon: "info",
          });
        } else {
          await Swal.fire({
            title: "Zapisano",
            text: "Zmiany danych kierowcy zostały zapisane.",
            icon: "success",
            timer: 1600,
            showConfirmButton: false,
          });
        }
        location.reload();
      } catch (e) {
        await this.toggleLoading(false);
        console.log(e);
        Swal.fire("Błąd", e.response.data.message.toString(), "error");
      }
    },
    async getInvoices() {
      this.loadingInvoices = true;
      if (this.searchInvoices.trim().length > 0) {
        this.optionsInvoices.page = 1;
      }
      try {
        const invoices = await axios({
          method: "get",
          url:
            process.env.VUE_APP_ROOT_API +
            `invoices/driver/${this.currentDriverId}?take=${this.optionsInvoices.itemsPerPage}&skip=${this.optionsInvoices.page}&search=${this.searchInvoices}`,
          headers: {
            Authorization: "Bearer " + localStorage.getItem("token"),
          },
        });
        this.invoices = invoices.data.data.result.map((invoice) => {
          return {
            brutto23: ((invoice.brutto23 || 0) / 100).toFixed(2),
            netto23: ((invoice.netto23 || 0) / 100).toFixed(2),
            brutto8: ((invoice.brutto8 || 0) / 100).toFixed(2),
            netto8: ((invoice.netto8 || 0) / 100).toFixed(2),
            brutto5: ((invoice.brutto5 || 0) / 100).toFixed(2),
            netto5: ((invoice.netto5 || 0) / 100).toFixed(2),
            brutto0: ((invoice.brutto0 || 0) / 100).toFixed(2),
            netto0: ((invoice.netto0 || 0) / 100).toFixed(2),
            returnToDriver: ((invoice.driverReturn || 0) / 100).toFixed(2),
            status: invoice.status,
            accepted: invoice.accepted || false,
            deleted: invoice.deleted || false,
            isCompanyInvoice: invoice.isCompanyInvoice || false,
            driverReturn: invoice.driverReturn || 0,
            date: dayjs(invoice.date).format("DD.MM.YYYY"),
            id: invoice.id,
          };
        });
        this.totalItemsInvoices = invoices.data.data.count;
      } catch (e) {
        console.log(e);
        Swal.fire("Błąd", e.response.data.message.toString(), "error");
      }
      this.loadingInvoices = false;
    },
    async getBonuses(){
    this.loadingBonuses = true;
      if (this.searchBonuses.trim().length > 0) {
        this.optionsBonuses.page = 1;
      }
      try {
        const bonuses = await axios({
          method: "get",
          url:
            process.env.VUE_APP_ROOT_API +
            `bonuses/driver/${this.currentDriverId}?take=${this.optionsBonuses.itemsPerPage}&skip=${this.optionsBonuses.page}&search=${this.searchBonuses}`,
          headers: {
            Authorization: "Bearer " + localStorage.getItem("token"),
          },
        });
        this.bonuses = bonuses.data.data.result.map((bonus) => {
          return {
            price: bonus.price / 100,
            type: bonus.type,
            status: bonus.status,
            date: dayjs(bonus.date).format("DD.MM.YYYY"),
            description: bonus.name,
            id: bonus.id,
            fileId: bonus.file?.length > 0 ? bonus.file[0]?.id : null,
          };
        });
        this.totalItemsBonuses = bonuses.data.data.count;
      } catch (e) {
        console.log(e);
        Swal.fire("Błąd", e.response.data.message.toString(), "error");
      }
      this.loadingBonuses = false;
    },
async getRents(){
    this.loadingRent = true;
      if (this.searchRent.trim().length > 0) {
        this.optionsRent.page = 1;
      }
      try {
        const bonuses = await axios({
          method: "get",
          url:
            process.env.VUE_APP_ROOT_API +
            `bonuses/driver-rent/${this.currentDriverId}?take=${this.optionsRent.itemsPerPage}&skip=${this.optionsRent.page}&search=${this.searchBonuses}`,
          headers: {
            Authorization: "Bearer " + localStorage.getItem("token"),
          },
        });
        this.rents = bonuses.data.data.result.map((bonus) => {
          return {
            price: bonus.price / 100,
            status: bonus.status,
            date: dayjs(bonus.date).format("DD.MM.YYYY"),
            id: bonus.id,
          };
        });
        this.totalItemsRent = bonuses.data.data.count;
      } catch (e) {
        console.log(e);
        Swal.fire("Błąd", e.response.data.message.toString(), "error");
      }
      this.loadingRent = false;
    },
async getImported(){
    this.loadingImport = true;
      if (this.searchImport.trim().length > 0) {
        this.optionsImport.page = 1;
      }
      try {
        const bonuses = await axios({
          method: "get",
          url:
            process.env.VUE_APP_ROOT_API +
            `import/driver/${this.currentDriverId}?take=${this.optionsImport.itemsPerPage}&skip=${this.optionsImport.page}&search=${this.searchImport}`,
          headers: {
            Authorization: "Bearer " + localStorage.getItem("token"),
          },
        });
        this.imported = bonuses.data.data.result.map((bonus) => {
          let rawData = null;
          let equation = null;
          try {
            if (bonus.importRawData) {
              rawData = JSON.parse(bonus.importRawData);
            }
          } catch (e) {
            console.log('Error parsing importRawData', e);
          }
          try {
            if (bonus.import?.equation) {
              equation = {
                ...bonus.import.equation,
                customNumbers: bonus.import.equation.customNumbers
                  ? JSON.parse(bonus.import.equation.customNumbers)
                  : [],
              };
            }
          } catch (e) {
            console.log('Error parsing equation', e);
          }
          return {
            calculated: bonus.calculated / 100,
            cash: bonus.cash /100,
            type: bonus.type,
            status: bonus.status,
            settlementId: bonus.settlementId,
            cooperationType: bonus.cooperationType,
            deleted: bonus.deleted,
            date: dayjs(bonus.import.date).format("DD.MM.YYYY"),
            id: bonus.id,
            equationName: bonus.import.equation?.name || '',
            rawData: rawData,
            equation: equation,
          };
        });
        this.totalItemsImport = bonuses.data.data.count;
      } catch (e) {
        console.log(e);
        Swal.fire("Błąd", e.response.data.message.toString(), "error");
      }
      this.loadingImport = false;
    },
    async getPayouts() {
      this.loadingPayouts = true;
      if (this.searchPayouts.trim().length > 0) {
        this.optionsPayouts.page = 1;
      }
      try {
        const response = await axios({
          method: "get",
          url:
            process.env.VUE_APP_ROOT_API +
            `settlements/driver/${this.currentDriverId}?take=${this.optionsPayouts.itemsPerPage}&skip=${this.optionsPayouts.page}&search=${this.searchPayouts}`,
          headers: {
            Authorization: "Bearer " + localStorage.getItem("token"),
          },
        });
        this.payouts = response.data.data.result.map((payout) => {
          // Process import data
          const importData = (payout.importedData || []).map((imp) => {
            let rawData = null;
            let equation = null;
            try {
              if (imp.importRawData) {
                rawData = JSON.parse(imp.importRawData);
              }
            } catch (e) {
              console.log('Error parsing importRawData', e);
            }
            try {
              if (imp.import?.equation) {
                equation = {
                  ...imp.import.equation,
                  customNumbers: imp.import.equation.customNumbers
                    ? JSON.parse(imp.import.equation.customNumbers)
                    : [],
                };
              }
            } catch (e) {
              console.log('Error parsing equation', e);
            }

            // Extract brutto, netto and bonus from rawData based on equation fields
            let brutto = 0;
            let netto = 0;
            let bonus = 0;
            if (rawData && equation) {
              if (equation.bruttoField && rawData[equation.bruttoField]) {
                brutto = parseFloat(rawData[equation.bruttoField]) * 100 || 0;
              }
              if (equation.nettoField && rawData[equation.nettoField]) {
                netto = parseFloat(rawData[equation.nettoField]) * 100 || 0;
              }
              // "Bonus" dla kolumny 15% = realna baza z członu 0.15*(...) wzoru
              // (kampanie + napiwki). To 15% JEST potrącane (siedzi w "Obliczone")
              // — tu je tylko uwidaczniamy. Fallback na bonusField, gdyby wzór
              // nie miał członu 15%.
              bonus = bonus15Base(equation.equationStr, rawData);
              if (!bonus && equation.bonusField && rawData[equation.bonusField]) {
                bonus = parseFloat(String(rawData[equation.bonusField]).replace(',', '.')) * 100 || 0;
              }
            }

            return {
              id: imp.id,
              type: imp.type,
              calculated: imp.calculated || 0,
              cash: Math.abs(imp.cash || 0),
              brutto: brutto,
              netto: netto,
              bonus: bonus,
              onePercentFromBrutto: Math.round(brutto * 0.01),
              eightPercentFromNetto: Math.round(netto * 0.08),
              equationName: imp.import?.equation?.name || '-',
              cooperationType: imp.cooperationType,
              date: imp.import?.date ? dayjs(imp.import.date).format("DD.MM.YYYY") : '-',
              rawData: rawData,
              recordId: imp.recordId,
              equation: equation,
            };
          });
          const totalImportCalculated = importData.reduce((sum, imp) => sum + imp.calculated, 0);
          const totalImportCash = importData.reduce((sum, imp) => sum + imp.cash, 0);

          // Calculate totals per app type
          const appTypes = ['BOLT', 'UBER', 'FREENOW'];
          const perAppTotals = {};
          appTypes.forEach(appType => {
            const appData = importData.filter(imp => imp.type === appType);
            perAppTotals[appType] = {
              count: appData.length,
              brutto: appData.reduce((sum, imp) => sum + imp.brutto, 0),
              netto: appData.reduce((sum, imp) => sum + imp.netto, 0),
              calculated: appData.reduce((sum, imp) => sum + imp.calculated, 0),
              cash: appData.reduce((sum, imp) => sum + imp.cash, 0),
              onePercentFromBrutto: appData.reduce((sum, imp) => sum + imp.onePercentFromBrutto, 0),
              eightPercentFromNetto: appData.reduce((sum, imp) => sum + imp.eightPercentFromNetto, 0),
              bonus: appData.reduce((sum, imp) => sum + (imp.bonus || 0), 0),
            };
          });

          // Grand totals
          const totalBrutto = importData.reduce((sum, imp) => sum + imp.brutto, 0);
          const totalNetto = importData.reduce((sum, imp) => sum + imp.netto, 0);
          const totalOnePercent = importData.reduce((sum, imp) => sum + imp.onePercentFromBrutto, 0);
          const totalEightPercent = importData.reduce((sum, imp) => sum + imp.eightPercentFromNetto, 0);
          const totalBonus = importData.reduce((sum, imp) => sum + (imp.bonus || 0), 0);

          // Apps earnings = sum of calculated values from import data (BOLT + UBER + FREENOW)
          const appsEarnings = importData.reduce((sum, imp) => sum + imp.calculated, 0);

          // Max cost reimbursement = 8% of total NETTO (not brutto)
          const maxCostReimbursement = Math.round(totalNetto * 0.08);

          // Process invoices
          const invoices = (payout.invoices || []).map((inv) => ({
            id: inv.id,
            date: dayjs(inv.date).format("DD.MM.YYYY"),
            brutto: (inv.brutto23 || 0) + (inv.brutto8 || 0) + (inv.brutto5 || 0) + (inv.brutto0 || 0),
            netto: (inv.netto23 || 0) + (inv.netto8 || 0) + (inv.netto5 || 0) + (inv.netto0 || 0),
            driverReturn: inv.driverReturn || 0,
          }));
          const totalInvoicesBrutto = invoices.reduce((sum, inv) => sum + inv.brutto, 0);
          const totalInvoicesNetto = invoices.reduce((sum, inv) => sum + inv.netto, 0);
          const totalInvoicesReturn = invoices.reduce((sum, inv) => sum + inv.driverReturn, 0);

          const totalInvoicesVAT = totalInvoicesBrutto - totalInvoicesNetto;

          // Process bonuses
          const bonuses = (payout.bonuses || []).map((bonus) => ({
            id: bonus.id,
            name: bonus.name,
            type: bonus.type,
            price: bonus.price || 0,
          }));
          const totalBonusesPositive = bonuses.filter(b => b.type === 'POSITIVE').reduce((sum, b) => sum + b.price, 0);
          const totalBonusesNegative = bonuses.filter(b => b.type === 'NEGATIVE' || b.type === 'RENT').reduce((sum, b) => sum + b.price, 0);
          const totalBonusesNet = totalBonusesPositive - totalBonusesNegative;

          // Process incomes
          const incomes = (payout.incomes || []).map((inc) => ({
            id: inc.id,
            date: dayjs(inc.date).format("DD.MM.YYYY"),
            driverReturn: inc.driverReturn || 0,
          }));
          const totalIncomesReturn = incomes.reduce((sum, inc) => sum + inc.driverReturn, 0);

          // Process ZUS
          const zus = (payout.zus || []).map((z) => ({
            id: z.id,
            month: z.month,
            year: z.year,
            zusType: z.zusType,
            amount: z.amount || 0,
            chorobowe: z.chorobowe || 0,
            spoleczne: z.spoleczne || 0,
            funduszPracy: z.funduszPracy || 0,
            zdrowotne: z.zdrowotne || 0,
          }));

          const zusDeductionRaw = payout.zusDeduction || 0;
          const zleceniowkaAmountRaw = payout.zleceniowkaAmount || 0;
          const costInvoiceReimbursementRaw = payout.costInvoiceReimbursement || 0;
          const rentTransferAmountRaw = payout.rentTransferAmount || 0;
          const excludedDriverReturnRaw = payout.excludedDriverReturn || 0;
          const totalToPayoutRaw = payout.totalToPayout || 0;
          const settlementCostRaw = payout.settlementCost || 0;
          const administrationTaxRaw = payout.administrationTax || 0;
          const dailyAdvancesDeductedRaw = payout.dailyAdvancesDeducted || 0;
          const dailyPayoutFeesCollectedRaw = payout.dailyPayoutFeesCollected || 0;
          const dailyAdvancesCount = (payout.advancePayouts && payout.advancePayouts.length) || 0;

          // afterZus = "po odjęciu ZUS". Po migracji 20260507130000 totalToPayout
          // jest stored jako NETTO (z flagą totalToPayoutIsNet=true) — wtedy
          // jest już po ZUS i nie odejmujemy go drugi raz. Dla starych rekordów
          // (flag=false) totalToPayout jest brutto, odejmujemy zus.
          const afterZus = payout.totalToPayoutIsNet
            ? totalToPayoutRaw
            : (totalToPayoutRaw - zusDeductionRaw);
          const remainingAfterZleceniowka = afterZus - zleceniowkaAmountRaw;

          // Bonusy: pozytywne, ujemne i najem — OSOBNO (najem to typ RENT).
          const totalBonusesPositiveRaw = bonuses.filter(b => b.type === 'POSITIVE').reduce((sum, b) => sum + b.price, 0);
          const totalBonusesNegativeRaw = bonuses.filter(b => b.type === 'NEGATIVE').reduce((sum, b) => sum + b.price, 0);
          const totalRentRaw = bonuses.filter(b => b.type === 'RENT').reduce((sum, b) => sum + b.price, 0);

          // totalInvoicesReturn capped at maxCostReimbursement (for FIFTY_FIFTY display)
          const totalInvoicesReturnCapped = maxCostReimbursement > 0
            ? Math.min(totalInvoicesReturn, maxCostReimbursement)
            : totalInvoicesReturn;

          // Get ZUS type name from first ZUS record
          const zusTypeName = zus.length > 0 ? this.formatZusType(zus[0].zusType) : '-';


          return {
            id: payout.id,
            // SUROWA data ISO — formatowanie robi szablon (formatDateShort).
            // Pre-formatowany string "12.07.2026" trafiał do new Date(), który
            // parsował go po amerykańsku (miesiąc.dzień) → przestawiona data
            // i numer tygodnia liczony ze złego miesiąca.
            date: payout.date,
            totalToPayout: ((totalToPayoutRaw) / 100).toFixed(2) + ' PLN',
            zusDeduction: ((zusDeductionRaw) / 100).toFixed(2) + ' PLN',
            zusDeductionRaw,
            zleceniowkaAmount: ((zleceniowkaAmountRaw) / 100).toFixed(2) + ' PLN',
            zleceniowkaAmountRaw,
            costInvoiceReimbursement: ((costInvoiceReimbursementRaw) / 100).toFixed(2) + ' PLN',
            costInvoiceReimbursementRaw,
            rentTransferAmount: ((rentTransferAmountRaw) / 100).toFixed(2) + ' PLN',
            rentTransferAmountRaw,
            excludedDriverReturnRaw,
            settlementCostRaw,
            administrationTaxRaw,
            dailyAdvancesDeductedRaw,
            dailyPayoutFeesCollectedRaw,
            dailyAdvancesCount,
            netPayout: ((payout.netPayout || 0) / 100).toFixed(2) + ' PLN',
            cooperationType: this.getCooperationTypeLabel(payout.cooperationType),
            cooperationTypeRaw: payout.cooperationType,
            zusTypeName,
            isSettled: payout.isSettled,
            isPaidCash: payout.isPaidCash,
            isPublished: payout.isPublished,
            // Przychody
            importData,
            totalImportCalculated,
            totalImportCash,
            perAppTotals,
            totalBrutto,
            totalNetto,
            totalOnePercent,
            totalEightPercent,
            totalBonus,
            appsEarnings,
            // Kalkulacje
            afterZus,
            remainingAfterZleceniowka,
            maxCostReimbursement,
            totalBonusesPositiveRaw,
            totalBonusesNegativeRaw,
            totalRentRaw,
            // Faktury
            invoices,
            totalInvoicesBrutto,
            totalInvoicesNetto,
            totalInvoicesReturn,
            totalInvoicesReturnCapped,
            totalInvoicesVAT,
            // Inne
            bonuses,
            totalBonusesNet,
            incomes,
            totalIncomesReturn,
            zus,
            totalZus: zus.reduce((sum, z) => sum + z.amount, 0),
            // Napiwki / tips (3a) - TODO: wymaga osobnego pola tips w imporcie danych
            // Na razie = 0, bo system nie rozdziela napiwków od gotówki z przejazdów
            tipsGross: 0,
            tipsVat: 0,
            // Faktura settlement
            isInvoiceUploaded: payout.isInvoiceUploaded,
            invoiceFileId: payout.invoiceFileId,
          };
        });
        this.totalItemsPayouts = response.data.data.count;
      } catch (e) {
        console.log(e);
        Swal.fire("Błąd", e.response.data.message.toString(), "error");
      }
      this.loadingPayouts = false;
    },
    getPayoutStatusColor(payout) {
      if (payout.isSettled) return 'success';
      if (payout.isPublished) return 'blue';
      return 'orange';
    },
    getPayoutStatusText(payout) {
      if (payout.isSettled && payout.isPaidCash) return 'Zapłacony (Gotówka)';
      if (payout.isSettled) return 'Zapłacony';
      if (payout.isPublished) return 'Opublikowany';
      return 'Oczekujący';
    },
    formatCurrency(value) {
      return ((value || 0) / 100).toFixed(2) + ' PLN';
    },
    getAppColor(type) {
      const colors = {
        'BOLT': 'green',
        'UBER': 'black',
        'FREENOW': 'red',
      };
      return colors[type] || 'grey';
    },
    formatZusType(type) {
      const labels = {
        'B2B': 'B2B',
        'NOT_HIRED_L4': 'Niezatrudniony z L4',
        'NOT_HIRED_NO_L4': 'Niezatrudniony bez L4',
        'HIRED_BELOW_WITH_L4': 'Zatrudniony poniżej z L4',
        'HIRED_BELOW_NO_L4': 'Zatrudniony poniżej bez L4',
        'HIRED_ABOVE': 'Zatrudniony powyżej',
        'STUDENT': 'Student',
        'CONNECTION': 'Podpięcie',
        'UNDER_26_NO_L4': 'Poniżej 26 lat bez L4',
        'UNDER_26_L4': 'Poniżej 26 lat z L4',
      };
      return labels[type] || type || '-';
    },

    // Onboarding Documents Methods (4g)
    async fetchOnboardingDocuments() {
      this.loadingOnboardingDocuments = true;
      try {
        const response = await axios({
          method: "get",
          url: process.env.VUE_APP_ROOT_API + "driver-registration/admin/documents/" + this.currentDriverId,
          // Uses admin-protected endpoint for onboarding documents
          headers: {
            Authorization: "Bearer " + localStorage.getItem("token"),
          },
        });
        this.onboardingDocuments = response.data?.data || response.data || [];
      } catch (e) {
        console.log(e);
        this.onboardingDocuments = [];
      } finally {
        this.loadingOnboardingDocuments = false;
      }
    },

    daysUntilExpiry(date) {
      if (!date) return null;
      const ms = new Date(date).getTime() - new Date().setHours(0, 0, 0, 0);
      return Math.ceil(ms / (24 * 60 * 60 * 1000));
    },
    expiryChipColor(date) {
      const d = this.daysUntilExpiry(date);
      if (d === null) return 'grey';
      if (d < 0) return 'error';
      if (d <= 7) return 'error';
      if (d <= 30) return 'warning';
      return 'success';
    },
    expiryChipIcon(date) {
      const d = this.daysUntilExpiry(date);
      if (d === null) return 'mdi-calendar-blank-outline';
      if (d < 0) return 'mdi-calendar-alert';
      if (d <= 7) return 'mdi-calendar-alert';
      if (d <= 30) return 'mdi-calendar-clock';
      return 'mdi-calendar-check';
    },
    expiryLabel(date) {
      const d = this.daysUntilExpiry(date);
      if (d === null) return '';
      if (d < 0) return ` · przeterminowany (${Math.abs(d)} dni temu)`;
      if (d === 0) return ' · wygasa dziś';
      if (d === 1) return ' · wygasa jutro';
      if (d <= 30) return ` · za ${d} dni`;
      return '';
    },
    canAcceptDocument(rec) {
      if (rec.source === 'template') return !rec.raw.accepted && !rec.raw.deleted;
      return rec.status !== 'VERIFIED';
    },
    canRejectDocument(rec) {
      if (rec.source === 'template') return !rec.raw.accepted && !rec.raw.deleted;
      return rec.status !== 'REJECTED';
    },
    async rejectAnyDocument(rec) {
      const res = await Swal.fire({
        title: 'Odrzucić dokument?',
        html: `<b>${rec.docName}</b><br>Podaj powód odrzucenia — kierowca zobaczy ten komentarz.`,
        icon: 'warning',
        input: 'textarea',
        inputPlaceholder: 'np. Zdjęcie jest nieczytelne, prześlij wyraźniejsze...',
        inputAttributes: { 'aria-label': 'Powód odrzucenia' },
        inputValidator: (v) => (!v || v.trim().length < 3 ? 'Podaj powód (min. 3 znaki)' : null),
        showCancelButton: true,
        confirmButtonColor: '#d33',
        confirmButtonText: 'Odrzuć',
        cancelButtonText: 'Anuluj',
      });
      if (!res.isConfirmed) return;
      const reason = (res.value || '').trim();

      this.processingDocuments.push(rec.id);
      try {
        if (rec.source === 'template') {
          await axios({
            method: 'patch',
            url: process.env.VUE_APP_ROOT_API + 'documents/reject',
            headers: { Authorization: 'Bearer ' + localStorage.getItem('token') },
            data: { documentId: rec.rawId, reason },
          });
          await this.fetchDriverDocuments();
        } else {
          await axios({
            method: 'post',
            url: process.env.VUE_APP_ROOT_API + 'driver-registration/admin/document/reject',
            headers: { Authorization: 'Bearer ' + localStorage.getItem('token') },
            data: { documentId: rec.rawId, reason },
          });
          await this.fetchOnboardingDocuments();
        }
        Swal.fire({ title: 'Odrzucono', text: 'Kierowca otrzyma powiadomienie', icon: 'success', timer: 1800 });
      } catch (e) {
        const msg = e?.response?.data?.message?.toString?.() || e?.message || 'Błąd odrzucania dokumentu';
        Swal.fire('Błąd', msg, 'error');
      }
      this.processingDocuments = this.processingDocuments.filter((id) => id !== rec.id);
    },
    async previewAnyDocument(rec) {
      this.previewingDocuments.push(rec.id);
      try {
        if (rec.source === 'template') {
          const res = await axios({
            method: 'get',
            url: process.env.VUE_APP_ROOT_API + 'documents/preview/' + rec.rawId,
            headers: { Authorization: 'Bearer ' + localStorage.getItem('token') },
          });
          this.documentPreviewHtml = res.data?.data || res.data || '';
          this.documentPreviewDialog = true;
        } else {
          await this.previewOnboardingDocument(rec.raw);
        }
      } catch (e) {
        const msg = e?.response?.data?.message?.toString?.() || e?.message || 'Nie udało się wygenerować podglądu';
        Swal.fire('Błąd', msg, 'error');
      }
      this.previewingDocuments = this.previewingDocuments.filter((id) => id !== rec.id);
    },
    async downloadAnyDocument(rec) {
      this.downloadingDocuments.push(rec.id);
      try {
        if (rec.source === 'template') {
          if (!rec.raw.files || rec.raw.files.length === 0) {
            Swal.fire('Brak pliku', 'Dokument nie został jeszcze podpisany przez kierowcę', 'info');
            return;
          }
          await this.downloadTemplate(rec.raw.files[0].id);
        } else {
          await this.downloadOnboardingDocument(rec.raw);
        }
      } catch (e) {
        const msg = e?.response?.data?.message?.toString?.() || e?.message || 'Błąd pobierania';
        Swal.fire('Błąd', msg, 'error');
      }
      this.downloadingDocuments = this.downloadingDocuments.filter((id) => id !== rec.id);
    },
    async acceptAnyDocument(rec) {
      this.processingDocuments.push(rec.id);
      try {
        if (rec.source === 'template') {
          await axios({
            method: 'post',
            url: process.env.VUE_APP_ROOT_API + 'documents/accept',
            headers: { Authorization: 'Bearer ' + localStorage.getItem('token') },
            data: { documentId: rec.rawId },
          });
          await this.fetchDriverDocuments();
        } else {
          await axios({
            method: 'post',
            url: process.env.VUE_APP_ROOT_API + 'driver-registration/admin/document/verify',
            headers: { Authorization: 'Bearer ' + localStorage.getItem('token') },
            data: { documentId: rec.rawId },
          });
          await this.fetchOnboardingDocuments();
        }
        Swal.fire({ title: 'Sukces!', text: 'Dokument zaakceptowany.', icon: 'success', timer: 1500 });
      } catch (e) {
        const msg = e?.response?.data?.message?.toString?.() || e?.message || 'Błąd akceptacji';
        Swal.fire('Błąd', msg, 'error');
      }
      this.processingDocuments = this.processingDocuments.filter((id) => id !== rec.id);
    },
    openUploadDocDialog() {
      this.uploadDocForm = {
        documentType: null,
        documentNumber: '',
        expiryDate: '',
        file: null,
      };
      this.uploadDocError = '';
      this.uploadDocDialog = true;
    },
    openUploadDocDialogForType(documentType) {
      this.uploadDocForm = {
        documentType,
        documentNumber: '',
        expiryDate: '',
        file: null,
      };
      this.uploadDocError = '';
      this.uploadDocDialog = true;
    },
    async submitUploadDocument() {
      if (!this.uploadDocForm.documentType || !this.uploadDocForm.file || (this.uploadMaWaznosc && !this.uploadDocForm.expiryDate)) {
        this.uploadDocError = 'Typ dokumentu, plik i data ważności są wymagane';
        return;
      }
      this.uploadingDoc = true;
      this.uploadDocError = '';
      try {
        const fd = new FormData();
        fd.append('file', this.uploadDocForm.file);
        fd.append('driverId', this.currentDriverId);
        fd.append('documentType', this.uploadDocForm.documentType);
        if (this.uploadDocForm.documentNumber) {
          fd.append('documentNumber', this.uploadDocForm.documentNumber);
        }
        if (this.uploadMaWaznosc && this.uploadDocForm.expiryDate) {
          fd.append('expiryDate', this.uploadDocForm.expiryDate);
        }
        await axios({
          method: 'post',
          url: process.env.VUE_APP_ROOT_API + 'driver-registration/upload-document',
          data: fd,
          headers: {
            Authorization: 'Bearer ' + localStorage.getItem('token'),
            'Content-Type': 'multipart/form-data',
          },
        });
        this.uploadDocDialog = false;
        await this.fetchOnboardingDocuments();
        Swal.fire('Dodano', 'Dokument został przesłany', 'success');
      } catch (e) {
        this.uploadDocError = e?.response?.data?.message?.toString?.() || e?.message || 'Błąd uploadu';
      }
      this.uploadingDoc = false;
    },
    onboardingMimeFor(name) {
      const ext = (name || "").split(".").pop().toLowerCase();
      return ({
        pdf: "application/pdf",
        jpg: "image/jpeg",
        jpeg: "image/jpeg",
        png: "image/png",
        webp: "image/webp",
      })[ext] || "application/octet-stream";
    },
    base64ToBlob(b64, mime) {
      const bin = atob(b64);
      const bytes = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
      return new Blob([bytes], { type: mime });
    },
    async fetchOnboardingFile(doc) {
      const response = await axios({
        method: "get",
        url: process.env.VUE_APP_ROOT_API + "driver-registration/admin/document/file/" + doc.id,
        headers: { Authorization: "Bearer " + localStorage.getItem("token") },
      });
      const payload = response.data?.data || response.data || {};
      if (!payload.fileData) throw new Error("Brak danych pliku");
      const mime = this.onboardingMimeFor(payload.originalName || payload.fileUrl);
      const blob = this.base64ToBlob(payload.fileData, mime);
      return { blob, mime, name: payload.originalName || "dokument" };
    },
    async downloadOnboardingDocument(doc) {
      this.downloadingOnboardingDocs.push(doc.id);
      try {
        const { blob, name } = await this.fetchOnboardingFile(doc);
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = name;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        setTimeout(() => window.URL.revokeObjectURL(url), 1000);
      } catch (e) {
        console.log(e);
        Swal.fire("Błąd", e?.message || "Nie udało się pobrać dokumentu", "error");
      } finally {
        this.downloadingOnboardingDocs = this.downloadingOnboardingDocs.filter(id => id !== doc.id);
      }
    },

    async previewOnboardingDocument(doc) {
      try {
        const { blob, mime } = await this.fetchOnboardingFile(doc);
        const url = window.URL.createObjectURL(blob);
        this.onboardingDocPreviewUrl = url;
        this.onboardingDocPreviewMime = mime;
        this.onboardingDocPreviewDialog = true;
      } catch (e) {
        Swal.fire("Błąd", e?.message || "Nie udało się otworzyć podglądu", "error");
      }
    },

    // Driver Documents Methods
    async fetchDriverDocuments() {
      this.loadingDriverDocuments = true;
      try {
        const response = await axios({
          method: "get",
          url: process.env.VUE_APP_ROOT_API + "documents/driver-documents/" + this.currentDriverId,
          headers: {
            Authorization: "Bearer " + localStorage.getItem("token"),
          },
        });
        this.driverDocuments = response.data?.data?.result || response.data || [];
      } catch (e) {
        console.log(e);
        this.driverDocuments = [];
        Swal.fire("Błąd", "Nie udało się pobrać dokumentów kierowcy", "error");
      } finally {
        this.loadingDriverDocuments = false;
      }
    },

    async previewDriverDocument(document) {
      this.previewingDocuments.push(document.id);
      try {
        const res = await axios({
          method: 'get',
          url: process.env.VUE_APP_ROOT_API + 'documents/preview/' + document.id,
          headers: { Authorization: 'Bearer ' + localStorage.getItem('token') },
        });
        this.documentPreviewHtml = res.data.data;
        this.documentPreviewDialog = true;
      } catch (e) {
        console.error(e);
        Swal.fire('Błąd', 'Nie udało się wygenerować podglądu', 'error');
      }
      this.previewingDocuments = this.previewingDocuments.filter(id => id !== document.id);
    },

    async downloadDriverDocument(document) {
      if (!document.files || document.files.length === 0) {
        Swal.fire('Błąd', 'Dokument nie ma przypisanego pliku', 'warning');
        return;
      }
      this.downloadingDocuments.push(document.id);
      try {
        await this.downloadTemplate(document.files[0].id);
      } catch (error) {
        console.error('Error downloading driver document:', error);
        Swal.fire({
          title: 'Błąd!',
          text: 'Wystąpił błąd podczas pobierania dokumentu.',
          icon: 'error'
        });
      } finally {
        this.downloadingDocuments = this.downloadingDocuments.filter(id => id !== document.id);
      }
    },

    async downloadTemplate(id) {
      const file = await axios({
        method: "post",
        url: process.env.VUE_APP_ROOT_API + "aws/download-file/" + id,
        headers: {
          Authorization: "Bearer " + localStorage.getItem("token"),
        },
        responseType: "blob",
      });
      console.log(file);
      let blob = new Blob([file.data], { type: file.data.type }),
        url = window.URL.createObjectURL(blob);
      window.open(url);
    },

    async acceptDriverDocument(document) {
      this.processingDocuments.push(document.id);
      try {
        await axios({
          method: "post",
          url: process.env.VUE_APP_ROOT_API + "documents/accept",
          headers: {
            Authorization: "Bearer " + localStorage.getItem("token"),
          },
          data: { documentId: document.id },
        });

        Swal.fire({
          title: 'Sukces!',
          text: 'Dokument został zaakceptowany.',
          icon: 'success',
          timer: 2000
        });

        // Refresh driver documents
        await this.fetchDriverDocuments();
      } catch (error) {
        console.error('Error accepting document:', error);
        Swal.fire({
          title: 'Błąd!',
          text: 'Wystąpił błąd podczas akceptacji dokumentu.',
          icon: 'error'
        });
      } finally {
        this.processingDocuments = this.processingDocuments.filter(id => id !== document.id);
      }
    },

    async deleteDriverDocument(document) {
      const result = await Swal.fire({
        title: 'Czy na pewno?',
        text: 'Czy chcesz usunąć ten dokument? Ta operacja nie może być cofnięta.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6',
        confirmButtonText: 'Tak, usuń!',
        cancelButtonText: 'Anuluj'
      });

      if (result.isConfirmed) {
        this.processingDocuments.push(document.id);
        try {
          await axios({
            method: "delete",
            url: process.env.VUE_APP_ROOT_API + "documents/delete/" + document.id,
            headers: {
              Authorization: "Bearer " + localStorage.getItem("token"),
            },
          });

          Swal.fire({
            title: 'Usunięto!',
            text: 'Dokument został usunięty.',
            icon: 'success',
            timer: 2000
          });

          // Refresh driver documents
          await this.fetchDriverDocuments();
        } catch (error) {
          console.error('Error deleting document:', error);
          Swal.fire({
            title: 'Błąd!',
            text: 'Wystąpił błąd podczas usuwania dokumentu.',
            icon: 'error'
          });
        } finally {
          this.processingDocuments = this.processingDocuments.filter(id => id !== document.id);
        }
      }
    },

    getDriverDocumentStatusColor(document) {
      if (document.deleted) return 'red';
      if (document.accepted) return 'green';
      if (document.submitted) return 'blue';
      return 'orange';
    },

    getDriverDocumentStatusText(document) {
      if (document.deleted) return 'Usunięty';
      if (document.accepted) return 'Zaakceptowany';
      if (document.submitted) return 'Wysłany';
      return 'Oczekujący';
    },

    formatDate(dateString) {
      return new Date(dateString).toLocaleDateString('pl-PL', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    },

    formatDateShort(dateString) {
      return new Date(dateString).toLocaleDateString('pl-PL', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      });
    },

    // Numer tygodnia ISO-8601 (tydzień zaczyna się w poniedziałek, tydzień 1 to
    // ten, w którym jest pierwszy czwartek roku). Bez zależności od dayjs.
    weekNumber(dateString) {
      if (!dateString) return null;
      const d = new Date(dateString);
      if (isNaN(d.getTime())) return null;
      const date = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
      const dayNum = (date.getUTCDay() + 6) % 7; // pon=0 ... niedz=6
      date.setUTCDate(date.getUTCDate() - dayNum + 3); // czwartek tego tygodnia
      const firstThursday = new Date(Date.UTC(date.getUTCFullYear(), 0, 4));
      const firstDayNum = (firstThursday.getUTCDay() + 6) % 7;
      firstThursday.setUTCDate(firstThursday.getUTCDate() - firstDayNum + 3);
      return 1 + Math.round((date - firstThursday) / (7 * 24 * 60 * 60 * 1000));
    },

    // Zakres pon.–niedz. tygodnia ISO, do którego należy data — np. "06.07–12.07".
    // Rozliczenia mają datę = niedziela zamykająca tydzień, co bez zakresu myliło
    // (data 12.07 + „Tydz. 28" wyglądały na rozjechane, a to ten sam tydzień).
    weekRange(dateString) {
      if (!dateString) return '';
      const d = new Date(dateString);
      if (isNaN(d.getTime())) return '';
      const day = (d.getDay() + 6) % 7; // pon=0 ... niedz=6
      const monday = new Date(d);
      monday.setDate(d.getDate() - day);
      const sunday = new Date(monday);
      sunday.setDate(monday.getDate() + 6);
      const f = (x) => String(x.getDate()).padStart(2, '0') + '.' + String(x.getMonth() + 1).padStart(2, '0');
      return `${f(monday)}–${f(sunday)}`;
    },

    getZusTypeLabel(type) {
      const found = this.zusTypes.find((z) => z.value === type);
      return found ? found.text : type || '—';
    },
    getCooperationTypeLabel(type) {
      const typeMap = {
        'B2B_VAT': 'B2B z VAT',
        'B2B_NO_VAT': 'B2B bez VAT',
        'FIFTY_FIFTY': '50/50',
        'RENT': 'Wynajem',
        'CONNECTION': 'Połączenie',
        'STUDENT': 'Student'
      };
      return typeMap[type] || type;
    },

    getPayoutTypeLabel(type) {
      const found = this.payoutTypes.find((t) => t.value === type);
      return found ? found.text : type;
    },
    getPayoutScheduleLabel(schedule) {
      const found = this.payoutSchedules.find((s) => s.value === schedule);
      return found ? found.text : schedule;
    },

    async getCooperationTypeHistory() {
      try {
        const response = await axios({
          method: "get",
          url: process.env.VUE_APP_ROOT_API + "drivers/" + this.currentDriverId + "/cooperation-type-history",
          headers: {
            Authorization: "Bearer " + localStorage.getItem("token"),
          },
        });
        this.cooperationTypeHistory = response.data?.data || [];
      } catch (e) {
        console.log('Error fetching cooperation type history', e);
        this.cooperationTypeHistory = [];
      }
    },

    async getRentHistory() {
      try {
        const response = await axios({
          method: "get",
          url: process.env.VUE_APP_ROOT_API + "drivers/" + this.currentDriverId + "/rent-history",
          headers: {
            Authorization: "Bearer " + localStorage.getItem("token"),
          },
        });
        this.rentHistory = response.data?.data || [];
      } catch (e) {
        console.log('Error fetching rent history', e);
        this.rentHistory = [];
      }
    },

    async getSettingsHistory() {
      try {
        const response = await axios({
          method: "get",
          url: process.env.VUE_APP_ROOT_API + "drivers/" + this.currentDriverId + "/settings-history",
          headers: {
            Authorization: "Bearer " + localStorage.getItem("token"),
          },
        });
        this.settingsHistory = response.data?.data || [];
      } catch (e) {
        console.log('Error fetching settings history', e);
        this.settingsHistory = [];
      }
    },

    getSettingFieldLabel(field) {
      const map = {
        cooperationType: 'Typ współpracy',
        payoutSchedule: 'Harmonogram wypłat',
        zusType: 'Typ ZUS / zatrudnienia',
      };
      return map[field] || field;
    },

    getSettingValueLabel(field, value) {
      if (value === null || value === undefined || value === '') return '—';
      if (field === 'cooperationType') return this.getCooperationTypeLabel(value);
      if (field === 'payoutSchedule') {
        return {
          DAILY: 'Dzienny',
          WEEKLY: 'Tygodniowy',
          EVERY_2_WEEKS: 'Dwutygodniowy',
          MONTH: 'Miesięczny',
        }[value] || value;
      }
      if (field === 'zusType') {
        return {
          B2B: 'B2B',
          NOT_HIRED_L4: 'Niezatrudniony (L4)',
          NOT_HIRED_NO_L4: 'Niezatrudniony (bez L4)',
          HIRED_BELOW_WITH_L4: 'Zatrudniony — poniżej (z L4)',
          HIRED_BELOW_NO_L4: 'Zatrudniony — poniżej (bez L4)',
          HIRED_ABOVE: 'Zatrudniony — powyżej',
          STUDENT: 'Student',
          CONNECTION: 'Połączenie',
        }[value] || value;
      }
      return value;
    },

    async deleteDriverPrivateCar(car) {
      const r = await Swal.fire({
        title: "Usunąć auto kierowcy?",
        html: `<b>${car.brand || car.make || ''} ${car.model || ''}</b> (${car.plate || ''})`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        confirmButtonText: "Usuń",
        cancelButtonText: "Anuluj",
      });
      if (!r.isConfirmed) return;
      try {
        await axios({
          method: "delete",
          url: process.env.VUE_APP_ROOT_API + "cars/" + car.id,
          headers: { Authorization: "Bearer " + localStorage.getItem("token") },
        });
        Swal.fire("Usunięto", "Auto zostało usunięte", "success");
        await this.fetchDriverPrivateCars();
      } catch (e) {
        Swal.fire("Błąd", e.response?.data?.message?.toString() || "Wystąpił błąd", "error");
      }
    },
    // Driver private cars
    async fetchDriverPrivateCars() {
      if (!this.currentDriverId) return;
      this.driverPrivateCarsLoading = true;
      try {
        const response = await axios({
          method: "get",
          url: process.env.VUE_APP_ROOT_API + "cars/private/driver/" + this.currentDriverId,
          headers: {
            Authorization: "Bearer " + localStorage.getItem("token"),
          },
        });
        this.driverPrivateCars = response.data.data;
      } catch (e) {
        console.log(e);
      }
      this.driverPrivateCarsLoading = false;
    },

    // Settlement Invoice Upload/Preview
    openSettlementInvoiceUpload(settlementId) {
      this.settlementInvoiceUploadId = settlementId;
      this.settlementInvoiceFile = null;
      this.settlementInvoiceUploadDialog = true;
    },
    async uploadSettlementInvoice() {
      if (!this.settlementInvoiceFile || !this.settlementInvoiceUploadId) return;
      try {
        const formData = new FormData();
        formData.append("file", this.settlementInvoiceFile);
        await axios({
          method: "post",
          url: process.env.VUE_APP_ROOT_API + "settlements/upload-invoice/" + this.settlementInvoiceUploadId,
          headers: {
            Authorization: "Bearer " + localStorage.getItem("token"),
            "Content-Type": "multipart/form-data",
          },
          data: formData,
        });
        this.settlementInvoiceUploadDialog = false;
        Swal.fire("Sukces", "Faktura wgrana", "success");
        await this.getPayouts();
      } catch (e) {
        console.log(e);
        Swal.fire("Błąd", e.response?.data?.message?.toString() || "Wystąpił błąd", "error");
      }
    },
    async previewSettlementInvoice(fileId) {
      this.settlementInvoicePreviewDialog = true;
      this.settlementInvoicePreviewLoading = true;
      this.settlementInvoicePreviewUrl = null;
      try {
        const response = await axios({
          method: "post",
          url: process.env.VUE_APP_ROOT_API + "aws/download-file/" + fileId,
          headers: {
            Authorization: "Bearer " + localStorage.getItem("token"),
          },
          responseType: "blob",
        });
        const blob = new Blob([response.data], { type: response.headers["content-type"] });
        this.settlementInvoicePreviewUrl = URL.createObjectURL(blob);
        this.settlementInvoicePreviewIsPdf = response.headers["content-type"]?.includes("pdf");
      } catch (e) {
        console.log(e);
        Swal.fire("Błąd", "Nie udało się pobrać podglądu", "error");
        this.settlementInvoicePreviewDialog = false;
      }
      this.settlementInvoicePreviewLoading = false;
    },

    // Bailiffs (Komornicy)
    async fetchBailiffs() {
      if (!this.currentDriverId) return;
      this.bailiffsLoading = true;
      try {
        const response = await axios({
          method: "get",
          url: process.env.VUE_APP_ROOT_API + "bailiffs/driver/" + this.currentDriverId,
          headers: {
            Authorization: "Bearer " + localStorage.getItem("token"),
          },
        });
        this.bailiffs = response.data.data;
      } catch (e) {
        console.log(e);
      }
      this.bailiffsLoading = false;
    },
    openBailiffDialog(item = null) {
      this.editingBailiff = item;
      if (item) {
        this.bailiffForm = {
          name: item.name,
          accountNo: item.accountNo,
          wireTitle: item.wireTitle,
          addressLine1: item.addressLine1 || "",
          addressLine2: item.addressLine2 || "",
          email: item.email || "",
          isActive: item.isActive,
          transferTypes: item.transferTypes || [],
          totalAmountZl: item.totalAmountCents != null ? item.totalAmountCents / 100 : null,
          paidAmountZl: item.paidAmountCents != null ? item.paidAmountCents / 100 : 0,
          monthlyLimitZl: item.monthlyLimitCents != null ? item.monthlyLimitCents / 100 : null,
        };
      } else {
        this.bailiffForm = {
          name: "",
          accountNo: "",
          wireTitle: "",
          addressLine1: "",
          addressLine2: "",
          email: "",
          isActive: false,
          transferTypes: [],
          totalAmountZl: null,
          paidAmountZl: null,
          monthlyLimitZl: null,
        };
        this.fetchBailiffRegistry();
      }
      this.bailiffDialog = true;
    },
    async fetchBailiffRegistry() {
      try {
        const res = await axios({
          method: "get",
          url: process.env.VUE_APP_ROOT_API + "bailiffs/registry",
          headers: { Authorization: "Bearer " + localStorage.getItem("token") },
        });
        this.bailiffRegistry = res.data.data || [];
      } catch (e) {
        console.log(e);
        this.bailiffRegistry = [];
      }
    },
    registryItemText(b) {
      return `${b.name}${b.accountNo ? ' (' + b.accountNo + ')' : ''}`;
    },
    bailiffItemText(b) {
      return `${b.name}${b.accountNo ? ' (' + b.accountNo + ')' : ''}`;
    },
    onPickRegistryBailiff(b) {
      if (!b) return;
      this.bailiffForm.name = b.name || "";
      this.bailiffForm.accountNo = b.accountNo || "";
      this.bailiffForm.wireTitle = b.wireTitle || "";
      this.bailiffForm.addressLine1 = b.addressLine1 || "";
      this.bailiffForm.addressLine2 = b.addressLine2 || "";
      this.bailiffForm.email = b.email || "";
      this.bailiffForm.transferTypes = b.transferTypes || [];
    },
    async saveBailiff() {
      try {
        // eslint-disable-next-line no-unused-vars
        const { totalAmountZl, paidAmountZl, monthlyLimitZl, ...rest } = this.bailiffForm;
        const toCents = (v) => (v === null || v === "" || isNaN(v) ? null : Math.round(Number(v) * 100));
        const payload = {
          ...rest,
          totalAmountCents: toCents(totalAmountZl),
          paidAmountCents: toCents(paidAmountZl) ?? 0,
          // Puste = bez limitu. Zero znaczy „nic nie przekazuj", więc nie
          // wolno tego zwinąć do null przez `|| null`.
          monthlyLimitCents: toCents(monthlyLimitZl),
        };
        if (this.editingBailiff) {
          await axios({
            method: "patch",
            url: process.env.VUE_APP_ROOT_API + "bailiffs/" + this.editingBailiff.id,
            headers: {
              Authorization: "Bearer " + localStorage.getItem("token"),
            },
            data: payload,
          });
        } else {
          await axios({
            method: "post",
            url: process.env.VUE_APP_ROOT_API + "bailiffs",
            headers: {
              Authorization: "Bearer " + localStorage.getItem("token"),
            },
            data: {
              ...payload,
              driverId: this.currentDriverId,
            },
          });
        }
        this.bailiffDialog = false;
        await this.fetchBailiffs();
        Swal.fire("Sukces", "Zapisano komornika", "success");
      } catch (e) {
        console.log(e);
        Swal.fire("Błąd", e.response?.data?.message?.toString() || "Wystąpił błąd", "error");
      }
    },
    async deleteBailiff(id) {
      const result = await Swal.fire({
        title: "Usunąć komornika?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Tak",
        cancelButtonText: "Anuluj",
      });
      if (!result.isConfirmed) return;
      try {
        await axios({
          method: "delete",
          url: process.env.VUE_APP_ROOT_API + "bailiffs/" + id,
          headers: {
            Authorization: "Bearer " + localStorage.getItem("token"),
          },
        });
        await this.fetchBailiffs();
        Swal.fire("Sukces", "Usunięto komornika", "success");
      } catch (e) {
        console.log(e);
        Swal.fire("Błąd", e.response?.data?.message?.toString() || "Wystąpił błąd", "error");
      }
    },
    async fetchKomornikLetters() {
      if (!this.currentDriverId) return;
      this.komornikLettersLoading = true;
      try {
        const response = await axios({
          method: "get",
          url: process.env.VUE_APP_ROOT_API + "komornik-letters/driver/" + this.currentDriverId,
          headers: { Authorization: "Bearer " + localStorage.getItem("token") },
        });
        this.komornikLetters = response.data.data;
      } catch (e) {
        console.log(e);
      }
      this.komornikLettersLoading = false;
    },
    async fetchKomornikFee() {
      try {
        const response = await axios({
          method: "get",
          url: process.env.VUE_APP_ROOT_API + "payout-config",
          headers: { Authorization: "Bearer " + localStorage.getItem("token") },
        });
        this.komornikFeeZl = (response.data?.data?.komornikFee || 0) / 100;
      } catch (e) {
        console.log(e);
      }
    },
    komornikOverdue(item) {
      return !item.respondedAt && item.responseDeadline && new Date(item.responseDeadline) < new Date();
    },
    komornikStatusLabel(item) {
      if (item.respondedAt) return "Odpowiedziano";
      if (this.komornikOverdue(item)) return "Po terminie";
      return "Oczekuje";
    },
    komornikStatusColor(item) {
      if (item.respondedAt) return "green";
      if (this.komornikOverdue(item)) return "red";
      return "orange";
    },
    openKomornikLetterDialog() {
      const today = new Date().toISOString().slice(0, 10);
      this.komornikLetterForm = {
        title: "",
        caseNumber: "",
        bailiffId: null,
        description: "",
        receivedAt: today,
        responseDeadline: "",
        files: [],
      };
      this.fetchKomornikFee();
      this.komornikLetterDialog = true;
    },
    async saveKomornikLetter() {
      this.komornikLetterSaving = true;
      try {
        const formData = new FormData();
        formData.append("driverId", this.currentDriverId);
        formData.append("title", this.komornikLetterForm.title);
        if (this.komornikLetterForm.caseNumber) formData.append("caseNumber", this.komornikLetterForm.caseNumber);
        if (this.komornikLetterForm.bailiffId) formData.append("bailiffId", this.komornikLetterForm.bailiffId);
        if (this.komornikLetterForm.description) formData.append("description", this.komornikLetterForm.description);
        if (this.komornikLetterForm.receivedAt) formData.append("receivedAt", this.komornikLetterForm.receivedAt);
        if (this.komornikLetterForm.responseDeadline) formData.append("responseDeadline", this.komornikLetterForm.responseDeadline);
        const files = this.komornikLetterForm.files || [];
        for (const f of files) formData.append("files", f, f.name);
        await axios({
          method: "post",
          url: process.env.VUE_APP_ROOT_API + "komornik-letters",
          headers: {
            Authorization: "Bearer " + localStorage.getItem("token"),
            "Content-Type": "multipart/form-data",
          },
          data: formData,
        });
        this.komornikLetterDialog = false;
        await this.fetchKomornikLetters();
        Swal.fire("Sukces", "Dodano pismo komornicze i naliczono opłatę", "success");
      } catch (e) {
        console.log(e);
        Swal.fire("Błąd", e.response?.data?.message?.toString() || "Wystąpił błąd", "error");
      }
      this.komornikLetterSaving = false;
    },
    async markKomornikResponded(item) {
      try {
        await axios({
          method: "patch",
          url: process.env.VUE_APP_ROOT_API + "komornik-letters/" + item.id,
          headers: { Authorization: "Bearer " + localStorage.getItem("token") },
          data: { respondedAt: "now" },
        });
        await this.fetchKomornikLetters();
      } catch (e) {
        console.log(e);
        Swal.fire("Błąd", e.response?.data?.message?.toString() || "Wystąpił błąd", "error");
      }
    },
    async deleteKomornikLetter(id) {
      const result = await Swal.fire({
        title: "Usunąć pismo komornicze?",
        text: "Naliczona opłata zostanie cofnięta (jeśli nie jest w zatwierdzonym rozliczeniu).",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Tak",
        cancelButtonText: "Anuluj",
      });
      if (!result.isConfirmed) return;
      try {
        await axios({
          method: "delete",
          url: process.env.VUE_APP_ROOT_API + "komornik-letters/" + id,
          headers: { Authorization: "Bearer " + localStorage.getItem("token") },
        });
        await this.fetchKomornikLetters();
        Swal.fire("Sukces", "Usunięto pismo", "success");
      } catch (e) {
        console.log(e);
        Swal.fire("Błąd", e.response?.data?.message?.toString() || "Wystąpił błąd", "error");
      }
    },
    async sendResponseEmail(letter, response) {
      const prefill = (letter.bailiff && letter.bailiff.email) || response.sentTo || "";
      const { value: email } = await Swal.fire({
        title: "Wyślij odpowiedź e-mailem",
        input: "email",
        inputLabel: "Adres e-mail komornika",
        inputValue: prefill,
        showCancelButton: true,
        confirmButtonText: "Wyślij",
        cancelButtonText: "Anuluj",
        inputValidator: (v) => (!v ? "Podaj adres e-mail" : null),
      });
      if (!email) return;
      try {
        const res = await axios({
          method: "post",
          url: process.env.VUE_APP_ROOT_API + "komornik-letters/response/" + response.id + "/send",
          headers: { Authorization: "Bearer " + localStorage.getItem("token") },
          data: { email },
        });
        await this.fetchKomornikLetters();
        if (res.data?.data?.mocked) {
          Swal.fire("Uwaga", "SMTP nie jest skonfigurowany — e-mail zapisany tylko w logach (tryb testowy).", "warning");
        } else {
          Swal.fire("Wysłano", "Odpowiedź wysłana na " + email, "success");
        }
      } catch (e) {
        console.log(e);
        Swal.fire("Błąd", e.response?.data?.message?.toString() || "Nie udało się wysłać", "error");
      }
    },
    // Otwiera plik komorniczy (np. potwierdzenie wysłania) przez endpoint AWS.
    async openKomornikFile(fileId) {
      if (!fileId) return;
      try {
        const res = await axios({
          method: "post",
          url: process.env.VUE_APP_ROOT_API + "aws/download-file/" + fileId,
          headers: { Authorization: "Bearer " + localStorage.getItem("token") },
          responseType: "blob",
        });
        const blob = new Blob([res.data], { type: res.headers["content-type"] });
        window.open(URL.createObjectURL(blob), "_blank");
      } catch (e) {
        console.log(e);
        Swal.fire("Błąd", "Nie udało się otworzyć pliku", "error");
      }
    },
    async openResponseDialog(letter) {
      this.responseLetter = letter;
      this.responseForm = { variant: "WORKS", body: "", signatureType: "MANUAL" };
      // pobierz szablony + sprawdź uprawnienie do faksymilki
      try {
        const [tpl, me] = await Promise.all([
          axios.get(process.env.VUE_APP_ROOT_API + "komornik-letters/templates", {
            headers: { Authorization: "Bearer " + localStorage.getItem("token") },
          }),
          axios.get(process.env.VUE_APP_ROOT_API + "company-admins/me", {
            headers: { Authorization: "Bearer " + localStorage.getItem("token") },
          }),
        ]);
        this.komornikTemplates = tpl.data.data || [];
        const meData = me.data.data || {};
        this.canUseFacsimileSelf = !!(meData.canUseFacsimile && meData.facsimileSignature);
      } catch (e) {
        console.log(e);
        this.komornikTemplates = [];
        this.canUseFacsimileSelf = false;
      }
      if (this.canUseFacsimileSelf) this.responseForm.signatureType = "FACSIMILE";
      this.loadResponseTemplate();
      this.responseDialog = true;
      // Płótno podpisu w oknie dialogowym ma 0px dopóki dialog się nie pokaże —
      // trzeba je przeskalować po wyświetleniu, inaczej rysowanie nie działa.
      if (this.responseForm.signatureType === "MANUAL") this.resizeKomornikSig();
    },
    resizeKomornikSig() {
      const doResize = () => {
        const pad = this.$refs.komornikSigPad;
        if (!pad) return;
        if (pad.clearSignature) pad.clearSignature();
        if (pad.resizeCanvas) pad.resizeCanvas();
      };
      this.$nextTick(doResize);
      setTimeout(doResize, 350);
    },
    onResponseSigTypeChange(val) {
      if (val === "MANUAL") this.resizeKomornikSig();
    },
    loadResponseTemplate() {
      const tpl = this.komornikTemplates.find((t) => t.variant === this.responseForm.variant);
      this.responseForm.body = tpl ? tpl.body : "";
    },
    clearKomornikSig() {
      if (this.$refs.komornikSigPad) this.$refs.komornikSigPad.clearSignature();
    },
    getManualSignature() {
      const pad = this.$refs.komornikSigPad;
      return pad ? pad.grab() : null;
    },
    buildResponsePayload() {
      const payload = {
        variant: this.responseForm.variant,
        body: this.responseForm.body,
        signatureType: this.responseForm.signatureType,
      };
      if (this.responseForm.signatureType === "MANUAL") {
        payload.manualSignature = this.getManualSignature() || " ";
      }
      return payload;
    },
    async previewResponse() {
      this.responsePreviewing = true;
      try {
        const res = await axios({
          method: "post",
          url: process.env.VUE_APP_ROOT_API + "komornik-letters/" + this.responseLetter.id + "/response/preview",
          headers: { Authorization: "Bearer " + localStorage.getItem("token") },
          data: this.buildResponsePayload(),
          responseType: "blob",
        });
        const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
        window.open(url, "_blank");
      } catch (e) {
        console.log(e);
        Swal.fire("Błąd", "Nie udało się wygenerować podglądu", "error");
      }
      this.responsePreviewing = false;
    },
    async generateResponse() {
      if (this.responseForm.signatureType === "MANUAL" && !this.getManualSignature()) {
        Swal.fire("Brak podpisu", "Podpisz pismo w polu lub wybierz auto-podpis", "warning");
        return;
      }
      this.responseSaving = true;
      try {
        await axios({
          method: "post",
          url: process.env.VUE_APP_ROOT_API + "komornik-letters/" + this.responseLetter.id + "/response",
          headers: { Authorization: "Bearer " + localStorage.getItem("token") },
          data: this.buildResponsePayload(),
        });
        this.responseDialog = false;
        await this.fetchKomornikLetters();
        Swal.fire("Sukces", "Wygenerowano i podpisano odpowiedź", "success");
      } catch (e) {
        console.log(e);
        Swal.fire("Błąd", e.response?.data?.message?.toString() || "Wystąpił błąd", "error");
      }
      this.responseSaving = false;
    },
  },
};
</script>

<style scoped>
/* === Kalkulacja rozliczenia — identyczny widok jak w aplikacji kierowcy === */
.section-label {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #6E7691;
  padding: 8px 4px 6px;
}
.dt-table {
  background: #232838;
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 14px;
  overflow: hidden;
}
.dt-row {
  display: flex;
  padding: 10px 14px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.04);
  font-size: 13px;
  align-items: center;
  gap: 10px;
}
.dt-row:last-child {
  border-bottom: none;
}
.dt-row.dt-total {
  background: #1A2433;
  font-weight: 700;
}
.dt-cell {
  flex: 1;
  font-variant-numeric: tabular-nums;
  min-width: 0;
  word-break: break-word;
  color: #fff;
}
.dt-cell.text-right { text-align: right; }
.dt-cell.text-center { text-align: center; }
.dt-cell .caption {
  display: block;
  font-size: 10px;
  line-height: 1.3;
  margin-top: 2px;
  color: #6E7691;
}

/* === Driver-app aesthetic for admin DriverDetails === */
.driver-details-page {
  background: #181C28;
  min-height: calc(100vh - 56px);
  padding: 16px 16px 40px;
  margin: -24px;
  color: #fff;
}
.driver-details-page * { box-sizing: border-box; }

/* Page header */
.page-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 4px 4px 18px;
}
.back-btn {
  width: 40px;
  height: 40px;
  border-radius: 12px;
  background: #232838;
  border: 1px solid rgba(255,255,255,0.06);
  color: #fff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s ease, border-color 0.15s ease;
}
.back-btn:hover { background: #2A3042; border-color: rgba(255,255,255,0.16); }
.back-btn .v-icon { color: inherit !important; }
.page-avatar {
  width: 44px;
  height: 44px;
  border-radius: 14px;
  background: linear-gradient(135deg, #2962FF 0%, #5478FF 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-shadow: 0 6px 18px rgba(41, 98, 255, 0.4);
}
.page-eyebrow {
  font-size: 11px;
  color: #6E7691;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}
.page-title {
  font-size: 22px;
  font-weight: 800;
  color: #fff;
  margin: 2px 0 0;
  letter-spacing: -0.02em;
}

/* Segmented tabs (10 tabs — scrollable) */
.tabs-pill-wrap {
  margin-bottom: 16px;
  overflow-x: auto;
  overflow-y: hidden;
  padding-bottom: 4px;
}
.tabs-pill-wrap::-webkit-scrollbar { height: 4px; }
.tabs-pill-wrap::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.08);
  border-radius: 2px;
}
.tabs-pill {
  display: inline-flex;
  flex-wrap: nowrap;
  gap: 4px;
  background: #232838;
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 999px;
  padding: 5px;
  width: max-content;
}
.tab-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: transparent;
  border: none;
  color: #B0BEC5;
  padding: 8px 14px;
  border-radius: 999px;
  font-size: 12.5px;
  font-weight: 600;
  letter-spacing: -0.01em;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;
  white-space: nowrap;
  font-family: inherit;
}
.tab-pill:hover { color: #fff; background: rgba(255,255,255,0.04); }
.tab-pill--active {
  background: #2962FF;
  color: #fff;
  box-shadow: 0 4px 12px rgba(41, 98, 255, 0.35);
}
.tab-pill--active .v-icon { color: #fff !important; }
.tab-pill .v-icon { color: inherit !important; }

/* === Vuetify deep overrides — same recipe as PayoutsPage === */
.driver-details-page >>> .v-card {
  background: #232838 !important;
  border: 1px solid rgba(255, 255, 255, 0.06) !important;
  border-radius: 18px !important;
  box-shadow: none !important;
  color: #fff !important;
}
.driver-details-page >>> .v-card .v-card__title {
  color: #fff !important;
  font-size: 15px !important;
  font-weight: 700 !important;
  letter-spacing: -0.01em !important;
  padding: 16px 20px 8px !important;
}
.driver-details-page >>> .v-card .v-card__title .v-icon { color: #5478FF !important; }
.driver-details-page >>> .v-card .v-card__subtitle { color: #B0BEC5 !important; }

/* v-tabs-items wrapper transparent */
.driver-details-page >>> .v-tabs-items,
.driver-details-page >>> .v-window {
  background: transparent !important;
}

/* Inputs */
.driver-details-page >>> .v-text-field--outlined fieldset,
.driver-details-page >>> .v-select--outlined fieldset,
.driver-details-page >>> .v-textarea--outlined fieldset {
  border-color: rgba(255,255,255,0.1) !important;
}
.driver-details-page >>> .v-text-field--outlined .v-input__slot,
.driver-details-page >>> .v-select--outlined .v-input__slot,
.driver-details-page >>> .v-textarea--outlined .v-input__slot {
  background: #181C28 !important;
  border-radius: 12px !important;
}
.driver-details-page >>> .v-label { color: #6E7691 !important; font-size: 13px !important; }
.driver-details-page >>> .v-text-field input,
.driver-details-page >>> .v-select__selection,
.driver-details-page >>> .v-textarea textarea { color: #fff !important; }
.driver-details-page >>> .v-input__prepend-inner .v-icon,
.driver-details-page >>> .v-input__append-inner .v-icon { color: #6E7691 !important; }
.driver-details-page >>> .v-text-field--outlined.v-input--is-focused fieldset {
  border-color: #5478FF !important;
}

/* Non-outlined inputs (legacy in this page) — give them a baseline too */
.driver-details-page >>> .v-text-field:not(.v-text-field--outlined) input,
.driver-details-page >>> .v-text-field:not(.v-text-field--outlined) textarea,
.driver-details-page >>> .v-select:not(.v-select--outlined) .v-select__selection { color: #fff !important; }
.driver-details-page >>> .v-text-field:not(.v-text-field--outlined) > .v-input__control > .v-input__slot::before {
  border-color: rgba(255,255,255,0.16) !important;
}

/* Data tables */
.driver-details-page >>> .v-data-table {
  background: #232838 !important;
  border: 1px solid rgba(255, 255, 255, 0.06) !important;
  border-radius: 14px !important;
  color: #fff !important;
  box-shadow: none !important;
  margin-bottom: 12px;
}
.driver-details-page >>> .v-data-table thead tr th {
  background: #1F2434 !important;
  color: #6E7691 !important;
  font-size: 11px !important;
  font-weight: 700 !important;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  border-bottom: 1px solid rgba(255,255,255,0.06) !important;
}
.driver-details-page >>> .v-data-table tbody tr { background: #232838 !important; }
.driver-details-page >>> .v-data-table tbody tr:hover { background: #2A3042 !important; }
.driver-details-page >>> .v-data-table tbody tr td {
  color: #fff !important;
  border-bottom: 1px solid rgba(255,255,255,0.04) !important;
}
.driver-details-page >>> .v-data-footer {
  background: #1F2434 !important;
  color: #B0BEC5 !important;
  border-top: 1px solid rgba(255,255,255,0.06) !important;
}

/* Toolbars (used at top of tables) */
.driver-details-page >>> .v-toolbar {
  background: transparent !important;
  color: #fff !important;
}
.driver-details-page >>> .v-toolbar .v-btn {
  border-radius: 10px !important;
  text-transform: none !important;
  font-weight: 600 !important;
  letter-spacing: -0.01em !important;
}

/* Buttons inside cards (general) */
.driver-details-page >>> .v-btn:not(.v-btn--icon) {
  border-radius: 10px !important;
  text-transform: none !important;
  letter-spacing: -0.01em !important;
  font-weight: 600 !important;
}

/* Chips */
.driver-details-page >>> .v-chip {
  border-radius: 999px !important;
  font-weight: 700 !important;
}

/* Dialog cards */
.driver-details-page >>> .v-dialog .v-card { background: #232838 !important; }

/* Dividers / progress / list */
.driver-details-page >>> .v-divider { border-color: rgba(255,255,255,0.06) !important; }
.driver-details-page >>> .v-progress-linear { background: rgba(255,255,255,0.06) !important; }
.driver-details-page >>> .v-list { background: transparent !important; }
.driver-details-page >>> .v-list-item__title { color: #fff !important; }
.driver-details-page >>> .v-list-item__subtitle { color: #B0BEC5 !important; }

/* Tooltips for legacy v-tabs (now unused) — just in case */
.driver-details-page >>> .v-tabs { background: transparent !important; }

/* Expansion panels */
.driver-details-page >>> .v-expansion-panel {
  background: #232838 !important;
  border: 1px solid rgba(255,255,255,0.06) !important;
  border-radius: 14px !important;
  margin-bottom: 8px;
}
.driver-details-page >>> .v-expansion-panel-header { color: #fff !important; }
.driver-details-page >>> .v-expansion-panel-content__wrap { color: #B0BEC5 !important; }

/* Kroki obliczeń — czytelne na obu motywach.
   Podświetlamy kontrastowo wynik końcowy (zielony) i kroki pośrednie (szary). */
.calc-step-code {
  display: block;
  font-size: 12px !important;
  line-height: 1.5;
  border-radius: 6px;
  white-space: pre-wrap;
  word-break: break-all;
  margin: 4px 0 8px 0;
}
.calc-step-code-sm {
  font-size: 11px !important;
}
.calc-step-normal {
  background: rgba(255, 255, 255, 0.06) !important;
  color: #e0e0e0 !important;
  border: 1px solid rgba(255, 255, 255, 0.08);
}
.calc-step-final {
  background: rgba(76, 175, 80, 0.18) !important;
  color: #a5d6a7 !important;
  border: 1px solid rgba(76, 175, 80, 0.4);
  font-weight: bold;
}
/* W motywie jasnym zachowaj jasne tło */
.theme--light .calc-step-normal {
  background: #f5f5f5 !important;
  color: #212121 !important;
  border-color: #e0e0e0;
}
.theme--light .calc-step-final {
  background: #e8f5e9 !important;
  color: #2e7d32 !important;
  border-color: #a5d6a7;
}

/* Oswiadczenia ZUS — wiersz „ikona + nazwa" w podgladzie */
.zus-row {
  display: flex;
  align-items: center;
  padding: 2px 0;
}
</style>
